/**
 * Angel Ashes — booking wizard engine (core).
 *
 * Split across theme assets for Shopify's 256 KB Theme Editor limit:
 *   booking-wizard-memorials.js      — memorial catalog
 *   booking-wizard-memorial-ui.js    — memorial cards / ashes / photo / engraving UI
 *   booking-wizard-nameplate.js      — nameplate personalise
 *   booking-wizard-arrival.js        — transport / calendar / return choices
 *   booking-wizard-cart.js           — ledger / draft / summary
 *   booking-wizard.js                — boot, config, pet/cremation, chrome (this file)
 *
 * Vanilla JS, no build step. Cart line-item property contract must stay aligned
 * with lib/booking-shopify-cart-contract.ts and lib/shopify.ts.
 */
(function () {
  'use strict'

  /* ----------------------------- utilities ------------------------------ */

  function escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    })
  }

  /**
   * Shared step intro: heading + optional supporting copy.
   * Spacing to primary content is owned by `.baw-step-intro` CSS.
   */
  function stepIntroHtml(headline, helperHtml, headlineClass) {
    var extra = headlineClass ? ' ' + String(headlineClass).trim() : ''
    var helper = helperHtml ? String(helperHtml) : ''
    return (
      '<div class="baw-step-intro">' +
      '<h2 class="baw-headline baw-wizard-title' + extra + '">' + escapeHtml(headline) + '</h2>' +
      helper +
      '</div>'
    )
  }

  /** Quiet text-link CTA for optional steps (under the card selector, not in the footer). */
  function optionalSkipButtonHtml(label, attrs) {
    var text = String(label || '').trim()
    if (!text) return ''
    return (
      '<button type="button" class="baw-optional-skip" data-baw-optional-skip' +
      (attrs ? ' ' + attrs : '') +
      '>' +
      escapeHtml(text) +
      '</button>'
    )
  }

  /** Group the digits of a non-negative integer string with `sep` every 3 digits. */
  function groupThousands(intStr, sep) {
    return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, sep)
  }

  /**
   * Mirrors Shopify's `money_format` tokens. Prices arrive as integer cents
   * (Shopify Liquid convention — `variant.price | json` is raw cents, e.g. 5600 = $56.00).
   */
  function formatMoney(cents, moneyFormat) {
    var amount = Math.max(0, Number(cents) || 0) / 100
    var fmt = moneyFormat || '${{amount}}'
    var fixedParts = amount.toFixed(2).split('.')
    var wholeRounded = String(Math.round(amount))

    var plainAmount = groupThousands(fixedParts[0], ',') + '.' + fixedParts[1] // 1,234.56
    var commaAmount = groupThousands(fixedParts[0], '.') + ',' + fixedParts[1] // 1.234,56
    var noDecimals = groupThousands(wholeRounded, ',') // 1,234
    var noDecimalsComma = groupThousands(wholeRounded, '.') // 1.234

    return fmt
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimalsComma)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, commaAmount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, plainAmount)
  }

  function formatCremationTimingDisplay(text) {
    var s = String(text || '')
      .replace(/\s+from\s+collection\.?\s*$/i, '')
      .replace(/\s+/g, ' ')
      .trim()
    if (/^ready within\s+/i.test(s)) return s
    if (/^home within\s+/i.test(s)) return s.replace(/^home\s+/i, 'Ready ')
    if (/^usually home within\s+/i.test(s)) {
      return s.replace(/^usually\s+home\s+/i, 'Ready ')
    }
    if (/^usually ready within\s+/i.test(s)) {
      return s.replace(/^usually\s+/i, '')
    }
    // Normalise Theme Editor / legacy “Ready in 10–14 days” → ready-within copy
    var m = s.match(/(?:ready\s+in|home\s+within|usually|typically|about|approx\.?|~)?\s*(\d+\s*[–\-]\s*\d+|\d+)\s*days?/i)
    if (m) {
      var range = m[1].replace(/\s+/g, '').replace(/-/g, '–')
      return 'Ready within ' + range + ' days'
    }
    return s
  }

  function cremationReadyWithinLabel(text, fallbackRange) {
    var formatted = formatCremationTimingDisplay(text)
    if (/^ready within\s+/i.test(formatted)) return formatted
    var m = String(text || '').match(/(\d+\s*[–\-]\s*\d+|\d+)\s*days?/i)
    var range = m
      ? m[1].replace(/\s+/g, '').replace(/-/g, '–')
      : fallbackRange
    return 'Ready within ' + range + ' days'
  }

  function firstAvailableVariant(product) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return null
    var i
    for (i = 0; i < product.variants.length; i++) {
      var v = product.variants[i]
      // Never fall back to a sold-out variant — /cart/add.js 422s the whole booking.
      if (v && v.available !== false) return v
    }
    return null
  }

  function sizedShopifyImageUrl(url, width) {
    if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.sizedShopifyImageUrl) {
      return AngelAshesBookingMemorials.sizedShopifyImageUrl(url, width)
    }
    return url || ''
  }

  function memorialCdnPx(key, fallback) {
    var Mem = typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
    var n = Mem && Mem[key]
    return n || fallback
  }

  /* ----------------------------- accessibility ------------------------------ */

  /** Roving tabindex + arrow keys for a group of option buttons (tiles, slots, etc.). */
  function enhanceOptionGroup(container, itemSelector, groupLabel) {
    if (!container) return
    var items = Array.prototype.slice.call(container.querySelectorAll(itemSelector))
    if (!items.length) return

    var role = container.getAttribute('role')
    var asListbox = role === 'listbox'
    // `role="group"` marks a set of tiles that can each be switched back off
    // (see renderTileStep's `deselectable`). A radio can't be unchecked, so those
    // tiles stay plain buttons reporting aria-pressed instead.
    var asToggleGroup = role === 'group'
    if (groupLabel && !container.getAttribute('aria-label')) container.setAttribute('aria-label', groupLabel)
    if (!role) container.setAttribute('role', 'radiogroup')

    var selected = items.filter(function (el) { return el.classList.contains('is-selected') })[0]
    var active = selected || items[0]
    items.forEach(function (el) {
      el.setAttribute('tabindex', el === active ? '0' : '-1')
      if (asListbox) {
        el.setAttribute('role', 'option')
        el.setAttribute('aria-selected', el.classList.contains('is-selected') ? 'true' : 'false')
      } else if (asToggleGroup) {
        el.setAttribute('aria-pressed', el.classList.contains('is-selected') ? 'true' : 'false')
      } else {
        el.setAttribute('role', 'radio')
        el.setAttribute('aria-checked', el.classList.contains('is-selected') ? 'true' : 'false')
      }
    })

    container.addEventListener('keydown', function (e) {
      var current = document.activeElement
      var idx = items.indexOf(current)
      if (idx === -1) return

      var nextIdx = idx
      if (e.key === 'ArrowDown' || e.key === 'ArrowRight') nextIdx = (idx + 1) % items.length
      else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') nextIdx = (idx - 1 + items.length) % items.length
      else if (e.key === 'Home') nextIdx = 0
      else if (e.key === 'End') nextIdx = items.length - 1
      else return

      e.preventDefault()
      items.forEach(function (el, i) { el.setAttribute('tabindex', i === nextIdx ? '0' : '-1') })
      items[nextIdx].focus()
    })
  }

  /** Arrow-key movement across available calendar day buttons. */
  function enhanceCalendarKeyboard(calRoot) {
    if (!calRoot) return
    var days = Array.prototype.slice.call(calRoot.querySelectorAll('[data-baw-cal-day]'))
    if (!days.length) return

    var selected = calRoot.querySelector('[data-baw-cal-day].is-selected') || days[0]
    days.forEach(function (dayBtn) {
      dayBtn.setAttribute('tabindex', dayBtn === selected ? '0' : '-1')
      dayBtn.setAttribute('aria-selected', dayBtn.classList.contains('is-selected') ? 'true' : 'false')
    })

    calRoot.addEventListener('keydown', function (e) {
      var current = document.activeElement
      if (!current || !current.hasAttribute('data-baw-cal-day')) return
      var ymd = current.getAttribute('data-baw-cal-day')
      var parsed = String(ymd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (!parsed) return

      var date = new Date(Number(parsed[1]), Number(parsed[2]) - 1, Number(parsed[3]))
      var delta = 0
      if (e.key === 'ArrowLeft') delta = -1
      else if (e.key === 'ArrowRight') delta = 1
      else if (e.key === 'ArrowUp') delta = -7
      else if (e.key === 'ArrowDown') delta = 7
      else if (e.key === 'Home') {
        e.preventDefault()
        days.forEach(function (d, i) { d.setAttribute('tabindex', i === 0 ? '0' : '-1') })
        days[0].focus()
        return
      } else if (e.key === 'End') {
        e.preventDefault()
        var last = days[days.length - 1]
        days.forEach(function (d) { d.setAttribute('tabindex', d === last ? '0' : '-1') })
        last.focus()
        return
      } else return

      e.preventDefault()
      var guard = 0
      var target = null
      while (guard < 42) {
        date.setDate(date.getDate() + delta)
        var nextYmd =
          date.getFullYear() +
          '-' +
          String(date.getMonth() + 1).padStart(2, '0') +
          '-' +
          String(date.getDate()).padStart(2, '0')
        target = calRoot.querySelector('[data-baw-cal-day="' + nextYmd + '"]')
        if (target) break
        guard++
      }
      if (!target) return
      days.forEach(function (d) { d.setAttribute('tabindex', '-1') })
      target.setAttribute('tabindex', '0')
      target.focus()
    })
  }

  /** Enter in text/number fields activates Continue when enabled (not on buttons/textareas). */
  function bindEnterSubmitsContinue(stepEl) {
    if (!stepEl || stepEl.getAttribute('data-baw-enter-bound')) return
    stepEl.setAttribute('data-baw-enter-bound', '1')
    stepEl.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return
      var tag = e.target && e.target.tagName
      if (!tag) return
      if (tag === 'TEXTAREA' || tag === 'BUTTON' || tag === 'A' || tag === 'SELECT') return
      // Continue lives in .baw-footer after mount — search the main column
      var scope = stepEl.closest('.baw-main') || stepEl
      var cont = scope.querySelector('[data-baw-continue]:not([disabled])')
      if (!cont) return
      e.preventDefault()
      cont.click()
    })
  }

  /** Best-effort weight-range hint parsed from a variant title like "M | 1 - 40 kg" or "S | Under 1 kg". */
  function parseWeightRange(title) {
    if (!title) return null
    var s = String(title)

    // "Under 1 kg" / "under 5kg" — must run before bare "N kg" patterns
    var under = s.match(/\bunder\s+(\d+(?:\.\d+)?)\s*kgs?\b/i)
    if (under) return { min: 0, max: parseFloat(under[1]), exclusiveMax: true }

    // "Up to 1 kg" / "up to 40kg" — before the "N to N kg" range so "to" isn't stolen.
    var upTo = s.match(/\bup\s*to\s+(\d+(?:\.\d+)?)\s*kgs?\b/i)
    if (upTo) return { min: 0, max: parseFloat(upTo[1]), exclusiveMax: false }

    // "Over 40 kg" / "Above 40kg"
    var over = s.match(/\b(?:over|above)\s+(\d+(?:\.\d+)?)\s*kgs?\b/i)
    if (over) return { min: parseFloat(over[1]), max: Infinity, exclusiveMax: false }

    // "1 - 40 kg", "1–40kg", "1 to 40 kg"
    var range = s.match(/(\d+(?:\.\d+)?)\s*(?:-|–|—|to)\s*(\d+(?:\.\d+)?)\s*kgs?\b/i)
    if (range) return { min: parseFloat(range[1]), max: parseFloat(range[2]), exclusiveMax: false }

    // "40+ kg" / "40kg+"
    var plus = s.match(/(\d+(?:\.\d+)?)\s*kgs?\s*\+/i) || s.match(/(\d+(?:\.\d+)?)\s*\+\s*kgs?\b/i)
    if (plus) return { min: parseFloat(plus[1]), max: Infinity, exclusiveMax: false }

    return null
  }

  function weightInRange(weightKg, range) {
    if (!range || weightKg == null) return false
    var weight = Number(weightKg)
    if (!isFinite(weight)) return false
    if (weight < range.min) return false
    if (range.exclusiveMax) return weight < range.max
    return weight <= range.max
  }

  function suggestedVariantId(product, weightKg) {
    var variant = suggestedVariant(product, weightKg)
    return variant ? variant.id : null
  }

  function suggestedVariant(product, weightKg) {
    if (!product || !Array.isArray(product.variants)) return null
    var weight = Number(weightKg)
    if (!isFinite(weight) || !(weight > 0)) return null
    var best = null
    var bestSpan = Infinity
    var i
    for (i = 0; i < product.variants.length; i++) {
      var variant = product.variants[i]
      if (!variant || variant.available === false) continue
      var range = parseWeightRange(variant.title)
      if (!weightInRange(weight, range)) continue
      var span = range.max === Infinity ? Number.MAX_VALUE : range.max - range.min
      if (span < bestSpan) {
        bestSpan = span
        best = variant
      }
    }
    return best
  }

  /** Keep the Shopify cremation variant on the current pet weight — cards and the rail must agree. */
  function syncCremationVariantForWeight(state, config) {
    if (!state || !config) return
    if (config.flow === 'shared') {
      ensureSharedCremationVariant(state, config)
      return
    }
    if (!state.cremationType) return
    var product = cremationProductForType(config, state.cremationType)
    if (!product) {
      state.cremationVariant = null
      return
    }
    var match = suggestedVariant(product, state.pet && state.pet.weightKg)
    state.cremationVariant = match || null
  }

  function parseLineList(lines) {
    if (!lines) return []
    if (typeof lines === 'string') lines = lines.split(/\r?\n/)
    return (lines || []).map(function (s) { return String(s || '').trim() }).filter(function (s) {
      return s && s.charAt(0) !== '#'
    })
  }

  // Lonsdale drop-offs: 10am–1pm. Each Goodbye Room length has its own start times
  // so the visit finishes before closing (school pickup).
  var DEFAULT_LONSDALE_TIME_WINDOWS = {
    5: [
      '10:00am', '10:15am', '10:30am', '10:45am',
      '11:00am', '11:15am', '11:30am', '11:45am',
      '12:00pm', '12:15pm', '12:30pm', '12:45pm',
    ],
    30: [
      '10:00am', '10:30am', '11:00am', '11:30am',
      '12:00pm', '12:30pm',
    ],
    60: [
      '10:00am', '11:00am', '12:00pm',
    ],
  }

  function lonsdaleGoodbyeMinutes(state) {
    var blob = [state.transport.label, state.transport.subLabel, state.transport.categoryLabel]
      .filter(Boolean).join(' ')
    if (/\b60\s*(min|mins|minutes)?\b/i.test(blob)) return 60
    if (/\b30\s*(min|mins|minutes)?\b/i.test(blob)) return 30
    if (/\b5\s*(min|mins|minutes)?\b/i.test(blob)) return 5
    if (/extended/i.test(blob)) return 60
    if (/short/i.test(blob)) return 5
    if (/private/i.test(blob)) return 30
    return 5
  }

  function isLonsdaleTransport(state) {
    if (state.transport.categoryId === 'lonsdale') return true
    var blob = [state.transport.categoryLabel, state.transport.label, state.transport.subLabel]
      .filter(Boolean).join(' ')
    return /lonsdale|drop\s*-?\s*off|handover|goodbye/i.test(blob)
  }

  function isVetTransport(state) {
    return !!(state && state.transport && state.transport.categoryId === 'vet')
  }

  // Vet Priority Collection — same cues as mapVetCollectionDisplay.
  function isPriorityCollectionSelected(state) {
    var blob = [state.transport.label, state.transport.subLabel]
      .filter(Boolean).join(' ')
    return isPriorityOptionLabel(blob)
  }

  /**
   * Anticipated collection timeframe for the selected care arrangement.
   * Reuses tile timing phrases (mapVetCollectionDisplay / mapHomeCollectionDisplay)
   * and, when a ready-from date is chosen, a concrete estimated range (vet only).
   * Returns { short, helper, rangeLabel } or null when there is no meaningful SLA
   * (Bring to Lonsdale / home dedicated date).
   * short: tile / helper fallback timing phrase (not appended on the quiet rail);
   * helper: date-step / review subtitle;
   * rangeLabel: “Thu 6 Aug – Thu 13 Aug” when estimable (used on the rail — vet only).
   */
  function collectionTimeframeForState(state) {
    if (!state || !state.transport) return null
    // Exact arrival time is chosen on the Lonsdale path — no SLA line.
    if (isLonsdaleTransport(state)) return null

    var pet = petFirstNameRef(state)
    var petRef = pet.ref
    var isHome = state.transport.categoryId === 'home'
    var isVet = isVetTransport(state)
    if (!isVet && !isHome) return null

    if (isAfterHoursTransport(state)) {
      return {
        short: 'Today, weekends & public holidays',
        helper: 'Today, weekends & public holidays.',
        rangeLabel: '',
      }
    }

    // Home is a dedicated requested date — no numeric collection window on rail/review.
    if (isHome) return null

    var estimate = collectionEstimateRangeForState(state)

    if (isPriorityCollectionSelected(state)) {
      if (estimate && estimate.rangeLabel) {
        return {
          short: estimate.rangeLabel,
          helper:
            'We\u2019ll aim to collect ' +
            petRef +
            ' between ' +
            formatCollectionDateShort(estimate.startYmd) +
            ' and ' +
            formatCollectionDateShort(estimate.endYmd) +
            '.',
          rangeLabel: estimate.rangeLabel,
          startYmd: estimate.startYmd,
          endYmd: estimate.endYmd,
        }
      }
      return {
        short: 'Usually within 2 working days',
        helper: 'We\u2019ll aim to collect within 2 working days of this date.',
        rangeLabel: '',
      }
    }

    // Standard + Regional Collection — tile: “Up to 7 days”
    if (estimate && estimate.rangeLabel) {
      return {
        short: estimate.rangeLabel,
        helper:
          'We\u2019ll aim to collect ' +
          petRef +
          ' between ' +
          formatCollectionDateShort(estimate.startYmd) +
          ' and ' +
          formatCollectionDateShort(estimate.endYmd) +
          '.',
        rangeLabel: estimate.rangeLabel,
        startYmd: estimate.startYmd,
        endYmd: estimate.endYmd,
      }
    }

    return {
      short: 'Up to 7 days',
      helper: 'We\u2019ll aim to collect ' + petRef + ' within about 7 days of this date.',
      rangeLabel: '',
    }
  }

  /** Optional Priority path — Standard vet collection only (not Regional / After Hours / Priority). */
  function vetPriorityDateHintHtml(state) {
    if (isPriorityCollectionSelected(state)) return ''
    if (isRegionalCareArrangement(state) || isAfterHoursTransport(state)) return ''
    return (
      escapeHtml('Need us sooner? Choose ') +
      '<button type="button" class="baw-inline-link" data-baw-goto="transport-main" data-baw-preselect-service="priority">Priority Collection</button>' +
      escapeHtml('.')
    )
  }

  /**
   * After Hours home — same SMS as home Standard: confirm date + arrange time.
   * (After Hours is not offered for vet clinic collection.)
   */
  function afterHoursCollectionSmsHelper(state, config) {
    if (!state || !state.transport) return ''
    if (isLonsdaleTransport(state)) return ''
    if (state.transport.categoryId !== 'home') return ''
    if (!isAfterHoursTransport(state)) return ''
    return stepCopyText(config, 'guidanceHomeSms')
  }

  /** SMS reassurance for home Standard only (not After Hours / Regional / Lonsdale / vet). */
  function collectionWindowSmsHelper(state, config) {
    if (!state || !state.transport) return ''
    if (isLonsdaleTransport(state)) return ''
    if (state.transport.categoryId !== 'home') return ''
    if (isAfterHoursTransport(state)) return ''
    if (isRegionalCareArrangement(state)) return ''
    return stepCopyText(config, 'guidanceHomeSms')
  }

  /** SMS reassurance on Bring to Lonsdale date + arrival-time step. */
  function lonsdaleArrivalSmsHelper(state, config) {
    if (!state || !state.transport) return ''
    if (!isLonsdaleTransport(state)) return ''
    return stepCopyText(config, 'guidanceLonsdaleSms')
  }

  /**
   * GuidanceBar — one reusable notice pattern (SMS / cutoff / email / reassurance).
   * opts.variant: info | timing | confirm
   * Max one bar per screen; stack at most two short lines in innerHtml.
   */
  function wrapGuidanceBarHtml(innerHtml, opts) {
    if (!innerHtml) return ''
    opts = opts || {}
    var variant = opts.variant || 'info'
    var variantClass = variant === 'timing' || variant === 'confirm' ? ' baw-guidance--' + variant : ''
    var extraClass = opts.className ? ' ' + String(opts.className).trim() : ''
    var liveAttr = opts.live ? ' aria-live="polite" aria-atomic="true"' : ''
    var footerAttr = opts.footer ? ' data-baw-step-footer-guidance' : ''
    return (
      '<div class="baw-guidance baw-step-footer-notes' + variantClass + extraClass + '" role="note" data-baw-guidance' + footerAttr + liveAttr + '>' +
      '<div class="baw-guidance-inner baw-step-footer-notes-inner">' +
      '<span class="baw-guidance-icon baw-step-footer-notes-icon" aria-hidden="true">i</span>' +
      '<div class="baw-guidance-body baw-step-footer-notes-body">' +
      innerHtml +
      '</div>' +
      '</div>' +
      '</div>'
    )
  }

  /** Step-specific calm-line as GuidanceBar (ashes-ready email, etc.). */
  function checkoutReassuranceFooterHtml(text) {
    var line = text || ''
    if (!line) return ''
    return wrapGuidanceBarHtml(
      '<p class="baw-guidance-line baw-step-footer-note">' + escapeHtml(line) + '</p>',
      { variant: 'confirm' }
    )
  }

  /**
   * Usable pet name for personalisation.
   * Allow short legitimate names (including 1–2 letters); only reject missing /
   * whitespace-only / names with no letters.
   */
  function isUsablePetName(name) {
    var s = String(name || '').replace(/\s+/g, ' ').trim()
    if (!s) return false
    return /[A-Za-zÀ-ÖØ-öø-ÿ]/.test(s)
  }

  /** English possessive with typographic apostrophe — James’ / Poppy’s. */
  function possessiveName(name) {
    var n = String(name || '').trim()
    if (!n) return ''
    return /[sS]$/.test(n) ? n + '\u2019' : n + '\u2019s'
  }

  function lonsdaleArrivalFooterNotesHtml(state, config) {
    var sms = lonsdaleArrivalSmsHelper(state, config)
    if (!sms) return ''
    return wrapGuidanceBarHtml(
      '<p class="baw-guidance-line baw-step-footer-note baw-guidance-line--emphasis baw-step-footer-note--emphasis baw-guidance-line--sms baw-step-footer-note--home-sms">' +
        escapeHtml(sms) +
        '</p>',
      { variant: 'confirm' }
    )
  }

  /**
   * Collection window line for the vet footer strip.
   * Concrete dates only when a ready-from date yields a numeric window.
   * Home uses SMS in the strip instead (sidebar shows the requested date alone).
   */
  function collectionRangeFooterNoteHtml(state, config) {
    var tf = collectionTimeframeForState(state)
    if (!tf || !tf.startYmd || !tf.endYmd) return ''
    var pet = petFirstNameRef(state)
    var leadTemplate = stepCopyText(config, 'guidanceVetCollectionAim')
    if (!leadTemplate) return ''
    var leadIn = leadTemplate.replace(/\{pet\}/g, pet.ref).replace(/\{pet_possessive\}/g, isUsablePetName(pet.name) ? possessiveName(pet.name) : 'your pet\u2019s')
    // Two deliberate lines so dates stay intact on narrow mobile widths.
    return (
      '<p class="baw-guidance-line baw-step-footer-note baw-guidance-primary baw-guidance-line--emphasis baw-step-footer-note--emphasis">' +
      escapeHtml(leadIn) +
      '</p>' +
      '<p class="baw-guidance-line baw-step-footer-note baw-guidance-date-range baw-guidance-line--emphasis baw-step-footer-note--emphasis">' +
      '<strong>' +
      escapeHtml(formatCollectionDateShort(tf.startYmd)) +
      '</strong>' +
      escapeHtml(' and ') +
      '<strong>' +
      escapeHtml(formatCollectionDateShort(tf.endYmd)) +
      '</strong>' +
      escapeHtml('.') +
      '</p>'
    )
  }

  /**
   * Anchored reassurances above Continue on vet/home ready-from date steps.
   * After Hours home: SMS only. Vet: range → Priority (Standard). In-care email
   * lives under the step heading (collectionInCareEmailSubheadingHtml). Home
   * (otherwise): SMS only — no date-range (sidebar: requested date).
   */
  function pickupCollectionFooterNotesHtml(state, includePriority, config) {
    if (!state || !state.transport) return ''
    if (isLonsdaleTransport(state)) return ''
    var isHome = state.transport.categoryId === 'home'
    var isVet = isVetTransport(state)
    if (!isVet && !isHome) return ''

    var parts = []

    // Hide home SMS while the Places address fails street-number or service-area checks.
    var hideHomeSms =
      isHome &&
      (!!state.transport.addressOutOfArea || !!state.transport.addressMissingStreetNumber)

    if (isAfterHoursTransport(state)) {
      // Home-only path — vet After Hours is filtered out of service options.
      var afterHoursSms = hideHomeSms ? '' : afterHoursCollectionSmsHelper(state, config)
      if (afterHoursSms) {
        parts.push(
          '<p class="baw-guidance-line baw-step-footer-note baw-guidance-line--emphasis baw-step-footer-note--emphasis baw-guidance-line--sms baw-step-footer-note--home-sms">' +
            escapeHtml(afterHoursSms) +
            '</p>'
        )
      }
      if (!parts.length) return ''
      return wrapGuidanceBarHtml(parts.join(''), { variant: 'confirm' })
    }

    // Date-range aim line is vet-only; home relies on SMS + sidebar requested date.
    var rangeHtml = isVet ? collectionRangeFooterNoteHtml(state, config) : ''
    if (rangeHtml) parts.push(rangeHtml)

    if (isHome) {
      var sms = hideHomeSms ? '' : collectionWindowSmsHelper(state, config)
      if (sms) {
        parts.push(
          '<p class="baw-guidance-line baw-step-footer-note baw-guidance-line--emphasis baw-step-footer-note--emphasis baw-guidance-line--sms baw-step-footer-note--home-sms">' +
            escapeHtml(sms) +
            '</p>'
        )
      }
    } else if (includePriority) {
      var priority = vetPriorityDateHintHtml(state)
      if (priority) {
        parts.push(
          '<p class="baw-guidance-line baw-step-footer-note baw-guidance-secondary baw-step-footer-note--secondary">' +
            priority +
            '</p>'
        )
      }
    }

    if (!parts.length) return ''
    return wrapGuidanceBarHtml(parts.join(''), {
      variant: isHome ? 'confirm' : 'timing',
    })
  }

  function timeWindowOptionsForState(state, config) {
    if (isLonsdaleTransport(state)) {
      var mins = lonsdaleGoodbyeMinutes(state)
      var custom = config.lonsdaleTimeWindows && config.lonsdaleTimeWindows[mins]
      if (custom && custom.length) return custom
      return DEFAULT_LONSDALE_TIME_WINDOWS[mins] || DEFAULT_LONSDALE_TIME_WINDOWS[5]
    }
    return []
  }

  function buildArrivalTimeSlotsHtml(selectedTime, timeOptions, dateSelected, emptyMessage) {
    if (!dateSelected) {
      return '<p class="baw-schedule-times-empty">Select a date to see preferred times</p>'
    }
    if (!timeOptions || !timeOptions.length) {
      return (
        '<p class="baw-schedule-times-empty">' +
        escapeHtml(emptyMessage || 'No preferred times available') +
        '</p>'
      )
    }
    return (
      '<div class="baw-schedule-times-list" role="listbox" aria-label="Preferred arrival time">' +
      timeOptions.map(function (t) {
        var selected = selectedTime === t
        return (
          '<button type="button" class="baw-time-slot' + (selected ? ' is-selected' : '') + '"' +
          ' data-baw-time="' + escapeHtml(t) + '"' +
          ' role="option" aria-selected="' + (selected ? 'true' : 'false') + '">' +
          escapeHtml(t) +
          '</button>'
        )
      }).join('') +
      '</div>'
    )
  }

  function lonsdaleHoursHelperText(config) {
    var custom = config && config.lonsdaleHoursHelper
    if (custom && String(custom).trim()) return String(custom).trim()
    return 'Weekdays · 10am–1pm'
  }

  function timeWindowHelperForState(state, config) {
    if (!isLonsdaleTransport(state)) return ''
    return lonsdaleHoursHelperText(config)
  }

  function cremationProductForType(config, type) {
    if (config.flow === 'shared') return config.products.shared
    if (type === 'flame') return config.products.flame
    if (type === 'water') return config.products.water
    return null
  }

  /** Flat-rate communal product — first sellable variant (no weight bands). */
  function resolveSharedCremationVariant(product) {
    if (!product || !Array.isArray(product.variants) || !product.variants.length) return null
    return firstAvailableVariant(product)
  }

  function ensureSharedCremationVariant(state, config) {
    if (!config || config.flow !== 'shared') return
    var product = config.products && config.products.shared
    if (!product) return
    state.cremationVariant = resolveSharedCremationVariant(product)
  }

  function sharedCremationDisplayLabel(config, variant) {
    var card = config && config.cremationCards && config.cremationCards.shared
    var override = card && String(card.title || '').trim()
    if (override) return override
    var title = variant && variant.title ? String(variant.title).trim() : ''
    if (title && title !== 'Default Title') return title
    return 'Shared cremation'
  }

  /** True when we could not auto-match a weight band and the client must pick a size. */
  function needsManualWeightPick(state, config) {
    if (config.flow === 'shared') return false
    if (!state.cremationType) return false
    var product = cremationProductForType(config, state.cremationType)
    if (!product || !product.variants || !product.variants.length) return false
    return !suggestedVariant(product, state.pet.weightKg)
  }

  /* ------------------------------ config --------------------------------- */

  function normalizeReviewTrust(rawTrust) {
    var trust = rawTrust && typeof rawTrust === 'object' ? rawTrust : {}
    var payments = trust.payments && typeof trust.payments === 'object' ? trust.payments : {}
    function flag(value) {
      return value !== false
    }
    return {
      // Theme Editor text settings — blank → null in Liquid JSON; trim so whitespace-only hides.
      ratingScore: String(trust.ratingScore == null ? '' : trust.ratingScore).trim(),
      reviewCount: String(trust.reviewCount == null ? '' : trust.reviewCount).trim(),
      reviewSource: String(trust.reviewSource == null ? '' : trust.reviewSource).trim(),
      showPayments: trust.showPayments !== false,
      vetPayImage: String(trust.vetPayImage == null ? '' : trust.vetPayImage).trim(),
      payments: {
        shopPay: flag(payments.shopPay),
        visa: flag(payments.visa),
        mastercard: flag(payments.mastercard),
        amex: flag(payments.amex),
        applePay: flag(payments.applePay),
        googlePay: flag(payments.googlePay),
        paypal: flag(payments.paypal),
        afterpay: flag(payments.afterpay),
        zip: flag(payments.zip),
        vetPay: flag(payments.vetPay),
      },
    }
  }

  var DEFAULT_STEP_COPY = {
    petHeadline: 'Tell us about your pet',
    petOverweightTitle: 'Our standard bookings are for pets up to {maxKg} kg',
    petOverweightCopy:
      'For heavier pets, please send us a short note about your situation. We\u2019ll review it carefully and let you know whether we can help.',
    cremationHeadline: 'How would you like us to care for {pet}?',
    cremationSubtitle: 'Choose the type of cremation that\u2019s right for your family.',
    cremationWeightHeadline: 'Which weight range fits {pet}?',
    cremationWeightSubtitle: 'Suggested from the weight you entered — change it if needed.',
    transportCategoryHeadline: 'How will {pet} come into our care?',
    transportCategorySubtitle: 'Bring them to us, or we\u2019ll come to you.',
    transportLonsdaleServiceHeadline: 'How long would you like with {pet}?',
    transportLonsdaleServiceSubtitle:
      'Private time in our Goodbye Room before cremation care continues.',
    transportVetServiceHeadline: 'How would you like us to collect {pet}?',
    transportHomeServiceHeadline: 'Choose your collection service',
    transportLonsdaleDetailsHeadline: 'When would you prefer to arrive?',
    transportVetDetailsHeadline: 'When will {pet} be ready for collection?',
    transportVetDetailsSubtitle: 'Once {pet} is in our care, we\u2019ll email you.',
    transportHomeDetailsHeadline: 'When and where should we collect {pet}?',
    returnSpeedHeadline: 'When do you need {pet} back?',
    returnSpeedHeadlineFallback: 'When do you need your pet back?',
    returnSpeedSubtitle: 'Timeframes begin once {pet} enters our care.',
    returnMethodHeadline: 'How should {pet} come home?',
    returnMethodHeadlineFallback: 'How should your pet come home?',
    reviewHeadline: 'Does everything look right?',
    reviewSubtitle: 'Review your booking, then continue to checkout.',
    memorialAddonPhotoHeadline: 'Add a photo for the {item}?',
    memorialAddonPhotoSubtitle:
      'We can print and fit a favourite photo for you. This is optional\u2014you can continue without adding one.',
    memorialAddonNameplateHeadline: 'Add a nameplate for the {item}?',
    memorialAddonNameplateIncludedHeadline: 'Personalise the nameplate?',
    memorialAddonEngravingHeadline: 'Add engraving for the {item}?',
    memorialAddonEngravingSubtitle: '',
    guidanceLonsdaleSms: 'We\u2019ll SMS you to confirm your requested date and arrival time.',
    guidanceHomeSms: 'We\u2019ll SMS you to confirm your requested date and arrange a suitable time.',
    guidanceVetCollectionAim: 'We\u2019ll aim to collect {pet} between',
    guidanceAshesReadyEmail: 'We\u2019ll email you when {pet} is ready to come home.',
    guidanceAshesReadyEmailFallback: 'We\u2019ll email you when your pet is ready to come home.',
    guidanceAshesReadyEstimate: 'We expect {pet_possessive} ashes to be ready between',
    guidanceEngravingHand:
      'Each inscription is engraved by hand, so lettering and placement will vary slightly.',
    sharedReviewConfirm:
      'Please note: With shared cremation, {pet_possessive} ashes are not returned.',
    sharedReviewConfirmFallback:
      'Please note: With shared cremation, your pet\u2019s ashes are not returned.',
  }

  /**
   * External urn-plate engraving limits.
   * L1 16 / L2 18 — hand engraving is letter-by-letter; fits short names +
   * Forever loved / Rest in peace / Forever missed. Drafts longer than max
   * are truncated on restore (sanitizeEngravingLine).
   */
  var ENGRAVING_LINE1_MAX = 16
  var ENGRAVING_LINE2_MAX = 18

  /** Default Engraving card description when Theme Editor subtitle is blank. */
  var ENGRAVING_CARD_SUBTITLE_DEFAULT = 'Name + optional line 2.'

  /**
   * Flow is owned by the page template (booking-individual / booking-shared).
   * Theme Editor “flow” settings must not override the template the page uses.
   */
  function resolveBookingFlow(raw) {
    if (!raw || typeof raw !== 'object') return 'individual'
    var suffix = String(raw.templateSuffix || '').trim().toLowerCase()
    if (suffix === 'booking-shared') return 'shared'
    if (suffix === 'booking-individual') return 'individual'
    var handle = String(raw.pageHandle || '').trim().toLowerCase()
    if (handle === 'booking-shared') return 'shared'
    if (handle === 'booking-individual') return 'individual'
    return raw.flow === 'shared' ? 'shared' : 'individual'
  }

  function normalizeStepCopy(raw) {
    var src = (raw && raw.stepCopy) || {}
    var out = {}
    Object.keys(DEFAULT_STEP_COPY).forEach(function (key) {
      var v = src[key]
      // Legacy top-level keys for fields that already shipped (only when key absent).
      if (v == null && key === 'cremationSubtitle' && raw && raw.cremationStepSubtitle != null) {
        v = raw.cremationStepSubtitle
      }
      if (v == null && key === 'transportLonsdaleDetailsHeadline' && raw && raw.lonsdaleDetailsHeadline != null) {
        v = raw.lonsdaleDetailsHeadline
      }
      // Present (including "") = merchant Theme Editor value — blank stays blank.
      // Missing/null = older config without the key — keep code default once.
      out[key] = v == null ? DEFAULT_STEP_COPY[key] : String(v).trim()
    })
    // Rewrite exact prior TE defaults so live themes pick up the shorter line
    // without a merchant re-save. Custom TE copy is left alone.
    if (
      out.transportLonsdaleServiceSubtitle ===
        'Private time together in our Goodbye Room, before cremation care continues.' ||
      out.transportLonsdaleServiceSubtitle ===
        'Private time with {pet} in our Goodbye Room before cremation care continues.'
    ) {
      out.transportLonsdaleServiceSubtitle =
        DEFAULT_STEP_COPY.transportLonsdaleServiceSubtitle
    }
    // Engraving ask copy is code-owned (like PHOTO_PRINT_GUIDANCE_LINE). Drop stale
    // Theme Editor values such as “Personalise the {item}” / “Optional — leave blank…”.
    out.memorialAddonEngravingHeadline = DEFAULT_STEP_COPY.memorialAddonEngravingHeadline
    out.memorialAddonEngravingSubtitle = ''
    if (raw && raw.flow === 'shared') {
      // Communal cremation — memorial delivery only when keepsakes are purchased.
      out.returnMethodHeadline =
        'How would you like your memorials delivered?'
      out.returnMethodHeadlineFallback =
        'How would you like your memorials delivered?'
      out.guidanceAshesReadyEmail =
        'We\u2019ll email you when your memorial keepsakes are ready.'
      out.guidanceAshesReadyEmailFallback =
        'We\u2019ll email you when your memorial keepsakes are ready.'
      out.guidanceAshesReadyEstimate = ''
      out.sharedReviewConfirm =
        'Please note: With shared cremation, {pet_possessive} ashes are not returned.'
      out.sharedReviewConfirmFallback =
        'Please note: With shared cremation, your pet\u2019s ashes are not returned.'
    }
    return out
  }

  function stepCopyText(config, key) {
    var sc = (config && config.stepCopy) || DEFAULT_STEP_COPY
    var v = sc[key]
    if (v == null) return DEFAULT_STEP_COPY[key] || ''
    var text = String(v).trim()
    if (COPY_LEGACY_REWRITES[text]) return COPY_LEGACY_REWRITES[text]
    return text
  }

  /** Exact stale Theme Editor strings — rewrite so merchants needn’t re-save. */
  var COPY_LEGACY_REWRITES = {
    'We\u2019ll aim for {pet_possessive} ashes to be ready between':
      'We expect {pet_possessive} ashes to be ready between',
    'We\'ll aim for {pet_possessive} ashes to be ready between':
      'We expect {pet_possessive} ashes to be ready between',
    'Does everything look right for {pet}?':
      'Does everything look right?',
    'Review your booking, then continue to secure checkout.':
      'Review your booking, then continue to checkout.',
    'You\u2019re booking a shared cremation for {pet}. Ashes aren\u2019t returned with this service \u2014 we\u2019ll prepare {pet_possessive} cremation certificate and any keepsakes you\u2019ve chosen.':
      'Please note: With shared cremation, {pet_possessive} ashes are not returned.',
    'You\u2019re booking a shared cremation. Ashes aren\u2019t returned with this service \u2014 we\u2019ll prepare the cremation certificate and any keepsakes you\u2019ve chosen.':
      'Please note: With shared cremation, your pet\u2019s ashes are not returned.',
    'You\u2019re booking a shared cremation for {pet}. Ashes aren\u2019t returned with this service.':
      'Please note: With shared cremation, {pet_possessive} ashes are not returned.',
    'You\u2019re booking a shared cremation. Ashes aren\u2019t returned with this service.':
      'Please note: With shared cremation, your pet\u2019s ashes are not returned.',
  }

  /** Legacy Theme Editor default — treat as blank so the caption stays hidden. */
  var GOODBYE_ROOM_CAPTION_LEGACY_DEFAULT = 'Our Goodbye Room at Lonsdale'

  /**
   * Theme Editor Goodbye Room photos for the Lonsdale duration step.
   * Hero required to show anything; secondary + caption are optional.
   * Caption may be blank (hide). Exact legacy TE default is treated as blank.
   */
  function normalizeGoodbyeRoomVisuals(raw) {
    var src = raw && typeof raw === 'object' ? raw : {}
    var heroUrl = src.heroUrl != null ? String(src.heroUrl).trim() : ''
    var secondaryUrl = src.secondaryUrl != null ? String(src.secondaryUrl).trim() : ''
    var caption = src.caption == null ? '' : String(src.caption).trim()
    if (caption === GOODBYE_ROOM_CAPTION_LEGACY_DEFAULT) caption = ''
    return {
      heroUrl: heroUrl,
      // Companion only when a hero is set — avoids a lone small frame.
      secondaryUrl: heroUrl && secondaryUrl ? secondaryUrl : '',
      caption: caption,
    }
  }

  /** Soft Goodbye Room hero + slim door/logo guide above duration tiles. */
  function goodbyeRoomVisualHtml(config) {
    var visuals = (config && config.goodbyeRoomVisuals) || normalizeGoodbyeRoomVisuals(null)
    if (!visuals.heroUrl) return ''
    var caption = visuals.caption || ''
    var alt = caption || 'Goodbye Room'
    // Eager + high priority: this is the step hero (never lazy — lazy deferred paint).
    var frames =
      '<div class="baw-goodbye-visual__frames' +
      (visuals.secondaryUrl ? ' baw-goodbye-visual__frames--duo' : '') +
      '">' +
      '<img class="baw-goodbye-visual__hero" src="' +
      escapeHtml(sizedShopifyImageUrl(visuals.heroUrl, memorialCdnPx('GOODBYE_VISUAL_PX', 800))) +
      '" alt="' +
      escapeHtml(alt) +
      '" loading="eager" fetchpriority="high" decoding="async" width="800" height="450" />'
    if (visuals.secondaryUrl) {
      // Caption (or hero alt) covers both; secondary is decorative context.
      frames +=
        '<img class="baw-goodbye-visual__secondary" src="' +
        escapeHtml(sizedShopifyImageUrl(visuals.secondaryUrl, memorialCdnPx('GOODBYE_VISUAL_PX', 800))) +
        '" alt="" loading="eager" decoding="async" width="800" height="600" />'
    }
    frames += '</div>'
    return (
      '<figure class="baw-goodbye-visual">' +
      frames +
      (caption
        ? '<figcaption class="baw-goodbye-visual__caption">' + escapeHtml(caption) + '</figcaption>'
        : '') +
      '</figure>'
    )
  }
  function normalizeConfig(raw) {
    raw = raw && typeof raw === 'object' ? raw : {}
    var resolvedFlow = resolveBookingFlow(raw)
    raw = Object.assign({}, raw, { flow: resolvedFlow })
    // Section-local blocks (memorial_group / memorial_item / return_method_option)
    // are emitted in data-baw-config. Product blocks share Category via group_key.
    var blocksByType = {}
    ;(raw.blocks || []).forEach(function (block) {
      if (!blocksByType[block.type]) blocksByType[block.type] = []
      blocksByType[block.type].push(block)
    })

    var memorialItemsByGroup = {}
    ;(blocksByType.memorial_item || []).forEach(function (block) {
      var key = block.settings && block.settings.group_key
      if (!key) return
      if (!memorialItemsByGroup[key]) memorialItemsByGroup[key] = []
      memorialItemsByGroup[key].push(block)
    })

    function normalizeCardBenefits(list) {
      return (Array.isArray(list) ? list : [])
        .map(function (v) { return v == null ? '' : String(v).trim() })
        .filter(Boolean)
    }

    var waterCardRaw = (raw.cremationCards && raw.cremationCards.water) || {}
    var flameCardRaw = (raw.cremationCards && raw.cremationCards.flame) || {}
    var sharedCardRaw = (raw.cremationCards && raw.cremationCards.shared) || {}
    var timingWater = (raw.cremationTiming && raw.cremationTiming.water) || 'Ready within 10–14 days'
    var timingFlame = (raw.cremationTiming && raw.cremationTiming.flame) || 'Ready within 14–21 days'
    var waterBenefits = normalizeCardBenefits(waterCardRaw.benefits)
    var flameBenefits = normalizeCardBenefits(flameCardRaw.benefits)
    if (!waterBenefits.length) waterBenefits = ['A gentle farewell', timingWater]
    if (!flameBenefits.length) flameBenefits = [timingFlame]

    var stepCopy = normalizeStepCopy(raw)

    return {
      flow: resolvedFlow,
      templateSuffix: String(raw.templateSuffix || ''),
      pageHandle: String(raw.pageHandle || ''),
      heading:
        resolvedFlow === 'shared'
          ? 'Book a shared cremation'
          : 'Book an individual cremation',
      showSubheading: !!raw.showSubheading,
      subheading: raw.subheading || '',
      showPetStepTestimonial: raw.showPetStepTestimonial !== false,
      logoUrl: (raw.logoUrl && String(raw.logoUrl).trim()) || '',
      nameplateSecondLineSuggestions: parseLineList(raw.nameplateSecondLineSuggestions),
      nameplateBlankGold: (raw.nameplateBlankGold && String(raw.nameplateBlankGold).trim()) || null,
      nameplateBlankSilver: (raw.nameplateBlankSilver && String(raw.nameplateBlankSilver).trim()) || null,
      checkoutRedirect: raw.checkoutRedirect === 'cart' ? 'cart' : 'checkout',
      reviewShopLinkEnabled: !!raw.reviewShopLinkEnabled,
      reviewShopLinkLabel: (raw.reviewShopLinkLabel && String(raw.reviewShopLinkLabel).trim()) || 'Memorial Store',
      reviewShopLinkUrl: (function () {
        var url = raw.reviewShopLinkUrl && String(raw.reviewShopLinkUrl).trim()
        if (!url) return ''
        if (url.charAt(0) === '/' && url.charAt(1) !== '/') return url
        if (/^https?:\/\//i.test(url)) return url
        return ''
      })(),
      photoUploadUrl: raw.photoUploadUrl || '',
      maxPetWeightKg: Math.max(1, Number(raw.maxPetWeightKg) || 80),
      moneyFormat: raw.moneyFormat || '${{amount}}',
      googleMapsApiKey: (raw.googleMapsApiKey && String(raw.googleMapsApiKey).trim()) || '',
      homeCollectionServiceArea: parseServiceAreaPolygon(raw.homeCollectionServiceArea),
      lonsdaleHoursHelper: (raw.lonsdaleHoursHelper && String(raw.lonsdaleHoursHelper).trim()) || 'Weekdays · 10am–1pm',
      lonsdaleTimeWindows: {
        5: parseLineList(raw.lonsdaleTimeWindows && raw.lonsdaleTimeWindows[5]),
        30: parseLineList(raw.lonsdaleTimeWindows && raw.lonsdaleTimeWindows[30]),
        60: parseLineList(raw.lonsdaleTimeWindows && raw.lonsdaleTimeWindows[60]),
      },
      goodbyeRoomVisuals: normalizeGoodbyeRoomVisuals(raw.goodbyeRoomVisuals),
      engravingExampleVisuals: normalizeEngravingExampleVisuals(raw.engravingExampleVisuals),
      bookingCalendar: {
        closedDates: parseClosedDateLines(raw.closedDates || []),
        // Allow 0 (= today). `|| 1` would wrongly coerce a deliberate 0 to 1.
        minLeadDays: (function () {
          var n = Number(raw.bookingMinLeadDays)
          if (!isFinite(n)) n = 1
          return Math.max(0, Math.min(14, n))
        })(),
        horizonDays: 90,
        // Adelaide wall-clock hours (0–23). Same-day blocked at/after this hour.
        // Theme toggle: when false, calendars ignore path cutoffs (no block, no hint).
        sameDayCutoffEnabled: raw.sameDayCutoffEnabled !== false,
        sameDayCutoffHome: parseSameDayCutoffHour(raw.sameDayCutoffHome, 13),
        sameDayCutoffLonsdale: parseSameDayCutoffHour(raw.sameDayCutoffLonsdale, 13),
      },
      individualBookingUrl:
        (raw.individualBookingUrl && String(raw.individualBookingUrl).trim()) ||
        '/pages/booking-individual',
      products: raw.products || {},
      transportLabels: {
        lonsdale: (raw.transportLabels && raw.transportLabels.lonsdale) || '',
        vet: (raw.transportLabels && raw.transportLabels.vet) || '',
        home: (raw.transportLabels && raw.transportLabels.home) || '',
      },
      transportNotes: {
        home: (raw.transportNotes && raw.transportNotes.home) || '',
      },
      returnSettings: {
        priorityWaterOnly: !!(raw.returnSettings && raw.returnSettings.priorityWaterOnly),
      },
      cremationTiming: {
        water: timingWater,
        flame: timingFlame,
        note: (raw.cremationTiming && raw.cremationTiming.note) || '',
      },
      stepCopy: stepCopy,
      cremationStepSubtitle: stepCopy.cremationSubtitle,
      lonsdaleDetailsHeadline: stepCopy.transportLonsdaleDetailsHeadline,
      cremationCards: {
        water: {
          title: (waterCardRaw.title && String(waterCardRaw.title).trim()) || 'Water Cremation',
          sublabel: waterCardRaw.sublabel == null
            ? 'Chosen by most families'
            : String(waterCardRaw.sublabel).trim(),
          benefits: waterBenefits,
        },
        flame: {
          title: (flameCardRaw.title && String(flameCardRaw.title).trim()) || 'Flame Cremation',
          sublabel: flameCardRaw.sublabel == null
            ? 'Traditional cremation'
            : String(flameCardRaw.sublabel).trim(),
          benefits: flameBenefits,
        },
        shared: {
          title: (sharedCardRaw.title && String(sharedCardRaw.title).trim()) || 'Shared cremation',
          sublabel: sharedCardRaw.sublabel == null
            ? ''
            : String(sharedCardRaw.sublabel).trim(),
        },
      },
      // Must pass through — payments default-on when missing, but rating score cannot.
      reviewTrust: normalizeReviewTrust(raw.reviewTrust),
      memorialGroups: (blocksByType.memorial_group || []).filter(function (g) {
        var flows = g.settings && g.settings.flows
        var enabled = g.settings && g.settings.enabled
        if (enabled === false || enabled === 'false') return false
        if (!(g.settings && g.settings.group_key)) return false
        return flows === 'both' || flows === raw.flow || !flows
      }),
      memorialItemsByGroup: memorialItemsByGroup,
      memorials: (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.normalizeMemorialSettings)
        ? AngelAshesBookingMemorials.normalizeMemorialSettings(raw)
        : { enabled: true, emptyReviewLabel: 'No additional keepsakes selected' },
      returnMethodOptions: blocksByType.return_method_option || [],
      // Theme Editor memorial_item images for predictive warm (collectMemorialBlockWarmParts).
      blocks: raw.blocks || [],
    }
  }

  function createInitialState() {
    return {
      currentStepId: 'pet',
      pet: { name: '', breed: '', weightKg: null },
      cremationType: null,
      cremationVariant: null,
      transport: {
        categoryId: null, categoryLabel: null,
        key: null, label: null, variant: null,
        subKey: null, subLabel: null, subVariant: null,
        date: null, calendarMonth: null, timeWindow: null,
        vetClinic: null, vetAddress: null, vetClinicId: null,
        vetClinicEditing: false, vetClinicOutOfArea: false,
        newAddress: '',
        newAddressLat: null, newAddressLng: null,
        addressOutOfArea: false, addressMissingStreetNumber: false, addressEditing: false,
      },
      memorials: {},
      portraitOffer: null,
      memorialUnavailableNotice: false,
      returnSpeed: { key: null, label: null, variant: null },
      returnMethod: {
        key: null,
        label: null,
        variant: null,
        location: null,
        locationBlockId: null,
        displayOverride: null,
      },
      specialInstructions: '',
      specialInstructionsOpen: false,
      sharedAshesAcknowledged: false,
      sharedAshesAckInvalid: false,
    }
  }


  /* -------------------- split asset wiring (Theme Editor 256 KB) -------------------- */

  function requireWizardPart(name, globalObj) {
    if (!globalObj || typeof globalObj.create !== 'function') {
      throw new Error('Booking wizard: missing ' + name + ' (load order: memorials → memorial-ui → nameplate → arrival → cart → booking-wizard)')
    }
    return globalObj
  }

  var MemorialUIMod = requireWizardPart('booking-wizard-memorial-ui.js', typeof AngelAshesBookingMemorialUI !== 'undefined' ? AngelAshesBookingMemorialUI : null)
  var NameplateMod = requireWizardPart('booking-wizard-nameplate.js', typeof AngelAshesBookingNameplate !== 'undefined' ? AngelAshesBookingNameplate : null)
  var ArrivalMod = requireWizardPart('booking-wizard-arrival.js', typeof AngelAshesBookingArrival !== 'undefined' ? AngelAshesBookingArrival : null)
  var CartMod = requireWizardPart('booking-wizard-cart.js', typeof AngelAshesBookingCart !== 'undefined' ? AngelAshesBookingCart : null)

  var MemorialUI = MemorialUIMod.create({
    escapeHtml: escapeHtml,
    stepIntroHtml: stepIntroHtml,
    optionalSkipButtonHtml: optionalSkipButtonHtml,
    formatMoney: formatMoney,
    firstAvailableVariant: firstAvailableVariant,
    stepCopyText: stepCopyText,
    DEFAULT_STEP_COPY: DEFAULT_STEP_COPY,
    parseLineList: parseLineList,
    possessiveName: possessiveName,
    isUsablePetName: isUsablePetName,
    sizedShopifyImageUrl: sizedShopifyImageUrl,
  })

  var getMemorialCatalog = MemorialUI.getMemorialCatalog
  var getMemorialEntry = MemorialUI.getMemorialEntry
  var urnAshSplitDisplayValue = MemorialUI.urnAshSplitDisplayValue
  var urnAshesPlacementDisplayValue = MemorialUI.urnAshesPlacementDisplayValue
  var urnAshDestinationDisplayValue = MemorialUI.urnAshDestinationDisplayValue
  var ashesPlacementSummaryLine = MemorialUI.ashesPlacementSummaryLine
  var ashesPlacementSummaryLines = MemorialUI.ashesPlacementSummaryLines
  var withAshesPlacementSummaryLine = MemorialUI.withAshesPlacementSummaryLine
  var memorialCategorySummaryLabel = MemorialUI.memorialCategorySummaryLabel
  var findMemorialProductBlock = MemorialUI.findMemorialProductBlock
  var normalizePhotoPrintSize = MemorialUI.normalizePhotoPrintSize
  var normalizePhotoOrientation = MemorialUI.normalizePhotoOrientation
  var normalizePhotoOrientationDefault = MemorialUI.normalizePhotoOrientationDefault
  var photoSizeInches = MemorialUI.photoSizeInches
  var photoAspectRatio = MemorialUI.photoAspectRatio
  var photoSizeLabel = MemorialUI.photoSizeLabel
  var effectivePhotoOrientation = MemorialUI.effectivePhotoOrientation
  var memorialPhotoOrientationPrefix = MemorialUI.memorialPhotoOrientationPrefix
  var memorialPhotoSummaryDisplay = MemorialUI.memorialPhotoSummaryDisplay
  var resolveMemorialItemAddonFlags = MemorialUI.resolveMemorialItemAddonFlags
  var resolvePhotoAddonVariant = MemorialUI.resolvePhotoAddonVariant
  var isMemorialMultiSelectMode = MemorialUI.isMemorialMultiSelectMode
  var isMemorialRequiredSelectionMode = MemorialUI.isMemorialRequiredSelectionMode
  var parseMemorialAddonStepId = MemorialUI.parseMemorialAddonStepId
  var memorialAddonStepId = MemorialUI.memorialAddonStepId
  var focusMemorialAddonProduct = MemorialUI.focusMemorialAddonProduct
  var memorialAddonSourceForProduct = MemorialUI.memorialAddonSourceForProduct
  var firstMemorialAddonSource = MemorialUI.firstMemorialAddonSource
  var resolveMemorialAddonProductId = MemorialUI.resolveMemorialAddonProductId
  var memorialAddonParentLineId = MemorialUI.memorialAddonParentLineId
  var isMemorialAddonLineId = MemorialUI.isMemorialAddonLineId
  var nestMemorialLines = MemorialUI.nestMemorialLines
  var isMemorialIncludedProductLine = MemorialUI.isMemorialIncludedProductLine
  var orderMemorialLinesForSummary = MemorialUI.orderMemorialLinesForSummary
  var memorialAddonItemContext = MemorialUI.memorialAddonItemContext
  var memorialAddonHeadline = MemorialUI.memorialAddonHeadline
  var renderMemorialPersonaliseIntroHtml = MemorialUI.renderMemorialPersonaliseIntroHtml
  var truncateEngravingLine = MemorialUI.truncateEngravingLine
  var sanitizeEngravingLine = MemorialUI.sanitizeEngravingLine
  var formatEngravingCombinedText = MemorialUI.formatEngravingCombinedText
  var splitLegacyEngravingText = MemorialUI.splitLegacyEngravingText
  var normalizeEngravingExampleVisuals = MemorialUI.normalizeEngravingExampleVisuals
  var normalizeMemorialAddonItemPossessive = MemorialUI.normalizeMemorialAddonItemPossessive
  var memorialAddonHeadlineFromCopy = MemorialUI.memorialAddonHeadlineFromCopy
  var PHOTO_PRINT_GUIDANCE_LINE = MemorialUI.PHOTO_PRINT_GUIDANCE_LINE
  var PHOTO_UPLOAD_MAX_BYTES = MemorialUI.PHOTO_UPLOAD_MAX_BYTES
  var PHOTO_UPLOAD_SAFE_BYTES = MemorialUI.PHOTO_UPLOAD_SAFE_BYTES
  var PHOTO_UPLOAD_TIMEOUT_MS = MemorialUI.PHOTO_UPLOAD_TIMEOUT_MS
  var PHOTO_UPLOAD_ACCEPT = MemorialUI.PHOTO_UPLOAD_ACCEPT
  var PHOTO_UPLOAD_ACCEPT_SET = MemorialUI.PHOTO_UPLOAD_ACCEPT_SET
  var ENGRAVING_EXAMPLE_CAPTION_DEFAULT = MemorialUI.ENGRAVING_EXAMPLE_CAPTION_DEFAULT
  var MEMORIAL_CATEGORY_SUMMARY_LABELS = MemorialUI.MEMORIAL_CATEGORY_SUMMARY_LABELS

  var Nameplate = NameplateMod.create({
    escapeHtml: escapeHtml,
    optionalSkipButtonHtml: optionalSkipButtonHtml,
    firstAvailableVariant: firstAvailableVariant,
    parseLineList: parseLineList,
    stepCopyText: stepCopyText,
    possessiveName: possessiveName,
    isUsablePetName: isUsablePetName,
    getMemorialCatalog: getMemorialCatalog,
    getMemorialEntry: getMemorialEntry,
    focusMemorialAddonProduct: focusMemorialAddonProduct,
    memorialAddonSourceForProduct: memorialAddonSourceForProduct,
    resolveMemorialAddonProductId: resolveMemorialAddonProductId,
    memorialAddonHeadline: memorialAddonHeadline,
    memorialAddonItemContext: memorialAddonItemContext,
    renderMemorialPersonaliseIntroHtml: renderMemorialPersonaliseIntroHtml,
    sanitizeEngravingLine: sanitizeEngravingLine,
    ENGRAVING_LINE2_MAX: ENGRAVING_LINE2_MAX,
  })

  var normalizeNameplateFont = Nameplate.normalizeNameplateFont
  var normalizeNameplateLines = Nameplate.normalizeNameplateLines
  var nameplateFontDef = Nameplate.nameplateFontDef
  var nameplateSecondLineSuggestions = Nameplate.nameplateSecondLineSuggestions
  var engravingSecondLineSuggestions = Nameplate.engravingSecondLineSuggestions
  var resolveNameplateSecondLineMode = Nameplate.resolveNameplateSecondLineMode
  var nameplateProductsForFlags = Nameplate.nameplateProductsForFlags
  var findNameplateVariantById = Nameplate.findNameplateVariantById
  var resolveNameplateAddonVariant = Nameplate.resolveNameplateAddonVariant
  var nameplateSummaryLabelForAddon = Nameplate.nameplateSummaryLabelForAddon
  var nameplateColourLabelFromFlags = Nameplate.nameplateColourLabelFromFlags
  var nameplateAddonTileImage = Nameplate.nameplateAddonTileImage
  var buildNameplateColourOptions = Nameplate.buildNameplateColourOptions
  var ensureDefaultNameplateColour = Nameplate.ensureDefaultNameplateColour
  var nameplatePreviewImageForOption = Nameplate.nameplatePreviewImageForOption
  var nameplateArtboardUrl = Nameplate.nameplateArtboardUrl
  var NAMEPLATE_FONTS = Nameplate.NAMEPLATE_FONTS
  var NAMEPLATE_BODY_FONT = Nameplate.NAMEPLATE_BODY_FONT
  var NAMEPLATE_SECOND_LINE_SUGGESTIONS_DEFAULT = Nameplate.NAMEPLATE_SECOND_LINE_SUGGESTIONS_DEFAULT
  var NAMEPLATE_SECOND_LINE_CUSTOM_VALUE = Nameplate.NAMEPLATE_SECOND_LINE_CUSTOM_VALUE
  var ENGRAVING_SECOND_LINE_NONE_OPTION_LABEL = Nameplate.ENGRAVING_SECOND_LINE_NONE_OPTION_LABEL
  var NAMEPLATE_LINE1_MAX = Nameplate.NAMEPLATE_LINE1_MAX
  var NAMEPLATE_LINE2_MAX = Nameplate.NAMEPLATE_LINE2_MAX
  var sanitizeNameplateLine = Nameplate.sanitizeNameplateLine
  var clampNameplateInscription = Nameplate.clampNameplateInscription

  var Arrival = ArrivalMod.create({
    escapeHtml: escapeHtml,
    stepIntroHtml: stepIntroHtml,
    optionalSkipButtonHtml: optionalSkipButtonHtml,
    formatMoney: formatMoney,
    firstAvailableVariant: firstAvailableVariant,
    isLonsdaleTransport: isLonsdaleTransport,
    isVetTransport: isVetTransport,
    isPriorityCollectionSelected: isPriorityCollectionSelected,
    lonsdaleGoodbyeMinutes: lonsdaleGoodbyeMinutes,
    collectionTimeframeForState: collectionTimeframeForState,
    timeWindowOptionsForState: timeWindowOptionsForState,
    timeWindowHelperForState: timeWindowHelperForState,
    buildArrivalTimeSlotsHtml: buildArrivalTimeSlotsHtml,
    lonsdaleHoursHelperText: lonsdaleHoursHelperText,
    lonsdaleArrivalFooterNotesHtml: lonsdaleArrivalFooterNotesHtml,
    pickupCollectionFooterNotesHtml: pickupCollectionFooterNotesHtml,
    afterHoursCollectionSmsHelper: afterHoursCollectionSmsHelper,
    collectionWindowSmsHelper: collectionWindowSmsHelper,
    lonsdaleArrivalSmsHelper: lonsdaleArrivalSmsHelper,
    vetPriorityDateHintHtml: vetPriorityDateHintHtml,
    wrapGuidanceBarHtml: wrapGuidanceBarHtml,
    goodbyeRoomVisualHtml: goodbyeRoomVisualHtml,
    cremationProductForType: cremationProductForType,
    ensureSharedCremationVariant: ensureSharedCremationVariant,
    sharedCremationDisplayLabel: sharedCremationDisplayLabel,
    needsManualWeightPick: needsManualWeightPick,
    suggestedVariant: suggestedVariant,
    suggestedVariantId: suggestedVariantId,
    parseWeightRange: parseWeightRange,
    weightInRange: weightInRange,
    syncCremationVariantForWeight: syncCremationVariantForWeight,
    enhanceOptionGroup: enhanceOptionGroup,
    enhanceCalendarKeyboard: enhanceCalendarKeyboard,
    stepCopyText: stepCopyText,
    possessiveName: possessiveName,
    isUsablePetName: isUsablePetName,
    formatCremationTimingDisplay: formatCremationTimingDisplay,
    cremationReadyWithinLabel: cremationReadyWithinLabel,
    getMemorialCatalog: getMemorialCatalog,
    getMemorialEntry: getMemorialEntry,
    memorialAddonStepId: memorialAddonStepId,
    resolveMemorialItemAddonFlags: resolveMemorialItemAddonFlags,
    isMemorialMultiSelectMode: isMemorialMultiSelectMode,
    isMemorialRequiredSelectionMode: isMemorialRequiredSelectionMode,
  })

  var transportCategoryDef = Arrival.transportCategoryDef
  var variantDisplayLabel = Arrival.variantDisplayLabel
  var mapLonsdaleGoodbyeDisplay = Arrival.mapLonsdaleGoodbyeDisplay
  var lonsdaleGoodbyeOptionLabel = Arrival.lonsdaleGoodbyeOptionLabel
  var isAfterHoursOptionLabel = Arrival.isAfterHoursOptionLabel
  var isPriorityOptionLabel = Arrival.isPriorityOptionLabel
  var findAfterHoursTransportOption = Arrival.findAfterHoursTransportOption
  var findPriorityTransportOption = Arrival.findPriorityTransportOption
  var serviceOptionsFromProduct = Arrival.serviceOptionsFromProduct
  var isLegacyLonsdaleDropOffLabel = Arrival.isLegacyLonsdaleDropOffLabel
  var normalizeLonsdaleFamilyLabel = Arrival.normalizeLonsdaleFamilyLabel
  var transportCategoryLabel = Arrival.transportCategoryLabel
  var familyFacingTransportCategoryLabel = Arrival.familyFacingTransportCategoryLabel
  var lonsdaleServiceSummaryLabel = Arrival.lonsdaleServiceSummaryLabel
  var resolveTransportSmsNote = Arrival.resolveTransportSmsNote
  var buildTransportCategories = Arrival.buildTransportCategories
  var getTransportCategories = Arrival.getTransportCategories
  var findTransportCategory = Arrival.findTransportCategory
  var optionsForTransportCategory = Arrival.optionsForTransportCategory
  var getTransportMainOptions = Arrival.getTransportMainOptions
  var needsTransportCategoryStep = Arrival.needsTransportCategoryStep
  var categoryNeedsServiceStep = Arrival.categoryNeedsServiceStep
  var categoryTilePriceLabel = Arrival.categoryTilePriceLabel
  var findTransportMainOption = Arrival.findTransportMainOption
  var requirementsForTransportState = Arrival.requirementsForTransportState
  var transportLeaf = Arrival.transportLeaf
  var centsPriceLabel = Arrival.centsPriceLabel
  var formatPickupAddress = Arrival.formatPickupAddress
  var formatSummaryAddress = Arrival.formatSummaryAddress
  var formatAddressConfirmHtml = Arrival.formatAddressConfirmHtml
  var streetLineHasNumberOrUnit = Arrival.streetLineHasNumberOrUnit
  var formattedAddressHasStreetNumberOrUnit = Arrival.formattedAddressHasStreetNumberOrUnit
  var addressComponentsHaveStreetNumberOrUnit = Arrival.addressComponentsHaveStreetNumberOrUnit
  var placeHasStreetNumberOrUnit = Arrival.placeHasStreetNumberOrUnit
  var isHomeAddressInvalid = Arrival.isHomeAddressInvalid
  var isHomeAddressCollapsed = Arrival.isHomeAddressCollapsed
  var parseServiceAreaPolygon = Arrival.parseServiceAreaPolygon
  var hasHomeServiceArea = Arrival.hasHomeServiceArea
  var pointInPolygon = Arrival.pointInPolygon
  var isHomeAddressInServiceArea = Arrival.isHomeAddressInServiceArea
  var clearPickupAddressState = Arrival.clearPickupAddressState
  var isPickupAddressReady = Arrival.isPickupAddressReady
  var pad2 = Arrival.pad2
  var toYmd = Arrival.toYmd
  var parseYmdLocal = Arrival.parseYmdLocal
  var addDaysLocal = Arrival.addDaysLocal
  var addWorkingDaysLocal = Arrival.addWorkingDaysLocal
  var parseFlexibleDateLocal = Arrival.parseFlexibleDateLocal
  var parseClosedDateLines = Arrival.parseClosedDateLines
  var isAfterHoursTransport = Arrival.isAfterHoursTransport
  var reconcileVetAfterHoursSelection = Arrival.reconcileVetAfterHoursSelection
  var isHomeNonAfterHoursTransport = Arrival.isHomeNonAfterHoursTransport
  var parseSameDayCutoffHour = Arrival.parseSameDayCutoffHour
  var adelaideDateTimeParts = Arrival.adelaideDateTimeParts
  var parsePreferredTimeLabelMinutes = Arrival.parsePreferredTimeLabelMinutes
  var filterArrivalTimeWindowsForDate = Arrival.filterArrivalTimeWindowsForDate
  var bookableArrivalTimeWindowsForState = Arrival.bookableArrivalTimeWindowsForState
  var reconcileArrivalTimeWindowSelection = Arrival.reconcileArrivalTimeWindowSelection
  var isAdelaideTodayYmd = Arrival.isAdelaideTodayYmd
  var isPastSameDayCutoff = Arrival.isPastSameDayCutoff
  var isSameDayBlockedByCutoff = Arrival.isSameDayBlockedByCutoff
  var sameDayCutoffHourForState = Arrival.sameDayCutoffHourForState
  var formatSameDayCutoffHourLabel = Arrival.formatSameDayCutoffHourLabel
  var sameDayCutoffHintForState = Arrival.sameDayCutoffHintForState
  var categoryOffersAfterHours = Arrival.categoryOffersAfterHours
  var sameDayCutoffHintHtml = Arrival.sameDayCutoffHintHtml
  var bookingDateRules = Arrival.bookingDateRules
  var isBookingDateAllowed = Arrival.isBookingDateAllowed
  var earliestBookingDate = Arrival.earliestBookingDate
  var buildBookableDates = Arrival.buildBookableDates
  var formatAuDateDmy = Arrival.formatAuDateDmy
  var formatBookableDateLabel = Arrival.formatBookableDateLabel
  var formatCollectionDateShort = Arrival.formatCollectionDateShort
  var formatCollectionDateRange = Arrival.formatCollectionDateRange
  var formatSummaryDateLabel = Arrival.formatSummaryDateLabel
  var formatSummaryDateRange = Arrival.formatSummaryDateRange
  var petFirstNameRef = Arrival.petFirstNameRef
  var collectionEstimateRangeForState = Arrival.collectionEstimateRangeForState
  var collectionInCareEmailHelper = Arrival.collectionInCareEmailHelper
  var collectionInCareEmailSubheadingHtml = Arrival.collectionInCareEmailSubheadingHtml
  var buildPickupAddressConfirmHtml = Arrival.buildPickupAddressConfirmHtml
  var buildPickupAddressFieldHtml = Arrival.buildPickupAddressFieldHtml
  var partnerVetClinicsForState = Arrival.partnerVetClinicsForState
  var partnerVetClinicById = Arrival.partnerVetClinicById
  var clearVetClinicState = Arrival.clearVetClinicState
  var isVetClinicReady = Arrival.isVetClinicReady
  var isVetClinicInServiceArea = Arrival.isVetClinicInServiceArea
  var buildVetClinicConfirmHtml = Arrival.buildVetClinicConfirmHtml
  var buildVetClinicFieldHtml = Arrival.buildVetClinicFieldHtml
  var monthKeyFromDate = Arrival.monthKeyFromDate
  var parseMonthKey = Arrival.parseMonthKey
  var shiftMonthKey = Arrival.shiftMonthKey
  var formatMonthTitle = Arrival.formatMonthTitle
  var bookingCalendarBounds = Arrival.bookingCalendarBounds
  var resolveCalendarMonth = Arrival.resolveCalendarMonth
  var buildBookingCalendarHtml = Arrival.buildBookingCalendarHtml
  var optionsFromProductVariants = Arrival.optionsFromProductVariants
  var getReturnSpeedChoices = Arrival.getReturnSpeedChoices
  var isPriorityReturnChoice = Arrival.isPriorityReturnChoice
  var returnSpeedAllowedForState = Arrival.returnSpeedAllowedForState
  var getFilteredReturnSpeedChoices = Arrival.getFilteredReturnSpeedChoices
  var applyReturnSpeedChoice = Arrival.applyReturnSpeedChoice
  var hasReachedWizardStep = Arrival.hasReachedWizardStep
  var reconcileReturnSpeedSelection = Arrival.reconcileReturnSpeedSelection
  var findReturnSpeedChoice = Arrival.findReturnSpeedChoice
  var matchReturnMethodProductChoice = Arrival.matchReturnMethodProductChoice
  var returnMethodDisplayOverrideFromBlock = Arrival.returnMethodDisplayOverrideFromBlock
  var getReturnMethodChoices = Arrival.getReturnMethodChoices
  var findReturnMethodChoice = Arrival.findReturnMethodChoice
  var locationFromReturnMethodLabel = Arrival.locationFromReturnMethodLabel
  var applyReturnMethodChoice = Arrival.applyReturnMethodChoice
  var isRegionalCareArrangement = Arrival.isRegionalCareArrangement
  var classifyReturnMethodKind = Arrival.classifyReturnMethodKind
  var stripReturnMethodCollectPrefix = Arrival.stripReturnMethodCollectPrefix
  var splitReturnMethodTitleParts = Arrival.splitReturnMethodTitleParts
  var defaultReturnMethodDisplay = Arrival.defaultReturnMethodDisplay
  var mapReturnMethodDisplay = Arrival.mapReturnMethodDisplay
  var returnMethodAllowedForState = Arrival.returnMethodAllowedForState
  var getFilteredReturnMethodChoices = Arrival.getFilteredReturnMethodChoices
  var reconcileReturnMethodSelection = Arrival.reconcileReturnMethodSelection
  var hasPurchasedMemorialItems = Arrival.hasPurchasedMemorialItems
  var computeSteps = Arrival.computeSteps
  var stepLabel = Arrival.stepLabel
  var stepSectionKey = Arrival.stepSectionKey
  var computeSectionProgress = Arrival.computeSectionProgress
  var mapVetCollectionDisplay = Arrival.mapVetCollectionDisplay
  var mapHomeCollectionDisplay = Arrival.mapHomeCollectionDisplay
  var formatTransportTileParts = Arrival.formatTransportTileParts
  var compactTransportChoice = Arrival.compactTransportChoice
  var parseReturnSpeedLabel = Arrival.parseReturnSpeedLabel
  var LONSDALE_FAMILY_LABEL = Arrival.LONSDALE_FAMILY_LABEL
  var LONSDALE_PRODUCT_DISPLAY_TITLE = Arrival.LONSDALE_PRODUCT_DISPLAY_TITLE
  var LONSDALE_SERVICE_HEADLINE = Arrival.LONSDALE_SERVICE_HEADLINE
  var TRANSPORT_CATEGORY_DEFS = Arrival.TRANSPORT_CATEGORY_DEFS

  var Cart = CartMod.create({
    escapeHtml: escapeHtml,
    formatMoney: formatMoney,
    firstAvailableVariant: firstAvailableVariant,
    sizedShopifyImageUrl: sizedShopifyImageUrl,
    isLonsdaleTransport: isLonsdaleTransport,
    isVetTransport: isVetTransport,
    isPriorityCollectionSelected: isPriorityCollectionSelected,
    collectionTimeframeForState: collectionTimeframeForState,
    cremationProductForType: cremationProductForType,
    ensureSharedCremationVariant: ensureSharedCremationVariant,
    sharedCremationDisplayLabel: sharedCremationDisplayLabel,
    suggestedVariant: suggestedVariant,
    possessiveName: possessiveName,
    isUsablePetName: isUsablePetName,
    wrapGuidanceBarHtml: wrapGuidanceBarHtml,
    stepCopyText: stepCopyText,
    createInitialState: createInitialState,
    computeSteps: computeSteps,
    stepLabel: stepLabel,
    stepSectionKey: stepSectionKey,
    computeSectionProgress: computeSectionProgress,
    findTransportCategory: findTransportCategory,
    findTransportMainOption: findTransportMainOption,
    requirementsForTransportState: requirementsForTransportState,
    needsTransportCategoryStep: needsTransportCategoryStep,
    getTransportCategories: getTransportCategories,
    getReturnSpeedChoices: getReturnSpeedChoices,
    getFilteredReturnSpeedChoices: getFilteredReturnSpeedChoices,
    findReturnSpeedChoice: findReturnSpeedChoice,
    applyReturnSpeedChoice: applyReturnSpeedChoice,
    reconcileReturnSpeedSelection: reconcileReturnSpeedSelection,
    getReturnMethodChoices: getReturnMethodChoices,
    getFilteredReturnMethodChoices: getFilteredReturnMethodChoices,
    findReturnMethodChoice: findReturnMethodChoice,
    applyReturnMethodChoice: applyReturnMethodChoice,
    reconcileReturnMethodSelection: reconcileReturnMethodSelection,
    hasPurchasedMemorialItems: hasPurchasedMemorialItems,
    mapReturnMethodDisplay: mapReturnMethodDisplay,
    classifyReturnMethodKind: classifyReturnMethodKind,
    hasReachedWizardStep: hasReachedWizardStep,
    familyFacingTransportCategoryLabel: familyFacingTransportCategoryLabel,
    lonsdaleServiceSummaryLabel: lonsdaleServiceSummaryLabel,
    lonsdaleGoodbyeOptionLabel: lonsdaleGoodbyeOptionLabel,
    mapLonsdaleGoodbyeDisplay: mapLonsdaleGoodbyeDisplay,
    mapVetCollectionDisplay: mapVetCollectionDisplay,
    mapHomeCollectionDisplay: mapHomeCollectionDisplay,
    formatTransportTileParts: formatTransportTileParts,
    compactTransportChoice: compactTransportChoice,
    parseReturnSpeedLabel: parseReturnSpeedLabel,
    formatPickupAddress: formatPickupAddress,
    formatSummaryAddress: formatSummaryAddress,
    formatAuDateDmy: formatAuDateDmy,
    formatBookableDateLabel: formatBookableDateLabel,
    formatCollectionDateShort: formatCollectionDateShort,
    formatCollectionDateRange: formatCollectionDateRange,
    formatSummaryDateLabel: formatSummaryDateLabel,
    formatSummaryDateRange: formatSummaryDateRange,
    collectionEstimateRangeForState: collectionEstimateRangeForState,
    collectionInCareEmailHelper: collectionInCareEmailHelper,
    petFirstNameRef: petFirstNameRef,
    bookingDateRules: bookingDateRules,
    isBookingDateAllowed: isBookingDateAllowed,
    bookingCalendarBounds: bookingCalendarBounds,
    toYmd: toYmd,
    parseYmdLocal: parseYmdLocal,
    addDaysLocal: addDaysLocal,
    addWorkingDaysLocal: addWorkingDaysLocal,
    isHomeAddressInvalid: isHomeAddressInvalid,
    timeWindowOptionsForState: timeWindowOptionsForState,
    bookableArrivalTimeWindowsForState: bookableArrivalTimeWindowsForState,
    LONSDALE_FAMILY_LABEL: LONSDALE_FAMILY_LABEL,
    getMemorialCatalog: getMemorialCatalog,
    getMemorialEntry: getMemorialEntry,
    urnAshSplitDisplayValue: urnAshSplitDisplayValue,
    urnAshesPlacementDisplayValue: urnAshesPlacementDisplayValue,
    urnAshDestinationDisplayValue: urnAshDestinationDisplayValue,
    ashesPlacementSummaryLine: ashesPlacementSummaryLine,
    ashesPlacementSummaryLines: ashesPlacementSummaryLines,
    withAshesPlacementSummaryLine: withAshesPlacementSummaryLine,
    memorialCategorySummaryLabel: memorialCategorySummaryLabel,
    memorialPhotoSummaryDisplay: memorialPhotoSummaryDisplay,
    effectivePhotoOrientation: effectivePhotoOrientation,
    normalizePhotoPrintSize: normalizePhotoPrintSize,
    normalizePhotoOrientation: normalizePhotoOrientation,
    isMemorialMultiSelectMode: isMemorialMultiSelectMode,
    isMemorialIncludedProductLine: isMemorialIncludedProductLine,
    orderMemorialLinesForSummary: orderMemorialLinesForSummary,
    nestMemorialLines: nestMemorialLines,
    isMemorialAddonLineId: isMemorialAddonLineId,
    memorialAddonParentLineId: memorialAddonParentLineId,
    memorialAddonSourceForProduct: memorialAddonSourceForProduct,
    firstMemorialAddonSource: firstMemorialAddonSource,
    resolveMemorialAddonProductId: resolveMemorialAddonProductId,
    parseMemorialAddonStepId: parseMemorialAddonStepId,
    memorialAddonStepId: memorialAddonStepId,
    focusMemorialAddonProduct: focusMemorialAddonProduct,
    resolveMemorialItemAddonFlags: resolveMemorialItemAddonFlags,
    resolvePhotoAddonVariant: resolvePhotoAddonVariant,
    // Gallery helpers live in Cart.create — passing MemorialUI copies as deps
    // would shadow Cart's own function declarations (broken memorialReviewGroupLabel).
    formatEngravingCombinedText: formatEngravingCombinedText,
    sanitizeEngravingLine: sanitizeEngravingLine,
    splitLegacyEngravingText: splitLegacyEngravingText,
    normalizeEngravingExampleVisuals: normalizeEngravingExampleVisuals,
    buildNameplateColourOptions: buildNameplateColourOptions,
    ensureDefaultNameplateColour: ensureDefaultNameplateColour,
    nameplatePreviewImageForOption: nameplatePreviewImageForOption,
    nameplateSummaryLabelForAddon: nameplateSummaryLabelForAddon,
    nameplateColourLabelFromFlags: nameplateColourLabelFromFlags,
    resolveNameplateAddonVariant: resolveNameplateAddonVariant,
    findNameplateVariantById: findNameplateVariantById,
    normalizeNameplateFont: normalizeNameplateFont,
    normalizeNameplateLines: normalizeNameplateLines,
    NAMEPLATE_FONTS: NAMEPLATE_FONTS,
    NAMEPLATE_BODY_FONT: NAMEPLATE_BODY_FONT,
    NAMEPLATE_LINE1_MAX: NAMEPLATE_LINE1_MAX,
    NAMEPLATE_LINE2_MAX: NAMEPLATE_LINE2_MAX,
    sanitizeNameplateLine: sanitizeNameplateLine,
    clampNameplateInscription: clampNameplateInscription,
  })

  var petProperties = Cart.petProperties
  var cremationGroupLabel = Cart.cremationGroupLabel
  var bookingRailTitle = Cart.bookingRailTitle
  var sentenceCase = Cart.sentenceCase
  var transportNoteLabel = Cart.transportNoteLabel
  var transportCategoryShort = Cart.transportCategoryShort
  var compactServiceLevel = Cart.compactServiceLevel
  var transportSummaryLabel = Cart.transportSummaryLabel
  var buildLedger = Cart.buildLedger
  var buildSummarySections = Cart.buildSummarySections
  var transportNoteLabelForReturn = Cart.transportNoteLabelForReturn
  var returnSpeedDayBounds = Cart.returnSpeedDayBounds
  var ashesReadyEstimateRangeForState = Cart.ashesReadyEstimateRangeForState
  var ashesReadyEstimateRangeLabel = Cart.ashesReadyEstimateRangeLabel
  var ashesReturnFooterNotesHtml = Cart.ashesReturnFooterNotesHtml
  var cremationSummaryLabel = Cart.cremationSummaryLabel
  var petWeightSummaryLabel = Cart.petWeightSummaryLabel
  var cremationRailValueLabel = Cart.cremationRailValueLabel
  var returnSpeedSummaryLabel = Cart.returnSpeedSummaryLabel
  var returnSpeedTileDesc = Cart.returnSpeedTileDesc
  var memorialReturnSectionTitle = Cart.memorialReturnSectionTitle
  var ledgerTotal = Cart.ledgerTotal
  var reviewPrimaryCta = Cart.reviewPrimaryCta
  var renderReviewCheckoutPanelHtml = Cart.renderReviewCheckoutPanelHtml
  var renderReviewNotesHtml = Cart.renderReviewNotesHtml
  var renderReviewCalloutsHtml = Cart.renderReviewCalloutsHtml
  var reviewTrustConfig = Cart.reviewTrustConfig
  var buildMemorialGalleryItems = Cart.buildMemorialGalleryItems
  var buildReviewDetailGroups = Cart.buildReviewDetailGroups
  var buildCartLines = Cart.buildCartLines
  var buildCartAttributes = Cart.buildCartAttributes
  var partitionCartAddBatches = Cart.partitionCartAddBatches
  var attachCartLineParentRefs = Cart.attachCartLineParentRefs
  var cartAddPayloadItems = Cart.cartAddPayloadItems
  var reviewLinesMissingVariant = Cart.reviewLinesMissingVariant
  var cartAddCoversPostedLines = Cart.cartAddCoversPostedLines
  var indexCartLineKeys = Cart.indexCartLineKeys
  var cartLineBookingId = Cart.cartLineBookingId
  var cartLineParentBookingId = Cart.cartLineParentBookingId
  var previousBookingCartUpdates = Cart.previousBookingCartUpdates
  var cartBatchHasParentRefs = Cart.cartBatchHasParentRefs
  var normalizeCartAttributes = Cart.normalizeCartAttributes
  var specialInstructionsText = Cart.specialInstructionsText
  var specialInstructionsStoredText = Cart.specialInstructionsStoredText
  var specialInstructionsPreview = Cart.specialInstructionsPreview
  var SPECIAL_INSTRUCTIONS_MAX = Cart.SPECIAL_INSTRUCTIONS_MAX || 1000
  var variantIdOf = Cart.variantIdOf
  var findVariantById = Cart.findVariantById
  var serializeBookingDraft = Cart.serializeBookingDraft
  var clearBookingDraft = Cart.clearBookingDraft
  var saveBookingDraft = Cart.saveBookingDraft
  var readBookingDraftRaw = Cart.readBookingDraftRaw
  var restoreBookingDraft = Cart.restoreBookingDraft
  var bookingDraftStorageKey = Cart.bookingDraftStorageKey

  function createWizard(root, config) {
    var state = createInitialState()
    restoreBookingDraft(state, config)
    syncCremationVariantForWeight(state, config)
    ensureSharedCremationVariant(state, config)
    var a11yRestoreFocus = null
    var shouldFocusStep = false
    var pendingMobileReveal = null
    var draftSaveTimer = null
    var lastRenderedStepId = null
    var stepScrollResetGen = 0
    var petNameAutofocused = false
    var els = {
      shell: root.querySelector('[data-baw-root]'),
      step: root.querySelector('[data-baw-step]'),
      footer: root.querySelector('[data-baw-footer]'),
      back: root.querySelector('[data-baw-back]'),
      forward: root.querySelector('[data-baw-forward]'),
      stepCount: root.querySelector('[data-baw-step-count]'),
      progress: root.querySelector('[data-baw-progress]'),
      progressSegments: root.querySelector('[data-baw-progress-segments]'),
      reviewChrome: root.querySelector('[data-baw-review-chrome]'),
      dockNote: root.querySelector('[data-baw-dock-note]'),
      summaryMobile: root.querySelector('[data-baw-summary-mobile]'),
      summaryToggle: root.querySelector('[data-baw-summary-toggle]'),
      summarySheet: root.querySelector('[data-baw-summary-sheet]'),
      summaryLabel: root.querySelector('[data-baw-summary-label]'),
      summaryAmount: root.querySelector('[data-baw-summary-amount]'),
      summaryTotal: root.querySelector('[data-baw-summary-total]'),
      summaryTotalDesktop: root.querySelector('[data-baw-summary-total-desktop]'),
      summaryList: root.querySelector('[data-baw-summary-list]'),
      summaryIntro: root.querySelector('[data-baw-summary-intro]'),
      topbarSummary: root.querySelector('[data-baw-topbar-summary]'),
      summaryReassure: root.querySelector('[data-baw-summary-reassure]'),
    }

    /* Install split step modules (late-bind render/summary via ctx). */
    var __bawCtx = {
      root: root,
      config: config,
      els: els,
      escapeHtml: escapeHtml,
      stepIntroHtml: stepIntroHtml,
      optionalSkipButtonHtml: optionalSkipButtonHtml,
      formatMoney: formatMoney,
      money: null, // set below after money() is defined
      firstAvailableVariant: firstAvailableVariant,
      sizedShopifyImageUrl: sizedShopifyImageUrl,
      getMemorialCatalog: getMemorialCatalog,
      getMemorialEntry: getMemorialEntry,
      stepCopyText: stepCopyText,
      DEFAULT_STEP_COPY: DEFAULT_STEP_COPY,
      possessiveName: possessiveName,
      isUsablePetName: isUsablePetName,
      enhanceOptionGroup: enhanceOptionGroup,
      enhanceCalendarKeyboard: enhanceCalendarKeyboard,
      wrapGuidanceBarHtml: wrapGuidanceBarHtml,
      PHOTO_PRINT_GUIDANCE_LINE: PHOTO_PRINT_GUIDANCE_LINE,
      PHOTO_UPLOAD_MAX_BYTES: PHOTO_UPLOAD_MAX_BYTES,
      PHOTO_UPLOAD_SAFE_BYTES: PHOTO_UPLOAD_SAFE_BYTES,
      PHOTO_UPLOAD_TIMEOUT_MS: PHOTO_UPLOAD_TIMEOUT_MS,
      PHOTO_UPLOAD_ACCEPT: PHOTO_UPLOAD_ACCEPT,
      PHOTO_UPLOAD_ACCEPT_SET: PHOTO_UPLOAD_ACCEPT_SET,
      ENGRAVING_EXAMPLE_CAPTION_DEFAULT: ENGRAVING_EXAMPLE_CAPTION_DEFAULT,
      ENGRAVING_LINE1_MAX: ENGRAVING_LINE1_MAX,
      ENGRAVING_LINE2_MAX: ENGRAVING_LINE2_MAX,
      ENGRAVING_CARD_SUBTITLE_DEFAULT: ENGRAVING_CARD_SUBTITLE_DEFAULT,
      NAMEPLATE_FONTS: NAMEPLATE_FONTS,
      NAMEPLATE_BODY_FONT: NAMEPLATE_BODY_FONT,
      NAMEPLATE_SECOND_LINE_CUSTOM_VALUE: NAMEPLATE_SECOND_LINE_CUSTOM_VALUE,
      NAMEPLATE_SECOND_LINE_SUGGESTIONS_DEFAULT: NAMEPLATE_SECOND_LINE_SUGGESTIONS_DEFAULT,
      NAMEPLATE_LINE1_MAX: NAMEPLATE_LINE1_MAX,
      NAMEPLATE_LINE2_MAX: NAMEPLATE_LINE2_MAX,
      sanitizeNameplateLine: sanitizeNameplateLine,
      clampNameplateInscription: clampNameplateInscription,
      ENGRAVING_SECOND_LINE_NONE_OPTION_LABEL: ENGRAVING_SECOND_LINE_NONE_OPTION_LABEL,
      LONSDALE_FAMILY_LABEL: LONSDALE_FAMILY_LABEL,
      // Core helpers used by arrival install() (with ctx). Arrival.create()
      // closes over these for outer exports, but nested step renderers only see ctx.
      isLonsdaleTransport: isLonsdaleTransport,
      isVetTransport: isVetTransport,
      timeWindowOptionsForState: timeWindowOptionsForState,
      timeWindowHelperForState: timeWindowHelperForState,
      buildArrivalTimeSlotsHtml: buildArrivalTimeSlotsHtml,
      lonsdaleArrivalFooterNotesHtml: lonsdaleArrivalFooterNotesHtml,
      pickupCollectionFooterNotesHtml: pickupCollectionFooterNotesHtml,
      goodbyeRoomVisualHtml: goodbyeRoomVisualHtml,
    }
    // INSTALL_SRC runs under with(ctx). Plain data props for closed-over locals
    // would desync on assignment (ctx.state = x leaves createWizard's `state`
    // pointing at the old object). Accessors keep both sides identical.
    ;[
      ['state', function () { return state }, function (v) { state = v }],
      ['a11yRestoreFocus', function () { return a11yRestoreFocus }, function (v) { a11yRestoreFocus = v }],
      ['pendingMobileReveal', function () { return pendingMobileReveal }, function (v) { pendingMobileReveal = v }],
      ['shouldFocusStep', function () { return shouldFocusStep }, function (v) { shouldFocusStep = v }],
    ].forEach(function (spec) {
      Object.defineProperty(__bawCtx, spec[0], {
        configurable: true,
        enumerable: true,
        get: spec[1],
        set: spec[2],
      })
    })
    // Populate ctx with every outer helper the step modules may call.
    ;[
      MemorialUI, Nameplate, Arrival, Cart
    ].forEach(function (modApi) {
      if (!modApi) return
      Object.keys(modApi).forEach(function (k) {
        if (__bawCtx[k] === undefined) __bawCtx[k] = modApi[k]
      })
    })

    var memorialSteps = MemorialUIMod.install(__bawCtx)
    var nameplateSteps = NameplateMod.install(__bawCtx)
    var arrivalSteps = ArrivalMod.install(__bawCtx)
    var cartSteps = CartMod.install(__bawCtx)
    Object.assign(__bawCtx, memorialSteps, nameplateSteps, arrivalSteps, cartSteps)

    /* Local aliases so remaining createWizard code can call step APIs by name. */
    var clearAshesPlacementModalHost = memorialSteps.clearAshesPlacementModalHost
    var memorialMoney = memorialSteps.memorialMoney
    var memorialOptionFaceHtml = memorialSteps.memorialOptionFaceHtml
    var memorialOptionTrayHtml = memorialSteps.memorialOptionTrayHtml
    var memorialProductCardHtml = memorialSteps.memorialProductCardHtml
    var openMemorialOverlay = memorialSteps.openMemorialOverlay
    var renderMemorialItemStep = memorialSteps.renderMemorialItemStep
    var tryAdvanceMemorialAshesPlacement = memorialSteps.tryAdvanceMemorialAshesPlacement
    var memorialPhotoStepHeadline = memorialSteps.memorialPhotoStepHeadline
    var memorialPhotoStepSubtitle = memorialSteps.memorialPhotoStepSubtitle
    var photoUploadIconSvg = memorialSteps.photoUploadIconSvg
    var photoOrientationIconSvg = memorialSteps.photoOrientationIconSvg
    var renderPhotoPrintPreview = memorialSteps.renderPhotoPrintPreview
    var wirePhotoPreviewDecodeIn = memorialSteps.wirePhotoPreviewDecodeIn
    var resizePhotoMockupsIn = memorialSteps.resizePhotoMockupsIn
    var syncReviewNameplatePlateSizes = memorialSteps.syncReviewNameplatePlateSizes
    var wirePhotoPreviewDecode = memorialSteps.wirePhotoPreviewDecode
    var renderPhotoPrintGuidanceHtml = memorialSteps.renderPhotoPrintGuidanceHtml
    var photoFileLooksAllowed = memorialSteps.photoFileLooksAllowed
    var validatePhotoFileClient = memorialSteps.validatePhotoFileClient
    var focusPhotoUploadZone = memorialSteps.focusPhotoUploadZone
    var tryAdvanceMemorialPhotoStep = memorialSteps.tryAdvanceMemorialPhotoStep
    var renderMemorialAddonPhotoStep = memorialSteps.renderMemorialAddonPhotoStep
    var resetPhotoUploadState = memorialSteps.resetPhotoUploadState
    var wirePhotoOrientationToggle = memorialSteps.wirePhotoOrientationToggle
    var needsPhotoUpload = memorialSteps.needsPhotoUpload
    var revokePhotoLocalPreview = memorialSteps.revokePhotoLocalPreview
    var renderPhotoSetupHtml = memorialSteps.renderPhotoSetupHtml
    var renderPhotoOrientationToggleHtml = memorialSteps.renderPhotoOrientationToggleHtml
    var renderPhotoUploadParts = memorialSteps.renderPhotoUploadParts
    var openPhotoFilePicker = memorialSteps.openPhotoFilePicker
    var wirePhotoUpload = memorialSteps.wirePhotoUpload
    var composePhotoForEntry = memorialSteps.composePhotoForEntry
    var needsEngravingSelected = memorialSteps.needsEngravingSelected
    var clearEngravingSelection = memorialSteps.clearEngravingSelection
    var syncMemorialEngravingAddon = memorialSteps.syncMemorialEngravingAddon
    var preloadEngravingTextFromPet = memorialSteps.preloadEngravingTextFromPet
    var memorialEngravingStepHeadline = memorialSteps.memorialEngravingStepHeadline
    var engravingExampleVisuals = memorialSteps.engravingExampleVisuals
    var renderEngravingHandGuidanceHtml = memorialSteps.renderEngravingHandGuidanceHtml
    var focusEngravingNameField = memorialSteps.focusEngravingNameField
    var tryAdvanceMemorialEngravingStep = memorialSteps.tryAdvanceMemorialEngravingStep
    var renderEngravingCharCounter = memorialSteps.renderEngravingCharCounter
    var renderEngravingSetupHtml = memorialSteps.renderEngravingSetupHtml
    var renderMemorialAddonEngravingStep = memorialSteps.renderMemorialAddonEngravingStep
    var uploadPhoto = memorialSteps.uploadPhoto
    var composePhoto = memorialSteps.composePhoto
    var openNameplatePreviewModal = nameplateSteps.openNameplatePreviewModal
    var summaryNameplatePreviewButton = nameplateSteps.summaryNameplatePreviewButton
    var focusNameplateSecondLine = nameplateSteps.focusNameplateSecondLine
    var syncNameplateSecondLineFromDom = nameplateSteps.syncNameplateSecondLineFromDom
    var tryAdvanceMemorialNameplateStep = nameplateSteps.tryAdvanceMemorialNameplateStep
    var renderMemorialAddonNameplateStep = nameplateSteps.renderMemorialAddonNameplateStep
    var layoutNameplateDock = nameplateSteps.layoutNameplateDock
    var clearNameplateSelection = nameplateSteps.clearNameplateSelection
    var commitIncludedNameplateSampleDefaults = nameplateSteps.commitIncludedNameplateSampleDefaults
    var preloadNameplateTextFromPet = nameplateSteps.preloadNameplateTextFromPet
    var nameplateSampleLine2DisplayText = nameplateSteps.nameplateSampleLine2DisplayText
    var ensureNameplateInscriptionDefaults = nameplateSteps.ensureNameplateInscriptionDefaults
    var nameplateFontSampleText = nameplateSteps.nameplateFontSampleText
    var renderNameplateLayoutHtml = nameplateSteps.renderNameplateLayoutHtml
    var renderNameplatePersonaliseHtml = nameplateSteps.renderNameplatePersonaliseHtml
    var renderNameplatePreviewHtml = nameplateSteps.renderNameplatePreviewHtml
    var nameplateSafeAreaDebugEnabled = nameplateSteps.nameplateSafeAreaDebugEnabled
    var scaleNameplateBox = nameplateSteps.scaleNameplateBox
    var measureNameplateGlyphMm = nameplateSteps.measureNameplateGlyphMm
    var fitNameplateLineMm = nameplateSteps.fitNameplateLineMm
    var applyNameplateSvgLine = nameplateSteps.applyNameplateSvgLine
    var nameplatePreviewRealTexts = nameplateSteps.nameplatePreviewRealTexts
    var logNameplateFitDebug = nameplateSteps.logNameplateFitDebug
    var ensureNameplateSafeGuides = nameplateSteps.ensureNameplateSafeGuides
    var fitNameplatePreview = nameplateSteps.fitNameplatePreview
    var ensureNameplateFontsThenFit = nameplateSteps.ensureNameplateFontsThenFit
    var scheduleNameplateFit = nameplateSteps.scheduleNameplateFit
    var wireNameplatePersonalise = nameplateSteps.wireNameplatePersonalise
    var updateNameplatePreviewLine = nameplateSteps.updateNameplatePreviewLine
    var loadGooglePlaces = arrivalSteps.loadGooglePlaces
    var adelaidePlacesBounds = arrivalSteps.adelaidePlacesBounds
    var southEastPlacesBounds = arrivalSteps.southEastPlacesBounds
    var attachPickupAddressAutocomplete = arrivalSteps.attachPickupAddressAutocomplete
    var attachVetClinicAutocomplete = arrivalSteps.attachVetClinicAutocomplete
    var clearTransportSelection = arrivalSteps.clearTransportSelection
    var applyTransportOption = arrivalSteps.applyTransportOption
    var optionPriceLabel = arrivalSteps.optionPriceLabel
    var renderTransportCategoryStep = arrivalSteps.renderTransportCategoryStep
    var renderTransportMainStep = arrivalSteps.renderTransportMainStep
    var renderTransportSubStep = arrivalSteps.renderTransportSubStep
    var renderTransportDetailsStep = arrivalSteps.renderTransportDetailsStep
    var syncTimesScrollAffordances = arrivalSteps.syncTimesScrollAffordances
    var syncScheduleTimesHeight = arrivalSteps.syncScheduleTimesHeight
    var scheduleSyncScheduleLayout = arrivalSteps.scheduleSyncScheduleLayout
    var closePhotoPreviewModal = cartSteps.closePhotoPreviewModal
    var bindPreviewModalChrome = cartSteps.bindPreviewModalChrome
    var openEngravingExampleModal = cartSteps.openEngravingExampleModal
    var openProductImagePreviewModal = cartSteps.openProductImagePreviewModal
    var openPhotoPreviewModal = cartSteps.openPhotoPreviewModal
    var openEngravingTextPreviewModal = cartSteps.openEngravingTextPreviewModal
    var bindSummaryPhotoPreviewClicks = cartSteps.bindSummaryPhotoPreviewClicks
    var closeStartAgainModal = cartSteps.closeStartAgainModal
    var openStartAgainModal = cartSteps.openStartAgainModal
    var bindStartAgainControls = cartSteps.bindStartAgainControls
    var summaryEditButton = cartSteps.summaryEditButton
    var summaryPetHtml = cartSteps.summaryPetHtml
    var summaryIntroHtml = cartSteps.summaryIntroHtml
    var summaryFooterInnerHtml = cartSteps.summaryFooterInnerHtml
    var summaryFooterHtml = cartSteps.summaryFooterHtml
    var ledgerPriceLabel = cartSteps.ledgerPriceLabel
    var summaryPreviewIconButton = cartSteps.summaryPreviewIconButton
    var summaryPhotoPreviewButton = cartSteps.summaryPhotoPreviewButton
    var summaryEngravingPreviewButton = cartSteps.summaryEngravingPreviewButton
    var memorialLabelDecorationsHtml = cartSteps.memorialLabelDecorationsHtml
    var summaryValueWithPreview = cartSteps.summaryValueWithPreview
    var summaryCategorySubheadHtml = cartSteps.summaryCategorySubheadHtml
    var withMemorialCategorySubheads = cartSteps.withMemorialCategorySubheads
    var placementStatusNoteHtml = cartSteps.placementStatusNoteHtml
    var isPlacementLedgerLine = cartSteps.isPlacementLedgerLine
    var ashesPlacementEditButtonHtml = cartSteps.ashesPlacementEditButtonHtml
    var openAshesPlacementEditor = cartSteps.openAshesPlacementEditor
    var summarySectionLineHtml = cartSteps.summarySectionLineHtml
    var summarySectionHtml = cartSteps.summarySectionHtml
    var bindSummaryEditClicks = cartSteps.bindSummaryEditClicks
    var renderSummary = cartSteps.renderSummary


    function isMobileFunnel() {
      return !!(window.matchMedia && window.matchMedia('(max-width: 1199px)').matches)
    }

    /** Queue a mobile scroll-to-next-section after the upcoming re-render. */
    function queueMobileReveal(targetSel, focusSel, opts) {
      if (!isMobileFunnel()) return false
      if (!targetSel && !focusSel) return false
      a11yRestoreFocus = null
      pendingMobileReveal = { target: targetSel || null, focus: focusSel || null, opts: opts || null }
      return true
    }


    __bawCtx.queueMobileReveal = queueMobileReveal

    function measureFunnelDockHeight() {
      if (!isMobileFunnel()) return 0
      var h = 0
      if (els.dockNote && !els.dockNote.hidden) h += els.dockNote.getBoundingClientRect().height || 0
      if (els.summaryMobile && !els.summaryMobile.hidden) {
        h += els.summaryMobile.getBoundingClientRect().height || 0
      }
      if (els.footer && !els.footer.hidden) h += els.footer.getBoundingClientRect().height || 0
      return h
    }

    /**
     * Soft-keyboard overlap + comfort gap. Dock is a flex sibling below .baw-step
     * (not an overlay), so step.bottom is already clear of GuidanceBar / Total /
     * actions — only the keyboard can cover the visible band.
     */
    function funnelBottomChromeHeight() {
      var h = 12
      if (!isMobileFunnel()) {
        if (els.footer && !els.footer.hidden) h += els.footer.getBoundingClientRect().height || 0
        return h
      }
      if (window.visualViewport) {
        h += Math.max(0, window.innerHeight - window.visualViewport.height)
      }
      return h
    }

    /** Reserve step bottom space when the soft keyboard or dock stack changes. */
    function syncFunnelChromePadding() {
      if (!els.step) return
      var dockedScroll = els.step.querySelector(
        '.baw-step-inner.is-nameplate-docked > .baw-nameplate-scroll'
      )
      if (!isMobileFunnel()) {
        // Desktop: Continue lives in .baw-footer outside .baw-step. Docked
        // nameplate preview already sits above that footer via shell flex —
        // only pad the scroll band so the last field clears the cream dock bar.
        // Extra when Write-your-own L2 is showing so the custom input isn't flush.
        if (els.shell) els.shell.style.removeProperty('--baw-funnel-dock-h')
        els.step.style.scrollPaddingBottom = ''
        if (dockedScroll) {
          els.step.style.paddingBottom = '0px'
          var hasCustomL2 = !!dockedScroll.querySelector(
            'input[data-nameplate-line2], input[data-engraving-line2]'
          )
          var hasTwoLines = !!dockedScroll.querySelector('.baw-nameplate-second-line')
          var isEngravingDock = !!(
            els.step.querySelector('.baw-step-inner--engraving.is-nameplate-docked')
          )
          // Keep last field clear of the cream dock. Engraving Two-lines keeps
          // One-line card sizes; light pad (Write-your-own gets a touch more).
          var padBottom = 24
          if (isEngravingDock && hasTwoLines) padBottom = hasCustomL2 ? 18 : 10
          else if (hasCustomL2) padBottom = 40
          else if (hasTwoLines) padBottom = 28
          dockedScroll.style.paddingBottom = padBottom + 'px'
        } else {
          els.step.style.paddingBottom = ''
          var deskScroll = els.step.querySelector('.baw-nameplate-scroll')
          if (deskScroll) deskScroll.style.paddingBottom = ''
        }
        return
      }
      var dockH = measureFunnelDockHeight()
      if (els.shell) els.shell.style.setProperty('--baw-funnel-dock-h', dockH + 'px')
      var keyboard = 0
      if (window.visualViewport) {
        keyboard = Math.max(0, window.innerHeight - window.visualViewport.height)
      }
      var comfort = Math.max(16, 16 + keyboard)
      // Review dock is a flex sibling — idle padding is a visible hole above Total.
      // scroll-padding still needs the real dock height so last thumbs / notes /
      // rating / payments can be scrolled fully clear of the sticky bar.
      if (state.currentStepId === 'review') {
        comfort = keyboard > 0 ? Math.max(16, 16 + keyboard) : 0
        els.step.style.scrollPaddingBottom =
          (dockH + 16 + Math.round(keyboard)) + 'px'
      } else {
        els.step.style.scrollPaddingBottom = ''
      }
      // Nameplate dock: cream preview is a flex sibling under the scroll band —
      // keep .baw-step flush to the Booking Total chrome; pad the scroll band
      // instead. Photo step stays in normal document flow (no cream dock).
      if (dockedScroll) {
        els.step.style.paddingBottom = '0px'
        dockedScroll.style.paddingBottom = comfort + 'px'
      } else {
        var idleScroll = els.step.querySelector('.baw-nameplate-scroll')
        if (idleScroll) idleScroll.style.paddingBottom = ''
        // Flex already carves the dock out of .baw-step; keep a comfort gap and
        // extra room while the keyboard overlaps the visual viewport.
        els.step.style.paddingBottom = comfort + 'px'
      }
    }

    var funnelChromeObserver = null

    __bawCtx.syncFunnelChromePadding = syncFunnelChromePadding

    function ensureFunnelChromeObserver() {
      if (typeof ResizeObserver === 'undefined') return
      if (funnelChromeObserver) return
      funnelChromeObserver = new ResizeObserver(function () {
        onFunnelChromeResize()
      })
      if (els.dockNote) funnelChromeObserver.observe(els.dockNote)
      if (els.summaryMobile) funnelChromeObserver.observe(els.summaryMobile)
      if (els.footer) funnelChromeObserver.observe(els.footer)
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', onFunnelChromeResize)
        window.visualViewport.addEventListener('scroll', syncFunnelChromePadding)
      }
      window.addEventListener('resize', onFunnelChromeResize)
    }

    /** Resize: re-measure dock / keyboard overlap for step bottom padding. */
    function onFunnelChromeResize() {
      syncFunnelChromePadding()
      var notesPin = els.step && els.step.querySelector('[data-baw-review-notes].is-open')
      if (notesPin) syncCareTeamNotePin(notesPin)
    }

    /** Ashes-return email reassurance — dock on mobile; under-H1 helper on desktop only. */
    function ashesReadyEmailNote() {
      return personalize(
        stepCopyText(config, 'guidanceAshesReadyEmail'),
        stepCopyText(config, 'guidanceAshesReadyEmailFallback')
      )
    }

    /**
     * Step-specific GuidanceBar fallback only (ashes-ready email on return steps).
     * Generic “Nothing is booked until you complete checkout.” is NOT injected here — that line
     * lives in the desktop sidebar (steps 1–5). Review uses the secure-checkout line.
     * Steps that build their own bar (collection strip, return-speed estimate,
     * Lonsdale SMS, etc.) own that content for desktop footer + mobile dock.
     */
    function chromeReassuranceText() {
      var id = state.currentStepId
      if (id === 'return-speed' || id === 'return-method') {
        return ashesReadyEmailNote()
      }
      return ''
    }

    /** Priority / Same-Day helper CTAs inside GuidanceBar / dock note. */
    function applyGuidanceGoto(btn) {
      if (!btn) return
      var stepId = btn.getAttribute('data-baw-goto')
      var preselect = btn.getAttribute('data-baw-preselect-service')
      if (preselect === 'same-day') {
        var sameDayOpt = findAfterHoursTransportOption(config, state)
        if (sameDayOpt && state.transport.key !== sameDayOpt.key) {
          applyTransportOption(sameDayOpt)
        }
      } else if (preselect === 'priority') {
        var priorityOpt = findPriorityTransportOption(config, state)
        if (priorityOpt && state.transport.key !== priorityOpt.key) {
          applyTransportOption(priorityOpt)
        }
      }
      if (stepId) goTo(stepId)
    }

    /**
     * Delegate from the shell so mobile dock clones keep working.
     * The visible mobile bar is a cloneNode of the footer GuidanceBar;
     * clones do not copy click listeners, and the live footer bar is
     * display:none on mobile.
     */
    function bindGuidanceGotoClicks(rootEl) {
      var host = els.shell || rootEl
      if (!host) return
      if (host.getAttribute('data-baw-goto-delegated')) return
      host.setAttribute('data-baw-goto-delegated', 'true')
      host.addEventListener('click', function (e) {
        var t = e.target
        if (t && t.nodeType === 3) t = t.parentNode
        var btn = t && t.closest ? t.closest('[data-baw-goto]') : null
        if (!btn || !host.contains(btn)) return
        applyGuidanceGoto(btn)
      })
    }

    /**
     * Mobile dock note = the same GuidanceBar component as desktop (icon + warm
     * strip + body). Prefer cloning the live footer bar (single source); else
     * the pet-step Angela quote (still under Weight on desktop); else
     * chromeReassuranceText for step-specific messages (e.g. ashes email).
     * When none exist, hide the dock note entirely — no empty strip.
     */

    __bawCtx.bindGuidanceGotoClicks = bindGuidanceGotoClicks
    bindGuidanceGotoClicks(els.shell)

    function refreshMobileDockNote() {
      if (!els.dockNote) return
      ensureFunnelChromeObserver()
      if (!isMobileFunnel()) {
        els.dockNote.hidden = true
        els.dockNote.innerHTML = ''
        syncFunnelChromePadding()
        return
      }
      // Review checkout pins GuidanceBar in the sticky footer (above Total).
      if (els.footer && els.footer.classList.contains('baw-footer--review-checkout')) {
        els.dockNote.hidden = true
        els.dockNote.innerHTML = ''
        syncFunnelChromePadding()
        return
      }
      var bar =
        els.footer &&
        els.footer.querySelector('[data-baw-guidance], .baw-guidance, .baw-step-footer-notes')
      if (bar) {
        var body =
          bar.querySelector('.baw-guidance-body, .baw-step-footer-notes-body') || bar
        if (String(body.innerHTML || '').replace(/\s+/g, '').length) {
          els.dockNote.innerHTML = ''
          els.dockNote.appendChild(bar.cloneNode(true))
          els.dockNote.hidden = false
          bindGuidanceGotoClicks(els.dockNote)
          syncFunnelChromePadding()
          return
        }
      }
      // Pet details: clone the in-step quote into the dock above Continue.
      // In-form original is hidden <1200 so it does not float under Weight.
      var petQuote = els.step && els.step.querySelector('[data-baw-pet-testimonial]')
      if (petQuote) {
        els.dockNote.innerHTML = ''
        els.dockNote.appendChild(petQuote.cloneNode(true))
        els.dockNote.hidden = false
        syncFunnelChromePadding()
        return
      }
      var text = chromeReassuranceText()
      var html = text ? checkoutReassuranceFooterHtml(text) : ''
      if (!html) {
        els.dockNote.hidden = true
        els.dockNote.innerHTML = ''
        syncFunnelChromePadding()
        return
      }
      // Full GuidanceBar markup — never plain text on the white Total strip.
      els.dockNote.innerHTML = html
      els.dockNote.hidden = false
      syncFunnelChromePadding()
    }

    /** Previous lives beside Continue in the sticky mobile dock (header nav is hidden). */
    function ensureMobileDockPrev() {
      if (!els.footer) return null
      var actions =
        els.footer.querySelector('.baw-actions') ||
        (els.step && els.step.querySelector('.baw-actions'))
      if (!actions) return null
      var prev = actions.querySelector('[data-baw-dock-back]')
      if (!isMobileFunnel()) {
        if (prev && prev.parentNode) prev.parentNode.removeChild(prev)
        return null
      }
      // Review: Continue only at every breakpoint. Browser back + header arrows stay.
      if (state.currentStepId === 'review') {
        if (prev && prev.parentNode) prev.parentNode.removeChild(prev)
        return null
      }
      if (!prev) {
        prev = document.createElement('button')
        prev.type = 'button'
        prev.className = 'baw-mobile-prev'
        prev.setAttribute('data-baw-dock-back', '')
        prev.setAttribute('aria-label', 'Previous step')
        prev.innerHTML =
          '<span class="baw-mobile-prev-icon" aria-hidden="true">' +
          '<svg width="18" height="18" viewBox="0 0 20 20" fill="none">' +
          '<path d="M12.25 4.25 6.5 10l5.75 5.75" stroke="currentColor" stroke-width="1.85" stroke-linecap="round" stroke-linejoin="round"/>' +
          '</svg></span>' +
          '<span class="baw-mobile-prev-label">Previous</span>'
        actions.insertBefore(prev, actions.firstChild)
        prev.addEventListener('click', goBack)
      }
      return prev
    }

    function syncMobileDockPrev() {
      var prev = ensureMobileDockPrev()
      if (!prev || !els.back) return
      var disabled = !!els.back.disabled
      prev.disabled = disabled
      prev.setAttribute('aria-disabled', disabled ? 'true' : 'false')
      prev.hidden = disabled
    }

    function scrollStepToElement(el, opts) {
      opts = opts || {}
      if (!els.step || !el) return
      var scroller = els.step
      var padTop = opts.padTop != null ? opts.padTop : 16
      var behavior = opts.smooth === false ? 'auto' : 'smooth'
      var mainRect = scroller.getBoundingClientRect()
      var elRect = el.getBoundingClientRect()
      var chrome = funnelBottomChromeHeight()
      var visibleBottom = mainRect.bottom - chrome
      var nextTop = scroller.scrollTop + (elRect.top - mainRect.top) - padTop

      // Keep the field in the upper visible band (above Continue / Total / keyboard)
      if (opts.pinTop || elRect.bottom > visibleBottom || elRect.top < mainRect.top + padTop) {
        scroller.scrollTo({ top: Math.max(0, nextTop), behavior: behavior })
      }
    }

    function snapScrollerTop(el) {
      if (!el) return
      if (el === window) {
        try {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
        } catch (errWin) {
          window.scrollTo(0, 0)
        }
        return
      }
      if (typeof el.scrollTo === 'function') {
        try {
          el.scrollTo({ top: 0, left: 0, behavior: 'auto' })
        } catch (errEl) {
          el.scrollTop = 0
        }
      } else if (el.scrollTop != null) {
        el.scrollTop = 0
      }
    }

    function forceScrollerTop(el) {
      if (!el) return
      if (el === window) {
        snapScrollerTop(window)
        var se = document.scrollingElement || document.documentElement
        if (se) se.scrollTop = 0
        if (document.body && document.body !== se) document.body.scrollTop = 0
        return
      }
      snapScrollerTop(el)
      // iOS keeps a separate overflow scroller; scrollTop assignment is ignored
      // until -webkit-overflow-scrolling: touch is briefly disengaged.
      var prevOverflow = el.style.overflow
      var prevOverflowY = el.style.overflowY
      el.style.overflow = 'hidden'
      void el.offsetHeight
      el.scrollTop = 0
      el.style.overflow = prevOverflow
      el.style.overflowY = prevOverflowY
      snapScrollerTop(el)
    }

    /**
     * Snap the window and every wizard pane to the top after a real step change
     * (Continue / Previous / deep-link / Priority Collection jump). Instant —
     * mobile must not feel like a second animation. Same-step option toggles
     * must not call this (selecting an urn should not yank).
     */
    function resetStepScrollPosition() {
      var gen = ++stepScrollResetGen
      function snap() {
        if (gen !== stepScrollResetGen) return
        forceScrollerTop(window)
        forceScrollerTop(root)
        forceScrollerTop(els.shell)
        if (els.shell) forceScrollerTop(els.shell.querySelector('.baw-main'))
        forceScrollerTop(els.step)
        if (els.step) {
          var nested = els.step.querySelectorAll('.baw-nameplate-scroll')
          for (var i = 0; i < nested.length; i++) forceScrollerTop(nested[i])
        }
      }
      snap()
      window.requestAnimationFrame(function () {
        snap()
        window.requestAnimationFrame(snap)
      })
      window.setTimeout(snap, 50)
      // If the family starts scrolling, stop retrying so we don't fight them.
      var stepEl = els.step
      if (stepEl) {
        var cancel = function () {
          if (gen !== stepScrollResetGen) return
          stepScrollResetGen += 1
        }
        stepEl.addEventListener('touchstart', cancel, { passive: true, once: true })
        stepEl.addEventListener('wheel', cancel, { passive: true, once: true })
        window.setTimeout(function () {
          stepEl.removeEventListener('touchstart', cancel)
          stepEl.removeEventListener('wheel', cancel)
        }, 200)
      }
    }

    __bawCtx.resetStepScrollPosition = resetStepScrollPosition

    /**
     * Mobile: bring the next section into view.
     * opts.forTyping — focus first (opens keyboard), then re-scroll after viewport settles.
     */
    function revealSectionOnMobile(targetSel, focusSel, opts) {
      opts = opts || {}
      if (!isMobileFunnel()) return
      if (!els.step) return
      var target = targetSel ? els.step.querySelector(targetSel) : null
      var focusEl = focusSel ? els.step.querySelector(focusSel) : null
      if (!target && focusEl) {
        target = focusEl.closest('.baw-field, .baw-pickup-side, .baw-weight-limit') || focusEl
      }
      if (!target) return

      function focusNow() {
        if (!focusEl || typeof focusEl.focus !== 'function') return
        try { focusEl.focus({ preventScroll: true }) } catch (err) { focusEl.focus() }
      }

      function adjust(smooth) {
        scrollStepToElement(target, {
          padTop: opts.padTop != null ? opts.padTop : 16,
          pinTop: true,
          smooth: smooth,
        })
      }

      if (opts.forTyping && focusEl) {
        window.setTimeout(function () {
          focusNow()
          adjust(false)
          window.requestAnimationFrame(function () { adjust(false) })
          window.setTimeout(function () { adjust(false) }, 120)
          window.setTimeout(function () { adjust(true) }, 320)
          if (window.visualViewport) {
            var vv = window.visualViewport
            var onVv = function () { adjust(false) }
            vv.addEventListener('resize', onVv)
            vv.addEventListener('scroll', onVv)
            window.setTimeout(function () {
              vv.removeEventListener('resize', onVv)
              vv.removeEventListener('scroll', onVv)
            }, 1000)
          }
        }, opts.focusDelay != null ? opts.focusDelay : 80)
        return
      }

      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          adjust(true)
          if (focusEl) focusNow()
        })
      })
    }

    function flushMobileReveal() {
      if (!pendingMobileReveal) return
      var spec = pendingMobileReveal
      pendingMobileReveal = null
      revealSectionOnMobile(spec.target, spec.focus, spec.opts || null)
    }

    /** Continue button — may live in step markup or pinned .baw-footer after mount. */

    __bawCtx.flushMobileReveal = flushMobileReveal

    function continueButton() {
      return (
        (els.footer && els.footer.querySelector('[data-baw-continue]')) ||
        (els.step && els.step.querySelector('[data-baw-continue]')) ||
        null
      )
    }

    /** Single source of truth for the footer CTA: every step's Continue reflects
     *  canAdvanceFrom(currentStep). Steps that re-render into an existing footer
     *  (memorial categories after a photo step) would otherwise inherit a stale
     *  disabled CTA. Review owns its own checkout CTA — never touch it here. */
    function syncFooterContinueState() {
      if (state.currentStepId === 'review') return
      var btn = continueButton()
      if (!btn || btn.classList.contains('is-loading')) return
      var enabled = canAdvanceFrom(state.currentStepId)
      var reason = continueBlockedReason(state.currentStepId)
      btn.disabled = !enabled
      btn.setAttribute('aria-disabled', enabled ? 'false' : 'true')
      // Quiet hint on the disabled control only — no visible gate line above Continue.
      if (!enabled && reason) {
        btn.title = reason
        btn.setAttribute('aria-description', reason)
      } else {
        btn.removeAttribute('title')
        btn.removeAttribute('aria-description')
      }
      var gate = els.footer && els.footer.querySelector('[data-baw-continue-gate]')
      if (gate && gate.parentNode) gate.parentNode.removeChild(gate)
    }

    /** Pin notes + Continue into shell footer so they stay above Total / panel bottom.
     *  Notes must live in .baw-footer (not padded .baw-step) to share Continue’s full width.
     *  Only mount notes from the current step markup — never reuse leftovers already in
     *  .baw-footer (back/forward would otherwise keep the previous step’s notice).
     *  GuidanceBar only when step-specific (SMS / estimate / ashes email / etc.).
     *  Desktop shows it above Continue; mobile clones into .baw-dock-note.
     *  No empty strip when absent — dock measurement collapses. */
    function mountStepFooter() {
      if (!els.footer || !els.step) return
      els.footer.classList.remove('baw-footer--review-checkout')
      var notes = els.step.querySelector('[data-baw-step-footer-guidance]') ||
        els.step.querySelector('[data-baw-guidance], .baw-guidance, .baw-step-footer-notes')
      var actions =
        els.step.querySelector('.baw-actions') ||
        els.footer.querySelector('.baw-actions')
      // Drop prior footer children first (orphan notes + previous Continue).
      while (els.footer.firstChild) els.footer.removeChild(els.footer.firstChild)
      // Step-specific GuidanceBar fallback only (e.g. ashes-ready email) — never the
      // generic checkout calm-line (that lives in the desktop sidebar / Review CTA).
      if (!notes && actions && actions.querySelector('[data-baw-continue]')) {
        var reassure = chromeReassuranceText()
        var reassureHtml = reassure ? checkoutReassuranceFooterHtml(reassure) : ''
        if (reassureHtml) {
          var wrap = document.createElement('div')
          wrap.innerHTML = reassureHtml
          notes = wrap.firstChild
        }
      }
      if (notes || actions) {
        if (notes) els.footer.appendChild(notes)
        if (actions) els.footer.appendChild(actions)
        els.footer.hidden = false
      } else {
        els.footer.hidden = true
      }
      syncFooterContinueState()
      syncMobileDockPrev()
      refreshMobileDockNote()
      syncFunnelChromePadding()
    }

    /** Review: pin Total + Continue into the shell footer on mobile.
     *  Notes, shared ack, rating, and payments stay in the scroll column.
     *  Desktop keeps rating + payments in the sidebar under Continue. */

    __bawCtx.mountStepFooter = mountStepFooter

    function returnReviewCheckoutToPanel(panel) {
      if (!panel || !els.footer) return
      var dockPrev = els.footer.querySelector('.baw-mobile-prev')
      if (dockPrev && dockPrev.parentNode) dockPrev.parentNode.removeChild(dockPrev)
      var total = els.footer.querySelector('.baw-review-total') || panel.querySelector('.baw-review-total')
      var errorEl = els.footer.querySelector('#baw-submit-error') || panel.querySelector('#baw-submit-error')
      var panelActions = panel.querySelector('.baw-actions')
      var footerActions = els.footer.querySelector('.baw-actions')
      var reviewActions = panelActions && panelActions.querySelector('.baw-continue--review, [data-baw-continue-action="submit"]')
        ? panelActions
        : (footerActions && footerActions.querySelector('.baw-continue--review, [data-baw-continue-action="submit"]')
          ? footerActions
          : panelActions)
      if (footerActions && footerActions !== reviewActions && footerActions.parentNode) {
        footerActions.parentNode.removeChild(footerActions)
      }
      var trust = els.footer.querySelector('.baw-review-trust') || panel.querySelector('.baw-review-trust')
      var payments = els.footer.querySelector('.baw-review-payments') || panel.querySelector('.baw-review-payments')
      var guidance =
        els.footer.querySelector('[data-baw-step-footer-guidance]') ||
        panel.querySelector('[data-baw-step-footer-guidance]')
      var callouts =
        els.footer.querySelector('.baw-review-callouts') ||
        (panel && panel.querySelector('.baw-review-callouts'))
      if (callouts && panel) {
        var totalBefore = panel.querySelector('.baw-review-total')
        if (totalBefore) panel.insertBefore(callouts, totalBefore)
        else panel.appendChild(callouts)
      } else if (guidance && panel) {
        panel.appendChild(guidance)
      }
      if (total) panel.appendChild(total)
      if (errorEl) panel.appendChild(errorEl)
      if (reviewActions) panel.appendChild(reviewActions)
      if (trust) panel.appendChild(trust)
      if (payments) panel.appendChild(payments)
    }

    /**
     * Mobile + tablet Review dock: Total + Continue only (no Previous).
     * Notes, shared ack, rating, and payment badges stay in the scroll column
     * so the sticky bar cannot cover thumbs or trust marks.
     */
    function mountReviewCheckoutFooter() {
      if (!els.footer) return
      var panel = els.step && els.step.querySelector('.baw-review-checkout')
      returnReviewCheckoutToPanel(panel)
      while (els.footer.firstChild) els.footer.removeChild(els.footer.firstChild)
      els.footer.classList.remove('baw-footer--review-checkout')
      if (isMobileFunnel() && panel) {
        var total = panel.querySelector('.baw-review-total')
        var errorEl = panel.querySelector('#baw-submit-error')
        var actions = panel.querySelector('.baw-actions')
        if (total) els.footer.appendChild(total)
        if (errorEl) els.footer.appendChild(errorEl)
        if (actions) els.footer.appendChild(actions)
        els.footer.classList.add('baw-footer--review-checkout')
        els.footer.hidden = false
        syncMobileDockPrev()
      } else {
        els.footer.hidden = true
      }
      refreshMobileDockNote()
    }

    if (els.shell) {
      els.shell.setAttribute('role', 'region')
      els.shell.setAttribute('aria-label', 'Booking wizard')
    }
    if (els.step) {
      bindEnterSubmitsContinue(els.step)
    }
    if (els.summaryToggle) {
      els.summaryToggle.setAttribute('aria-expanded', 'false')
      if (els.summarySheet) {
        if (!els.summarySheet.id) els.summarySheet.id = 'baw-summary-sheet'
        els.summaryToggle.setAttribute('aria-controls', els.summarySheet.id)
      }
    }

    function focusStepLanding() {
      var heading = null
      if (state.currentStepId === 'review' && els.reviewChrome && !els.reviewChrome.hidden) {
        var chromeHeading = els.reviewChrome.querySelector('.baw-review-chrome-headline')
        if (chromeHeading && els.reviewChrome.offsetParent) heading = chromeHeading
      }
      if (!heading) heading = els.step && els.step.querySelector('.baw-headline')
      if (heading) {
        heading.setAttribute('tabindex', '-1')
        try { heading.focus({ preventScroll: true }) } catch (err) { heading.focus() }
        return
      }
      var first = els.step && els.step.querySelector(
        'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
      )
      if (first) {
        try { first.focus({ preventScroll: true }) } catch (err2) { first.focus() }
      }
    }

    function restoreA11yFocus() {
      if (!a11yRestoreFocus || !els.step) return
      // Placement modal is portaled onto root (same host as Start over).
      var el = els.step.querySelector(a11yRestoreFocus) || root.querySelector(a11yRestoreFocus)
      a11yRestoreFocus = null
      if (!el || el.disabled) {
        el = els.step.querySelector('[data-baw-cal-day].is-selected') ||
          els.step.querySelector('[data-baw-tile].is-selected') ||
          els.step.querySelector('[data-baw-cal-day]')
      }
      if (!el) return
      try { el.focus({ preventScroll: true }) } catch (err) { el.focus() }
    }

    __bawCtx.restoreA11yFocus = restoreA11yFocus

    function petFirstName() {
      if (!isUsablePetName(state.pet.name)) return null
      var first = String(state.pet.name).replace(/\s+/g, ' ').trim().split(' ')[0]
      return isUsablePetName(first) ? first : null
    }

    /**
     * Compact rail names (first word each). Multi-pet entries in one field
     * (“Max & Bella”, “Max and Bella”, “Max, Bella”) → up to 2 first names,
     * joined with “ & ” to match how families type them.
     */
    function petRailDisplayNames() {
      if (!isUsablePetName(state.pet.name)) return []
      var raw = String(state.pet.name).replace(/\s+/g, ' ').trim()
      var parts = raw.split(/\s*(?:&+|,\s*|\band\b)\s*/i).filter(Boolean)
      var names = []
      for (var i = 0; i < parts.length && names.length < 2; i++) {
        var first = String(parts[i]).replace(/\s+/g, ' ').trim().split(' ')[0]
        if (isUsablePetName(first)) names.push(first)
      }
      if (!names.length) {
        var one = petFirstName()
        if (one) names.push(one)
      }
      return names
    }


    __bawCtx.petRailDisplayNames = petRailDisplayNames

    function personalize(template, fallback) {
      var name = petFirstName()
      var out = String(template == null ? '' : template)
      // Blank Theme Editor copy stays blank (do not substitute fallback copy).
      if (!out) return ''
      if (!name) {
        out = fallback != null ? String(fallback) : out
        return out
          .replace(/\{pet_possessive\}/g, 'the')
          .replace(/\{pet\}/g, 'your pet')
      }
      return out
        .replace(/\{pet_possessive\}/g, possessiveName(name))
        .replace(/\{pet\}/g, name)
    }


    __bawCtx.personalize = personalize

    function copyHeadline(key, fallbackNoPet) {
      var template = stepCopyText(config, key)
      var noPet = fallbackNoPet != null
        ? fallbackNoPet
        : template.replace(/\{pet_possessive\}/g, 'the').replace(/\{pet\}/g, 'your pet')
      return personalize(template, noPet)
    }


    __bawCtx.copyHeadline = copyHeadline

    function money(cents) { return formatMoney(cents, config.moneyFormat) }


    __bawCtx.money = money

    /**
     * Human-readable Continue gate. Empty string = ready (or a soft validation
     * step that surfaces its own inline error on click). Prefer this over a
     * silent no-op when Theme Editor products are missing / misconfigured.
     */
    function continueBlockedReason(stepId) {
      if (stepId === 'pet') {
        if (!(state.pet.name && state.pet.weightKg)) return 'Add your pet’s name and weight to continue'
        if (state.pet.weightKg > config.maxPetWeightKg) return 'This weight is above our standard maximum — use the contact form above'
        return ''
      }
      if (stepId === 'cremation-type') {
        if (!state.cremationType) return 'Choose a cremation type to continue'
        var cremProduct = cremationProductForType(config, state.cremationType)
        if (!cremProduct) {
          return 'This cremation product isn’t linked in Theme Editor yet. Choose another option, or contact Angel Ashes.'
        }
        if (!(state.cremationVariant || needsManualWeightPick(state, config))) {
          return 'This cremation product has no available options. Check the Theme Editor product picker.'
        }
        return ''
      }
      if (stepId === 'cremation-weight') {
        return state.cremationVariant ? '' : 'Choose a weight range to continue'
      }
      if (stepId === 'transport-category') {
        if (!state.transport.categoryId) return 'Choose how your pet will arrive'
        if (!findTransportCategory(config, state.transport.categoryId)) {
          return 'This arrival option isn’t available (product missing in Theme Editor). Choose another option.'
        }
        return ''
      }
      if (stepId === 'transport-main') {
        if (!state.transport.key) return 'Choose a service option to continue'
        var mainOpt = findTransportMainOption(config, state.transport.key)
        if (!mainOpt) {
          return 'This service option isn’t available (product missing in Theme Editor). Choose another option.'
        }
        return ''
      }
      if (stepId === 'transport-details') {
        var leafReason = transportLeaf(state, config) || {}
        if (leafReason.requires_date && !state.transport.date) return 'Choose a preferred date to continue'
        if (leafReason.requires_time_window) {
          reconcileArrivalTimeWindowSelection(state, config)
          if (!state.transport.timeWindow) return 'Choose a preferred time to continue'
        }
        if (leafReason.requires_vet_clinic && !isVetClinicReady(state)) return 'Confirm the vet clinic to continue'
        if (leafReason.requires_address_choice && !isPickupAddressReady(state, config)) {
          return 'Confirm the collection address to continue'
        }
        return ''
      }
      if (stepId.indexOf('memorial:') === 0 && stepId.indexOf(':addon:') === -1) {
        var reasonGk = stepId.slice('memorial:'.length)
        var reasonCat = getMemorialCatalog(state, config).byKey[reasonGk]
        var reasonEntry = getMemorialEntry(state, reasonGk, reasonCat && reasonCat.selectionMode)
        if (
          reasonCat &&
          isMemorialRequiredSelectionMode(reasonCat.selectionMode) &&
          !(reasonEntry.items && reasonEntry.items.length)
        ) {
          return 'Choose a keepsake to continue'
        }
        return ''
      }
      if (stepId.indexOf(':addon:photo') !== -1) {
        var photoReasonParsed = parseMemorialAddonStepId(stepId)
        var photoReasonGk = photoReasonParsed ? photoReasonParsed.groupKey : stepId.split(':')[1]
        var photoReasonEntry = getMemorialEntry(state, photoReasonGk)
        var photoReasonPid = resolveMemorialAddonProductId(
          state, config, photoReasonGk, 'photo', photoReasonParsed && photoReasonParsed.productId
        )
        focusMemorialAddonProduct(photoReasonEntry, photoReasonPid)
        if (!photoReasonEntry || !needsPhotoUpload(photoReasonEntry)) return ''
        if (photoReasonEntry.addons.photo.uploading) return 'Please wait for the photo to finish uploading'
        if (!photoReasonEntry.addons.photo.photoUrl) return 'Choose a photo to continue'
        return ''
      }
      if (stepId.indexOf(':addon:nameplate') !== -1) {
        var npReasonParsed = parseMemorialAddonStepId(stepId)
        var npReasonGk = npReasonParsed ? npReasonParsed.groupKey : stepId.split(':')[1]
        var npReasonEntry = getMemorialEntry(state, npReasonGk)
        var npReasonPid = resolveMemorialAddonProductId(
          state, config, npReasonGk, 'nameplate', npReasonParsed && npReasonParsed.productId
        )
        focusMemorialAddonProduct(npReasonEntry, npReasonPid)
        var npReason = npReasonEntry && npReasonEntry.addons && npReasonEntry.addons.nameplate
        if (!npReason || !npReason.choiceId || String(npReason.choiceId) === 'no') return ''
        if (String(npReason.text || '').length > NAMEPLATE_LINE1_MAX) {
          return 'First line is too long — shorten it to continue'
        }
        if (String(npReason.textLine2 || '').length > NAMEPLATE_LINE2_MAX) {
          return 'Second line is too long — shorten it to continue'
        }
        if (normalizeNameplateLines(npReason.lines) === 2 && !String(npReason.textLine2 || '').trim()) {
          return 'Choose or enter a second line to continue'
        }
        return ''
      }
      if (stepId === 'return-speed') {
        if (!getFilteredReturnSpeedChoices(config, state).length) return ''
        return state.returnSpeed.key ? '' : 'Choose when you’d like ashes returned'
      }
      if (stepId === 'return-method') {
        if (!getFilteredReturnMethodChoices(config, state).length) return ''
        if (config.flow === 'shared') {
          return state.returnMethod.key
            ? ''
            : 'Choose how you\u2019d like your memorials delivered'
        }
        return state.returnMethod.key ? '' : 'Choose how you’d like ashes returned'
      }
      return ''
    }

    function canAdvanceFrom(stepId) {
      if (stepId === 'pet') {
        if (!(state.pet.name && state.pet.weightKg)) return false
        if (state.pet.weightKg > config.maxPetWeightKg) return false
        return true
      }
      if (stepId === 'cremation-type') {
        if (!state.cremationType) return false
        if (!cremationProductForType(config, state.cremationType)) return false
        return !!(state.cremationVariant || needsManualWeightPick(state, config))
      }
      if (stepId === 'cremation-weight') return !!state.cremationVariant
      if (stepId === 'transport-category') {
        return !!(
          state.transport.categoryId &&
          findTransportCategory(config, state.transport.categoryId)
        )
      }
      if (stepId === 'transport-main') {
        return !!(
          state.transport.key &&
          findTransportMainOption(config, state.transport.key)
        )
      }
      if (stepId === 'transport-sub') return !!state.transport.subKey
      if (stepId === 'transport-details') {
        var leaf = transportLeaf(state, config) || {}
        if (leaf.requires_date && !state.transport.date) return false
        if (leaf.requires_time_window) {
          reconcileArrivalTimeWindowSelection(state, config)
          if (!state.transport.timeWindow) return false
        }
        if (leaf.requires_vet_clinic && !isVetClinicReady(state)) return false
        if (leaf.requires_address_choice && !isPickupAddressReady(state, config)) return false
        return true
      }
      if (stepId.indexOf('memorial:') === 0 && stepId.indexOf(':addon:') === -1) {
        var gk = stepId.slice('memorial:'.length)
        var cat = getMemorialCatalog(state, config).byKey[gk]
        var entry = getMemorialEntry(state, gk, cat && cat.selectionMode)
        if (!cat) return true
        // Empty / misconfigured category (Theme Editor tip shown) — allow Continue
        if (cat.emptyReason && !(cat.allCards && cat.allCards.length) && !cat.includedCard) return true
        if (isMemorialRequiredSelectionMode(cat.selectionMode)) {
          if (!(entry.items && entry.items.length)) return false
        }
        // Grid only commits the product. Photo / nameplate / engraving are later
        // personalise steps — never gate leaving the grid on an upload.
        // Multi-urn ashes placement is validated on Continue (tryAdvance) so
        // errors only appear after the family presses Continue — not while picking.
        return true
      }
      if (stepId.indexOf(':addon:photo') !== -1) {
        var photoParsed = parseMemorialAddonStepId(stepId)
        var pgk = photoParsed ? photoParsed.groupKey : stepId.split(':')[1]
        var pEntry = getMemorialEntry(state, pgk)
        var photoPid = resolveMemorialAddonProductId(
          state, config, pgk, 'photo', photoParsed && photoParsed.productId
        )
        focusMemorialAddonProduct(pEntry, photoPid)
        // Unselected / declined = skip (always allowed). Once opted in, a photo
        // must finish uploading before Continue is available.
        if (!pEntry || !needsPhotoUpload(pEntry)) return true
        if (pEntry.addons.photo.uploading) return false
        return !!pEntry.addons.photo.photoUrl
      }
      // Nameplate is optional — no colour selected is a valid skip. Once a colour
      // is committed, Two lines requires a Second line before Continue.
      if (stepId.indexOf(':addon:nameplate') !== -1) {
        var npParsed = parseMemorialAddonStepId(stepId)
        var ngk = npParsed ? npParsed.groupKey : stepId.split(':')[1]
        var nEntry = getMemorialEntry(state, ngk)
        var npPid = resolveMemorialAddonProductId(
          state, config, ngk, 'nameplate', npParsed && npParsed.productId
        )
        focusMemorialAddonProduct(nEntry, npPid)
        var np = nEntry && nEntry.addons && nEntry.addons.nameplate
        if (!np || !np.choiceId || String(np.choiceId) === 'no') return true
        if (String(np.text || '').length > NAMEPLATE_LINE1_MAX) return false
        if (String(np.textLine2 || '').length > NAMEPLATE_LINE2_MAX) return false
        if (normalizeNameplateLines(np.lines) === 2) {
          return !!String(np.textLine2 || '').trim()
        }
        return true
      }
      // Engraving is optional until the card is selected; then line 1 is required,
      // and Two lines also requires a Second line.
      if (stepId.indexOf(':addon:engraving') !== -1) {
        var engParsed = parseMemorialAddonStepId(stepId)
        var egk = engParsed ? engParsed.groupKey : stepId.split(':')[1]
        var eEntry = getMemorialEntry(state, egk)
        var engPid = resolveMemorialAddonProductId(
          state, config, egk, 'engraving', engParsed && engParsed.productId
        )
        focusMemorialAddonProduct(eEntry, engPid)
        if (!eEntry || !needsEngravingSelected(eEntry)) return true
        var engAddon = eEntry.addons.engraving
        if (!engAddon || !String(engAddon.text || '').trim()) return false
        if (normalizeNameplateLines(engAddon.lines) === 2) {
          return !!String(engAddon.textLine2 || '').trim()
        }
        return true
      }
      if (stepId === 'return-speed') {
        return getFilteredReturnSpeedChoices(config, state).length
          ? !!state.returnSpeed.key
          : true
      }
      if (stepId === 'return-method') {
        if (!getFilteredReturnMethodChoices(config, state).length) return true
        return !!state.returnMethod.key
      }
      if (stepId === 'review') return false
      return true
    }


    __bawCtx.canAdvanceFrom = canAdvanceFrom

    function refreshChrome() {
      var steps = computeSteps(state, config)
      if (!steps.length) steps = [{ id: 'pet' }]
      var idx = steps.findIndex(function (s) { return s.id === state.currentStepId })
      if (idx === -1) { idx = 0; state.currentStepId = steps[0].id }
      var stepDef = steps[idx] || steps[0]
      var isFirst = idx === 0
      var isLast = idx === steps.length - 1
      var canGoForward = !isLast && canAdvanceFrom(stepDef.id)

      if (els.back) {
        els.back.disabled = isFirst
        els.back.setAttribute('aria-disabled', isFirst ? 'true' : 'false')
      }
      if (els.forward) {
        els.forward.disabled = !canGoForward
        els.forward.setAttribute('aria-disabled', canGoForward ? 'false' : 'true')
        // Keep the next slot visible (opacity) so the centred progress never shifts
        els.forward.classList.remove('is-invisible')
        els.forward.removeAttribute('hidden')
      }
      syncMobileDockPrev()

      var label = stepLabel(stepDef.id, config)
      var sectionProgress = computeSectionProgress(steps, idx, config)
      var stepNum = sectionProgress.current + 1
      var stepTotal = sectionProgress.total
      // Visible chrome: compact “X of 6” at every breakpoint (no “Step” prefix).
      // Step titles are NOT shown in the header — .baw-wizard-title carries the task.
      if (els.stepCount) {
        els.stepCount.textContent = stepNum + ' of ' + stepTotal
      }
      if (els.progress) {
        els.progress.setAttribute(
          'aria-label',
          'Step ' + stepNum + ' of ' + stepTotal + (label ? ': ' + label : '')
        )
      }
      renderProgressSegments(sectionProgress.total, sectionProgress.current)
      return { steps: steps, idx: idx, stepDef: stepDef }
    }


    __bawCtx.refreshChrome = refreshChrome

    function renderProgressSegments(total, currentIdx) {
      if (!els.progressSegments) return
      var track = els.progressSegments
      var dots = track.querySelectorAll('.baw-progress-dot')
      var needsRebuild = dots.length !== total

      if (needsRebuild) {
        var html = ''
        for (var i = 0; i < total; i++) {
          if (i > 0) html += '<span class="baw-progress-connector"></span>'
          html += '<span class="baw-progress-dot"></span>'
        }
        track.innerHTML = html
        dots = track.querySelectorAll('.baw-progress-dot')
      }

      var connectors = track.querySelectorAll('.baw-progress-connector')
      for (var d = 0; d < dots.length; d++) {
        dots[d].classList.toggle('is-done', d < currentIdx)
        dots[d].classList.toggle('is-current', d === currentIdx)
      }
      for (var c = 0; c < connectors.length; c++) {
        /* Connector after milestone c is complete once we've passed that step */
        connectors[c].classList.toggle('is-done', c < currentIdx)
      }
    }

    function scheduleDraftSave() {
      if (draftSaveTimer) window.clearTimeout(draftSaveTimer)
      draftSaveTimer = window.setTimeout(function () {
        draftSaveTimer = null
        saveBookingDraft(state, config)
      }, 180)
    }


    __bawCtx.scheduleDraftSave = scheduleDraftSave

    function render() {
      var stepChanged = lastRenderedStepId !== null && lastRenderedStepId !== state.currentStepId
      var isStepLanding = lastRenderedStepId === null || stepChanged || shouldFocusStep
      ensureSharedCremationVariant(state, config)
      syncCremationVariantForWeight(state, config)
      // Care arrangement may have changed — clear invalid ashes-return selections
      // before chrome + summary. Single-option defaults apply only once that step
      // is reached (see reconcileReturnSpeedSelection / reconcileReturnMethodSelection).
      reconcileVetAfterHoursSelection(state, config)
      reconcileReturnSpeedSelection(state, config)
      reconcileReturnMethodSelection(state, config)
      // Drop any prior portaled placement modal before the step rebuilds.
      clearAshesPlacementModalHost()
      if (state.currentStepId !== 'review') {
        if (els.reviewChrome) {
          els.reviewChrome.hidden = true
          els.reviewChrome.innerHTML = ''
        }
      } else if (lastRenderedStepId !== 'review') {
        // Review entry always starts with notes collapsed. Keep stored text.
        state.specialInstructionsOpen = false
      }
      var chrome = refreshChrome()
      root.classList.toggle('is-review', state.currentStepId === 'review')
      if (state.currentStepId !== 'review') root.removeAttribute('data-review-gallery')
      if (state.currentStepId === 'review' && els.summarySheet) {
        els.summarySheet.classList.remove('is-open')
        if (els.summaryToggle) els.summaryToggle.setAttribute('aria-expanded', 'false')
      }
      renderStep(chrome.stepDef, chrome.steps, chrome.idx)
      renderSummary()
      scheduleDraftSave()
      els.shell.hidden = false
      // Predictive image warm for upcoming steps (idle — keeps current paint snappy).
      if (
        typeof AngelAshesBookingMemorials !== 'undefined' &&
        AngelAshesBookingMemorials.warmImagesForStep
      ) {
        AngelAshesBookingMemorials.warmImagesForStep(config, state, state.currentStepId)
      }
      lastRenderedStepId = state.currentStepId
      if (shouldFocusStep) {
        shouldFocusStep = false
        a11yRestoreFocus = null
        focusStepLanding()
      } else {
        restoreA11yFocus()
      }
      // After focus: iOS may scroll a card/field into view and hide the heading.
      if (isStepLanding) resetStepScrollPosition()
    }


    __bawCtx.render = render

    /** Clear draft + wizard state and return to step 1 (Start-over confirm). */
    function resetBookingWizard() {
      if (draftSaveTimer) {
        window.clearTimeout(draftSaveTimer)
        draftSaveTimer = null
      }
      clearBookingDraft(config)
      state = createInitialState()
      lastRenderedStepId = null
      shouldFocusStep = true
      closeStartAgainModal()
      render()
    }

    __bawCtx.resetBookingWizard = resetBookingWizard

    function clearStepRenderError() {
      var el = root.querySelector('[data-baw-render-error]')
      if (el && el.parentNode) el.parentNode.removeChild(el)
    }

    function showStepRenderError(stepId, err) {
      clearStepRenderError()
      var host = els.footer && !els.footer.hidden ? els.footer : els.step
      if (!host) return
      var el = document.createElement('div')
      el.className = 'baw-error'
      el.setAttribute('data-baw-render-error', '')
      el.setAttribute('role', 'alert')
      el.textContent =
        'We couldn\u2019t open the next step. Please refresh and try again, or contact Angel Ashes if this continues.'
      host.insertBefore(el, host.firstChild)
      console.error('Booking wizard: failed to render step', stepId, err)
    }

    function goTo(stepId) {
      var previousStepId = state.currentStepId
      var previousShouldFocus = shouldFocusStep
      state.currentStepId = stepId
      shouldFocusStep = true
      // Warm the next step's CDN derivatives before paint so img tags hit cache.
      if (
        typeof AngelAshesBookingMemorials !== 'undefined' &&
        AngelAshesBookingMemorials.warmImagesForStep
      ) {
        AngelAshesBookingMemorials.warmImagesForStep(config, state, stepId)
      }
      try {
        render()
        clearStepRenderError()
      } catch (err) {
        // Never leave chrome on a step whose body failed to paint (Continue no-op).
        showStepRenderError(stepId, err)
        state.currentStepId = previousStepId
        shouldFocusStep = previousShouldFocus
        try {
          render()
        } catch (err2) {
          console.error('Booking wizard: failed to restore step', previousStepId, err2)
        }
      }
    }


    __bawCtx.goTo = goTo

    function goNext() {
      var steps = computeSteps(state, config)
      if (!steps.length) steps = [{ id: 'pet' }]
      var idx = steps.findIndex(function (s) { return s.id === state.currentStepId })
      if (idx < 0) {
        // Stale draft step id — recover instead of a silent Continue no-op.
        idx = 0
        state.currentStepId = steps[0].id
      }
      if (idx === steps.length - 1) { submitOrder(); return }
      var currentDef = steps[idx]
      var currentId = currentDef ? String(currentDef.id || '') : ''
      var addonGroupKey = currentDef && currentDef.groupKey
        ? currentDef.groupKey
        : (currentId.split(':')[1] || '')
      // Multi-urn ashes placement validates before the readiness gate so Continue
      // can surface inline error + focus even when the chrome next control is muted.
      if (currentId === 'memorial:urn') {
        if (!tryAdvanceMemorialAshesPlacement()) return
      }
      // Engraving validates before the readiness gate so an empty name can still
      // surface inline error + focus when Continue is muted/disabled.
      if (currentId.indexOf(':addon:engraving') !== -1) {
        if (!tryAdvanceMemorialEngravingStep(
          addonGroupKey,
          currentDef && currentDef.productId
        )) return
      }
      if (!canAdvanceFrom(steps[idx].id)) {
        // Keep gated Continues honest: refresh chrome + footer reason text.
        syncFooterContinueState()
        refreshChrome()
        return
      }
      if (currentId.indexOf(':addon:photo') !== -1) {
        if (!tryAdvanceMemorialPhotoStep(
          addonGroupKey,
          currentDef && currentDef.productId
        )) return
      }
      if (currentId.indexOf(':addon:nameplate') !== -1) {
        if (!tryAdvanceMemorialNameplateStep(
          addonGroupKey,
          currentDef && currentDef.productId
        )) return
      }
      goTo(steps[idx + 1].id)
    }


    __bawCtx.goNext = goNext

    function goBack() {
      var steps = computeSteps(state, config)
      var idx = steps.findIndex(function (s) { return s.id === state.currentStepId })
      if (idx <= 0) return
      goTo(steps[idx - 1].id)
    }

    /**
     * Capture-phase Continue / Previous — survives step re-renders and footer moves.
     * Per-step listeners that need custom gates set data-baw-continue-action="custom"
     * (or "submit" on Review). Default path always advances on the first real click.
     */
    if (root && !root.getAttribute('data-baw-nav-delegated')) {
      root.setAttribute('data-baw-nav-delegated', '1')
      root.addEventListener(
        'click',
        function (e) {
          var t = e.target
          if (!t || !t.closest) return
          var shop = t.closest('[data-baw-review-shop]')
          if (shop && root.contains(shop)) {
            if (shop.disabled || shop.classList.contains('is-loading')) return
            var shopUrl = config.reviewShopLinkEnabled && config.reviewShopLinkUrl
            if (!shopUrl) return
            e.preventDefault()
            e.stopPropagation()
            submitOrder(shopUrl)
            return
          }
          var cont = t.closest('[data-baw-continue]')
          if (cont && root.contains(cont)) {
            if (cont.disabled || cont.classList.contains('is-loading')) return
            var action = cont.getAttribute('data-baw-continue-action') || ''
            if (action === 'custom') return
            e.preventDefault()
            e.stopPropagation()
            if (action === 'submit') {
              var primary = typeof reviewPrimaryCta === 'function' ? reviewPrimaryCta(config) : null
              submitOrder((primary && primary.url) || '/checkout')
              return
            }
            goNext()
            return
          }
          var prev = t.closest('[data-baw-dock-back]')
          if (prev && root.contains(prev)) {
            if (prev.disabled) return
            e.preventDefault()
            e.stopPropagation()
            goBack()
          }
        },
        true
      )
    }

    if (root && !root.getAttribute('data-baw-notes-delegated')) {
      root.setAttribute('data-baw-notes-delegated', '1')
      root.addEventListener('click', function (e) {
        var t = e.target
        if (!t || !t.closest) return
        var toggle = t.closest('[data-baw-special-instructions-toggle]')
        if (toggle && root.contains(toggle)) {
          e.preventDefault()
          var boxOpen = toggle.closest('[data-baw-review-notes]')
          if (!boxOpen) return
          var willOpen = !boxOpen.classList.contains('is-open')
          var pointerToggle = typeof e.detail === 'number' && e.detail > 0
          setCareTeamNoteOpen(boxOpen, willOpen, {
            returnFocus: willOpen || !pointerToggle
          })
          if (!willOpen && pointerToggle && typeof toggle.blur === 'function') {
            toggle.blur()
          }
          return
        }
        var done = t.closest('[data-baw-special-instructions-done]')
        if (done && root.contains(done)) {
          e.preventDefault()
          var boxDone = done.closest('[data-baw-review-notes]')
          if (!boxDone) return
          var taDone = boxDone.querySelector('[data-baw-special-instructions]')
          if (taDone) {
            var trimmed = String(taDone.value || '')
            if (trimmed.length > SPECIAL_INSTRUCTIONS_MAX) {
              trimmed = trimmed.slice(0, SPECIAL_INSTRUCTIONS_MAX)
              taDone.value = trimmed
            }
            state.specialInstructions = trimmed
          }
          /* Pointer Done: blur so :focus-visible does not ring the closed card.
             Keyboard (detail 0 / Escape) still returns focus to the toggle. */
          var pointerClose = typeof e.detail === 'number' && e.detail > 0
          setCareTeamNoteOpen(boxDone, false, { returnFocus: !pointerClose })
          if (pointerClose) {
            var activeDone = document.activeElement
            if (activeDone && boxDone.contains(activeDone) && typeof activeDone.blur === 'function') {
              activeDone.blur()
            }
          }
        }
      })
      root.addEventListener('change', function (e) {
        var t = e.target
        if (!t || !t.getAttribute || t.getAttribute('data-baw-shared-ashes-ack-check') == null) return
        state.sharedAshesAcknowledged = !!t.checked
        if (t.checked) state.sharedAshesAckInvalid = false
        syncSharedAshesAckChrome(root)
      })
      root.addEventListener('input', function (e) {
        var t = e.target
        if (!t || !t.getAttribute || t.getAttribute('data-baw-special-instructions') == null) return
        var next = String(t.value || '')
        if (next.length > SPECIAL_INSTRUCTIONS_MAX) {
          next = next.slice(0, SPECIAL_INSTRUCTIONS_MAX)
          t.value = next
        }
        state.specialInstructions = next
        var box = t.closest('[data-baw-review-notes]')
        if (box) {
          state.specialInstructionsOpen = true
          syncCareTeamNoteChrome(box)
          var countEl = box.querySelector('[data-baw-special-instructions-count]')
          if (countEl) countEl.textContent = String(next.length) + ' / ' + SPECIAL_INSTRUCTIONS_MAX
        }
      })
      root.addEventListener('keydown', function (e) {
        var t = e.target
        if (!t || !t.closest) return
        if (e.key === 'Escape') {
          var boxEsc = t.closest('[data-baw-review-notes]')
          if (!boxEsc || !boxEsc.classList.contains('is-open')) return
          e.preventDefault()
          var taEsc = boxEsc.querySelector('[data-baw-special-instructions]')
          if (taEsc) state.specialInstructions = String(taEsc.value || '')
          setCareTeamNoteOpen(boxEsc, false)
        }
      })
    }

    els.back.addEventListener('click', goBack)
    els.forward.addEventListener('click', goNext)
    els.summaryToggle.addEventListener('click', function () {
      // Review dock total is display-only — details already live in the step body.
      if (state.currentStepId === 'review') return
      var open = els.summarySheet.classList.toggle('is-open')
      els.summaryToggle.setAttribute('aria-expanded', open ? 'true' : 'false')
    })
    window.addEventListener('resize', function () {
      scheduleSyncScheduleLayout()
      refreshChrome()
      refreshMobileDockNote()
      if (state.currentStepId === 'review') mountReviewCheckoutFooter()
      else syncMobileDockPrev()
      layoutNameplateDock()
      syncFunnelChromePadding()
      fitNameplatePreview()
    })

    // Address / clinic search: keep the field above the soft keyboard on mobile.
    if (els.step) {
      els.step.addEventListener('focusin', function (ev) {
        if (!isMobileFunnel()) return
        var t = ev.target
        if (!t || !t.id) return
        if (t.id !== 'baw-t-address' && t.id !== 'baw-t-vet') return
        var sel = '#' + t.id
        window.setTimeout(function () {
          revealSectionOnMobile(sel, sel, { forTyping: true, focusDelay: 0, padTop: 12 })
        }, 40)
      })
    }
    function renderStep(stepDef, steps, idx) {
      var id = stepDef.id
      if (id === 'pet') return renderPetStep()
      if (id === 'cremation-type') return renderCremationTypeStep()
      if (id === 'cremation-weight') return renderCremationWeightStep()
      if (id === 'transport-category') return renderTransportCategoryStep()
      if (id === 'transport-main') return renderTransportMainStep()
      if (id === 'transport-sub') return renderTransportSubStep()
      if (id === 'transport-details') return renderTransportDetailsStep()
      if (id.indexOf('memorial:') === 0 && id.indexOf(':addon:') === -1) return renderMemorialItemStep(stepDef.groupKey)
      if (id.indexOf(':addon:photo') !== -1) {
        return renderMemorialAddonPhotoStep(stepDef.groupKey, stepDef.productId)
      }
      if (id.indexOf(':addon:nameplate') !== -1) {
        return renderMemorialAddonNameplateStep(stepDef.groupKey, stepDef.productId)
      }
      if (id.indexOf(':addon:engraving') !== -1) {
        return renderMemorialAddonEngravingStep(stepDef.groupKey, stepDef.productId)
      }
      if (id === 'return-speed') return renderReturnSpeedStep()
      if (id === 'return-method') return renderReturnMethodStep()
      if (id === 'review') return renderReviewStep()
      throw new Error('Booking wizard: unknown step "' + String(id) + '"')
    }

    /* --------------------------------- generic tile step ---------------------- */

    function renderTileStep(opts) {
      // opts: headline, headlineClass, helper (html), tiles, layout, selectedId, onSelect, continueLabel,
      //       skipLabel + onSkip (optional-step text link under the tile row; hidden once selected),
      //       personaliseRow (wrap intro + tiles: product thumb left of Yes/No options)
      //       personaliseStackExtra (HTML nested under tiles in the personalise column)
      // layout: 'grid' = auto-fill card grid; 'inline' = compact row (wraps on narrow screens),
      //         used where tiles are short text-only choices (e.g. nameplate Gold/Silver colour).
      // deselectable: steps with no decline tile, where clicking the selected tile
      //         again clears it (the family's only way back to "none of these").
      // alwaysShowSkip: rare opt-in to keep skip visible alongside a selection.
      var layout = opts.layout === 'grid' ? ' baw-options--grid' : (opts.layout === 'inline' ? ' baw-options--inline' : '')
      var optionsClass =
        opts.optionsClass && String(opts.optionsClass).trim()
          ? ' ' + String(opts.optionsClass).trim()
          : ''
      function tileButtonHtml(t) {
        return (
          '<button type="button" class="baw-tile' + (t.id === opts.selectedId ? ' is-selected' : '') + '" data-baw-tile data-id="' + escapeHtml(t.id) + '">' +
          '<span class="baw-tile-check" aria-hidden="true"></span>' +
          (t.image
            ? '<img class="baw-tile-image" src="' + escapeHtml(sizedShopifyImageUrl(t.image, memorialCdnPx('MEMORIAL_GRID_THUMB_PX', 360))) + '" alt="" loading="eager" decoding="async" width="44" height="44" />'
            : '') +
          '<span class="baw-tile-body">' +
          (t.badge ? '<span class="baw-tile-badge">' + escapeHtml(t.badge) + '</span>' : '') +
          '<span class="baw-tile-title">' + escapeHtml(t.title) + '</span>' +
          (t.desc ? '<span class="baw-tile-desc">' + escapeHtml(t.desc) + '</span>' : '') +
          '</span>' +
          (t.priceLabel ? '<span class="baw-tile-price">' + t.priceLabel + '</span>' : '') +
          '</button>'
        )
      }
      function tilesRowHtml(tiles, withDataTiles) {
        return (
          '<div class="baw-options' + layout + optionsClass + '"' +
          (withDataTiles ? ' data-tiles' : '') +
          (opts.deselectable && withDataTiles ? ' role="group"' : '') +
          '>' +
          (tiles || []).map(tileButtonHtml).join('') +
          '</div>'
        )
      }
      var grouped = (opts.groups || []).filter(function (g) {
        return g && g.tiles && g.tiles.length
      })
      var optionsHtml
      if (grouped.length) {
        optionsHtml =
          '<div class="baw-option-groups" data-tiles' + (opts.deselectable ? ' role="group"' : '') + '>' +
          grouped.map(function (g) {
            return (
              '<section class="baw-option-group">' +
              (g.title ? '<p class="baw-option-group-title">' + escapeHtml(g.title) + '</p>' : '') +
              (g.helper ? '<p class="baw-option-group-helper">' + escapeHtml(g.helper) + '</p>' : '') +
              tilesRowHtml(g.tiles, false) +
              '</section>'
            )
          }).join('') +
          '</div>'
      } else {
        optionsHtml = tilesRowHtml(opts.tiles || [], true)
      }
      var introHtml = opts.introHtml || ''
      // personaliseStackExtra nests under colour tiles (same column as Gold/Silver),
      // e.g. nameplate One/Two line cards — not a full-bleed row under the thumb.
      var stackExtra = opts.personaliseStackExtra || ''
      var stackBody = optionsHtml + stackExtra
      var introAndOptions
      if (opts.personaliseRow && introHtml) {
        introAndOptions =
          '<div class="baw-personalise-choice">' + introHtml +
          '<div class="baw-personalise-choice-stack">' + stackBody + '</div>' +
          '</div>'
      } else if (stackExtra) {
        introAndOptions =
          introHtml + '<div class="baw-personalise-choice-stack">' + stackBody + '</div>'
      } else {
        introAndOptions = introHtml + optionsHtml
      }
      // Optional wrapper (e.g. Goodbye Room visual + duration tiles as one composition).
      if (opts.composeClass && introAndOptions) {
        introAndOptions = '<div class="' + escapeHtml(opts.composeClass) + '">' + introAndOptions + '</div>'
      }
      // Skip sits under the tile row (not in the footer), before extraHtml so
      // nameplate personalise never sandwiches it mid-form. Hide once a colour
      // (or other tile) is committed — alwaysShowSkip is legacy opt-in only.
      var skipHtml =
        opts.skipLabel && (!opts.selectedId || opts.alwaysShowSkip)
          ? optionalSkipButtonHtml(opts.skipLabel)
          : ''
      // `bodyClass` wraps the choice row + follow-up field in one centred column
      // so both read as a single composed block (photo step).
      var bodyHtml = introAndOptions + skipHtml + (opts.extraHtml || '')
      if (opts.bodyClass) bodyHtml = '<div class="' + opts.bodyClass + '">' + bodyHtml + '</div>'
      var html =
        '<div class="baw-step-inner">' +
        stepIntroHtml(
          opts.headline,
          opts.helper ? '<div class="baw-helper">' + opts.helper + '</div>' : '',
          opts.headlineClass
        ) +
        bodyHtml +
        (opts.footerNotesHtml || '') +
        '<div class="baw-actions">' +
        '<button type="button" class="baw-continue" data-baw-continue' + (opts.selectedId ? '' : ' disabled') + '>' + (opts.continueLabel || 'Continue') + '</button>' +
        '</div>' +
        '</div>'

      els.step.innerHTML = html

      els.step.querySelectorAll('[data-baw-tile]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var id = btn.getAttribute('data-id') || ''
          a11yRestoreFocus = '[data-baw-tile][data-id="' + String(id).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"]'
          opts.onSelect(id)
        })
      })
      enhanceOptionGroup(els.step.querySelector('[data-tiles]'), '[data-baw-tile]', opts.headline || 'Options')
      var continueBtn = els.step.querySelector('[data-baw-continue]')
      if (continueBtn) {
        if (opts.onContinue) continueBtn.addEventListener('click', opts.onContinue)
        else continueBtn.addEventListener('click', goNext)
      }
      var skipBtn = els.step.querySelector('[data-baw-optional-skip]')
      if (skipBtn && typeof opts.onSkip === 'function') {
        skipBtn.addEventListener('click', opts.onSkip)
      }
      mountStepFooter()

      return { continueBtn: continueBtn }
    }

    /** Enable/disable Continue from canAdvanceFrom (shared readiness). The
     *  boolean arg is ignored — kept so existing call sites stay valid. */

    __bawCtx.renderTileStep = renderTileStep

    function setContinueEnabled(_enabled) {
      syncFooterContinueState()
      refreshChrome()
    }


    __bawCtx.setContinueEnabled = setContinueEnabled

    /* ---------------------------------- pet step -------------------------------- */

    function maxPetWeightKg() {
      return config.maxPetWeightKg || 80
    }

    function isOverMaxPetWeight(weightKg) {
      return weightKg != null && !isNaN(weightKg) && weightKg > maxPetWeightKg()
    }

    function weightContactPosted() {
      var success = root.querySelector('[data-baw-contact-success]')
      return !!(success && !success.hasAttribute('hidden'))
    }

    function buildWeightContactBody() {
      var maxKg = maxPetWeightKg()
      var lines = [
        'Hello,',
        '',
        'I\u2019m getting in touch because our pet is above your standard maximum weight of ' +
          maxKg +
          ' kg. I\u2019d like to share a little more about our situation so you can let us know whether you may still be able to help.',
        '',
      ]
      if (state.pet.name) lines.push('Pet name: ' + state.pet.name)
      if (state.pet.breed) lines.push('Breed: ' + state.pet.breed)
      if (state.pet.weightKg != null) lines.push('Weight: ' + String(state.pet.weightKg).replace(/\.0$/, '') + ' kg')
      lines.push('Booking type: ' + (config.flow === 'shared' ? 'Shared cremation' : 'Individual cremation'))
      lines.push('')
      lines.push('Please let me know what further information you need.')
      lines.push('')
      lines.push('Thank you')
      return lines.join('\n')
    }

    function parkWeightContactForm() {
      var park = root.querySelector('[data-baw-contact-park]')
      if (!park) return
      var form = root.querySelector('#baw-weight-contact')
      if (!form) return
      if (els.footer) {
        var footerActions = els.footer.querySelector('.baw-actions')
        var submitBtn = footerActions && footerActions.querySelector('button[type="submit"][form="baw-weight-contact"]')
        if (footerActions && submitBtn) {
          var fields = form.querySelector('[data-baw-contact-fields]')
          if (fields) fields.appendChild(footerActions)
        }
      }
      if (form.parentNode !== park) park.appendChild(form)
      park.hidden = true
    }

    function mountWeightContactForm(mountEl) {
      var park = root.querySelector('[data-baw-contact-park]')
      if (!park || !mountEl) return null
      var form = park.querySelector('form') || root.querySelector('#baw-weight-contact')
      if (!form) return null
      mountEl.appendChild(form)
      park.hidden = true

      var body = form.querySelector('#baw-contact-body')
      var fields = form.querySelector('[data-baw-contact-fields]')
      var success = form.querySelector('[data-baw-contact-success]')
      if (success && !success.hasAttribute('hidden')) {
        if (fields) fields.setAttribute('hidden', '')
      } else if (body) {
        var current = String(body.value || '').trim()
        if (!current || current.indexOf('standard maximum weight') !== -1) {
          body.value = buildWeightContactBody()
        }
      }

      if (!form.getAttribute('data-baw-pet-persist')) {
        form.setAttribute('data-baw-pet-persist', '1')
        form.addEventListener('submit', function () {
          try {
            sessionStorage.setItem('baw-weight-contact-pet', JSON.stringify({
              name: state.pet.name || '',
              breed: state.pet.breed || '',
              weightKg: state.pet.weightKg,
            }))
          } catch (err) { /* ignore */ }
        })
      }
      return form
    }

    function restorePetAfterContactPost() {
      try {
        var raw = sessionStorage.getItem('baw-weight-contact-pet')
        if (!raw) return
        var saved = JSON.parse(raw)
        if (!state.pet.name && saved.name) state.pet.name = String(saved.name || '')
        if (!state.pet.breed && saved.breed) state.pet.breed = String(saved.breed || '')
        if (state.pet.weightKg == null && saved.weightKg != null) {
          var w = parseFloat(saved.weightKg)
          if (!isNaN(w)) state.pet.weightKg = w
        }
        if (weightContactPosted()) sessionStorage.removeItem('baw-weight-contact-pet')
      } catch (err) { /* ignore */ }
    }

    /**
     * Quiet first-load cursor in #baw-pet-name. Desktop only (≥1200): phones
     * skip so the keyboard does not cover Continue. One-shot flag only — do
     * not gate on lastRenderedStepId (render() sets that before some pet
     * paints). Skip Theme Editor (designMode), a filled name, or another
     * focused field. Live storefront preview query params are not a skip.
     */
    function maybeAutofocusPetName(nameInput) {
      if (petNameAutofocused) return
      if (!nameInput || state.currentStepId !== 'pet') return
      if (String(nameInput.value || '').trim()) {
        petNameAutofocused = true
        return
      }
      try {
        if (window.Shopify && window.Shopify.designMode) {
          petNameAutofocused = true
          return
        }
      } catch (errDesign) { /* ignore */ }
      if (isMobileFunnel()) {
        petNameAutofocused = true
        return
      }

      petNameAutofocused = true

      function applyFocus() {
        var el = els.step && els.step.querySelector('#baw-pet-name')
        if (!el || state.currentStepId !== 'pet') return
        if (String(el.value || '').trim()) return
        var active = document.activeElement
        if (active && active !== el && active !== document.body && active !== document.documentElement) {
          var tag = active.tagName
          if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || active.isContentEditable) return
        }
        try {
          el.focus({ preventScroll: true, focusVisible: true })
        } catch (errFocus) {
          try { el.focus({ preventScroll: true }) } catch (errFocus2) { el.focus() }
        }
      }

      function scheduleFocus() {
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(applyFocus)
        } else {
          window.setTimeout(applyFocus, 0)
        }
      }

      if (typeof queueMicrotask === 'function') {
        queueMicrotask(scheduleFocus)
      } else {
        scheduleFocus()
      }
    }

    function renderPetStep() {
      parkWeightContactForm()
      restorePetAfterContactPost()

      if (weightContactPosted()) {
        petNameAutofocused = true
        els.step.innerHTML =
          '<div class="baw-step-inner baw-step-inner--contact">' +
          '<div data-baw-contact-mount></div>' +
          '</div>'
        mountWeightContactForm(els.step.querySelector('[data-baw-contact-mount]'))
        mountStepFooter()
        return
      }

      var overMax = isOverMaxPetWeight(state.pet.weightKg)
      var maxKg = maxPetWeightKg()
      var petSubheading = ''
      if (config.showSubheading) {
        var petSubText = config.subheading ? String(config.subheading).trim() : ''
        if (petSubText) {
          petSubheading = '<p class="baw-helper">' + escapeHtml(petSubText) + '</p>'
        }
      }
      var petHeadline = stepCopyText(config, 'petHeadline')
      var overweightTitle = stepCopyText(config, 'petOverweightTitle').replace(/\{maxKg\}/g, String(maxKg))
      var overweightCopy = stepCopyText(config, 'petOverweightCopy')
      var petTestimonialHtml = ''
      if (config.showPetStepTestimonial) {
        petTestimonialHtml =
          '<figure class="baw-pet-quote" data-baw-pet-testimonial>' +
          '<p class="baw-pet-quote-said"><cite class="baw-pet-quote-name">Angela W</cite> said,</p>' +
          '<blockquote class="baw-pet-quote-text">' +
          '<p>The process is straightforward and easy to navigate. I knew my boy would be well looked after, with love and respect.</p>' +
          '</blockquote>' +
          '<figcaption class="baw-pet-quote-verified">Verified customer</figcaption>' +
          '</figure>'
      }
      var html =
        '<div class="baw-step-inner baw-step-inner--form' + (overMax ? ' baw-step-inner--contact' : '') + '">' +
        stepIntroHtml(petHeadline, petSubheading) +
        '<div class="baw-field">' +
        '<label class="baw-label" for="baw-pet-name">Their name <span class="baw-required">*</span></label>' +
        '<input class="baw-input" id="baw-pet-name" type="text" placeholder="e.g. Poppy" autocomplete="off" value="' + escapeHtml(state.pet.name) + '" />' +
        '</div>' +
        '<div class="baw-field">' +
        '<label class="baw-label" for="baw-pet-breed">Breed <span class="baw-optional">(optional)</span></label>' +
        '<input class="baw-input" id="baw-pet-breed" type="text" placeholder="e.g. Labrador" value="' + escapeHtml(state.pet.breed) + '" />' +
        '</div>' +
        '<div class="baw-field">' +
        '<label class="baw-label" for="baw-pet-weight">Weight (kg) <span class="baw-required">*</span></label>' +
        '<input class="baw-input" id="baw-pet-weight" type="number" min="0" step="0.1" inputmode="decimal" placeholder="e.g. 12.5" value="' +
        (state.pet.weightKg != null ? escapeHtml(String(state.pet.weightKg)) : '') +
        '"' + (overMax ? ' aria-invalid="true"' : '') + ' />' +
        '</div>'

      if (overMax) {
        html +=
          '<div class="baw-weight-limit" role="status">' +
          '<p class="baw-weight-limit-title">' + escapeHtml(overweightTitle) + '</p>' +
          '<p class="baw-weight-limit-copy">' + escapeHtml(overweightCopy) + '</p>' +
          '</div>' +
          '<div class="baw-contact-panel" data-baw-contact-mount></div>'
      } else {
        html +=
          petTestimonialHtml +
          '<div class="baw-actions">' +
          '<button type="button" class="baw-continue" data-baw-continue' +
          (state.pet.name && state.pet.weightKg ? '' : ' disabled') +
          '>Continue</button>' +
          '</div>'
      }

      html += '</div>'
      els.step.innerHTML = html

      var nameInput = els.step.querySelector('#baw-pet-name')
      var breedInput = els.step.querySelector('#baw-pet-breed')
      var weightInput = els.step.querySelector('#baw-pet-weight')

      function sync() {
        state.pet.name = nameInput.value.trim()
        state.pet.breed = breedInput.value.trim()
        state.pet.weightKg = parseFloat(weightInput.value)
        if (isNaN(state.pet.weightKg)) state.pet.weightKg = null
        syncCremationVariantForWeight(state, config)
        var nowOver = isOverMaxPetWeight(state.pet.weightKg)
        if (nowOver !== overMax) {
          renderPetStep()
          if (nowOver) {
            queueMobileReveal('.baw-weight-limit', '#baw-contact-name')
            flushMobileReveal()
          }
          return
        }
        if (!nowOver) setContinueEnabled(!!(state.pet.name && state.pet.weightKg))
        renderSummary()
        refreshChrome()
      }

      nameInput.addEventListener('input', sync)
      breedInput.addEventListener('input', sync)
      weightInput.addEventListener('input', sync)

      if (overMax) {
        mountWeightContactForm(els.step.querySelector('[data-baw-contact-mount]'))
        var bodyEl = els.step.querySelector('#baw-contact-body')
        if (bodyEl) bodyEl.value = buildWeightContactBody()
        // Keep prefilled details in sync while the panel is open (until they edit the message)
        var bodyTouched = false
        if (bodyEl) {
          bodyEl.addEventListener('input', function () { bodyTouched = true })
        }
        function refreshPrefillIfPristine() {
          if (bodyTouched || !bodyEl) return
          bodyEl.value = buildWeightContactBody()
        }
        nameInput.addEventListener('input', refreshPrefillIfPristine)
        breedInput.addEventListener('input', refreshPrefillIfPristine)
        weightInput.addEventListener('input', refreshPrefillIfPristine)
      } else {
        var continueBtn = els.step.querySelector('[data-baw-continue]')
        if (continueBtn) {
          continueBtn.setAttribute('data-baw-continue-action', 'custom')
          continueBtn.addEventListener('click', function () {
            if (!state.pet.name || !state.pet.weightKg) return
            if (isOverMaxPetWeight(state.pet.weightKg)) {
              renderPetStep()
              return
            }
            if (config.flow === 'shared') {
              ensureSharedCremationVariant(state, config)
            }
            var memCat = getMemorialCatalog(state, config)
            if (typeof AngelAshesBookingMemorials !== 'undefined' && AngelAshesBookingMemorials.validateSelections) {
              if (AngelAshesBookingMemorials.validateSelections(memCat, state)) {
                state.memorialUnavailableNotice = true
              }
            }
            goNext()
          })
        }
      }

      mountStepFooter()
      refreshChrome()
      maybeAutofocusPetName(nameInput)
    }

    /* ------------------------------ cremation steps ------------------------------ */

    function selectCremationType(type) {
      state.cremationType = type
      syncCremationVariantForWeight(state, config)
      if (type !== 'water') {
        state.returnSpeed = { key: null, label: null, variant: null }
      }
      state.returnMethod = { key: null, label: null, variant: null, location: null, locationBlockId: null }
      reconcileReturnSpeedSelection(state, config)
      reconcileReturnMethodSelection(state, config)
      // Shared flow pages use config.flow; individual↔shared is page-level.
      // When urn category is individual-only, ensureCategoryDefaults clears it on shared.
      getMemorialCatalog(state, config)
      render()
    }

    function cremationPriceForType(type) {
      var product = cremationProductForType(config, type)
      if (!product) return null
      var match = suggestedVariant(product, state.pet.weightKg)
      if (match) {
        return { matched: true, price: match.price, sizeLabel: match.title, variant: match }
      }
      var from = firstAvailableVariant(product)
      return from
        ? { matched: false, price: from.price, sizeLabel: '', variant: null }
        : null
    }

    function renderCremationTypeStep() {
      // Water is the primary path; pre-select it (and its weight-matched price) on first visit.
      if (!state.cremationType && config.products.water) {
        selectCremationType('water')
        return
      }
      syncCremationVariantForWeight(state, config)

      var waterSelected = state.cremationType === 'water'
      var flameSelected = state.cremationType === 'flame'
      var hasWater = !!config.products.water
      var hasFlame = !!config.products.flame
      var waterMeta = hasWater ? cremationPriceForType('water') : null
      var flameMeta = hasFlame ? cremationPriceForType('flame') : null
      var waterCard = (config.cremationCards && config.cremationCards.water) || {}
      var flameCard = (config.cremationCards && config.cremationCards.flame) || {}

      function cardBenefitsHtml(benefits, checked) {
        var lines = (benefits || []).filter(function (b) { return b && String(b).trim() })
        if (!lines.length) return ''
        if (checked) {
          return '<span class="baw-style-option-benefits">' +
            lines.map(function (b) {
              return '<span class="baw-style-option-benefit">' + escapeHtml(b) + '</span>'
            }).join('') +
            '</span>'
        }
        return lines.map(function (b) {
          return '<span class="baw-style-option-timing">' + escapeHtml(b) + '</span>'
        }).join('')
      }

      var cremationSubtitle = copyHeadline('cremationSubtitle')
      var html =
        '<div class="baw-step-inner">' +
        stepIntroHtml(
          copyHeadline('cremationHeadline'),
          cremationSubtitle
            ? '<p class="baw-helper">' + escapeHtml(cremationSubtitle) + '</p>'
            : ''
        ) +
        '<div class="baw-style-options">'

      if (hasWater) {
        var waterSublabel = waterCard.sublabel ? String(waterCard.sublabel).trim() : ''
        html +=
          '<button type="button" class="baw-style-option baw-style-option--water' + (waterSelected ? ' is-selected' : '') + '" data-baw-cremation="water">' +
          '<span class="baw-style-option-check" aria-hidden="true"></span>' +
          '<span class="baw-style-option-copy">' +
          '<span class="baw-style-option-title">' + escapeHtml(waterCard.title || 'Water Cremation') + '</span>' +
          (waterSublabel ? '<span class="baw-style-option-rec">' + escapeHtml(waterSublabel) + '</span>' : '') +
          cardBenefitsHtml(waterCard.benefits, true) +
          '</span>' +
          (waterMeta ? '<span class="baw-style-option-price">' + escapeHtml(money(waterMeta.price)) + '</span>' : '') +
          '</button>'
      }

      if (hasFlame) {
        var flameSublabel = flameCard.sublabel ? String(flameCard.sublabel).trim() : ''
        var flameBenefits = cardBenefitsHtml(flameCard.benefits, false)
        var flameSupport = (flameSublabel || flameBenefits)
          ? ('<span class="baw-style-option-support">' +
            (flameSublabel ? '<span class="baw-style-option-desc">' + escapeHtml(flameSublabel) + '</span>' : '') +
            flameBenefits +
            '</span>')
          : ''
        html +=
          '<button type="button" class="baw-style-option' + (flameSelected ? ' is-selected' : '') + '" data-baw-cremation="flame">' +
          '<span class="baw-style-option-check" aria-hidden="true"></span>' +
          '<span class="baw-style-option-copy">' +
          '<span class="baw-style-option-title">' + escapeHtml(flameCard.title || 'Flame Cremation') + '</span>' +
          flameSupport +
          '</span>' +
          (flameMeta ? '<span class="baw-style-option-price">' + escapeHtml(money(flameMeta.price)) + '</span>' : '') +
          '</button>'
      }

      html += '</div>'

      if (config.cremationTiming.note) {
        html += '<p class="baw-timing-note">' + escapeHtml(config.cremationTiming.note) + '</p>'
      }

      html +=
        '<div class="baw-actions">' +
        '<button type="button" class="baw-continue" data-baw-continue' + (state.cremationType ? '' : ' disabled') + '>Continue</button>' +
        '</div></div>'

      els.step.innerHTML = html
      els.step.querySelectorAll('[data-baw-cremation]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var type = btn.getAttribute('data-baw-cremation')
          a11yRestoreFocus = '[data-baw-cremation="' + type + '"]'
          selectCremationType(type)
        })
      })
      enhanceOptionGroup(els.step.querySelector('.baw-style-options'), '[data-baw-cremation]', 'Cremation type')
      var cremationContinue = els.step.querySelector('[data-baw-continue]')
      if (cremationContinue) {
        cremationContinue.setAttribute('data-baw-continue-action', 'custom')
        cremationContinue.addEventListener('click', function () {
          if (!state.cremationType) return
          goNext()
        })
      }
      mountStepFooter()
    }

    function activeCremationProduct() {
      return cremationProductForType(config, state.cremationType)
    }

    function renderCremationWeightStep() {
      var product = activeCremationProduct()
      var variants = product ? product.variants : []
      var suggestion = suggestedVariantId(product, state.pet.weightKg)

      var tiles = variants.map(function (v) {
        return {
          id: v.id,
          title: v.title,
          priceLabel: money(v.price),
          badge: !state.cremationVariant && v.id === suggestion ? 'Suggested for ' + state.pet.weightKg + ' kg' : null,
        }
      })

      renderTileStep({
        headline: copyHeadline('cremationWeightHeadline', 'Which weight range fits?'),
        helper: copyHeadline('cremationWeightSubtitle'),
        tiles: tiles,
        selectedId: state.cremationVariant ? state.cremationVariant.id : null,
        onSelect: function (id) {
          var variant = variants.find(function (v) { return String(v.id) === String(id) })
          state.cremationVariant = variant || null
          render()
        },
      })
    }
    function renderReturnSpeedStep() {
      var choices = reconcileReturnSpeedSelection(state, config)

      var tiles = choices.map(function (o) {
        var parsed = parseReturnSpeedLabel(o.label)
        var isPriority = parsed.isPriority || isPriorityReturnChoice(o)
        // Name on the title line; timeframe underneath (avoids long “Standard · 10–14…” titles).
        var title = parsed.title || o.label
        var desc = returnSpeedTileDesc(parsed) || o.description || ''
        if (!desc) {
          desc = isPriority ? 'If you need ashes sooner.' : 'Included with your cremation.'
        }
        return {
          id: o.key,
          title: title,
          desc: desc,
          priceLabel: centsPriceLabel(o.variant, money),
        }
      })

      var returnSpeedSubtitle = copyHeadline('returnSpeedSubtitle')
      var helper = returnSpeedSubtitle
        ? '<p>' + escapeHtml(returnSpeedSubtitle) + '</p>'
        : ''

      var speedHeadline = copyHeadline(
        'returnSpeedHeadline',
        stepCopyText(config, 'returnSpeedHeadlineFallback')
      )

      renderTileStep({
        headline: speedHeadline,
        helper: helper,
        tiles: tiles,
        selectedId: state.returnSpeed.key,
        extraHtml: ashesReturnFooterNotesHtml(state, config),
        onSelect: function (id) {
          var opt = findReturnSpeedChoice(config, id, state.cremationType, state)
          applyReturnSpeedChoice(state, opt || { key: id, label: id, variant: null })
          render()
        },
      })
    }

    function renderReturnMethodStep() {
      var choices = reconcileReturnMethodSelection(state, config)
      var collectTiles = []
      var deliverTiles = []
      choices.forEach(function (o) {
        var parts = mapReturnMethodDisplay(o.label, o)
        var tile = {
          id: o.key,
          title: parts.title || o.label,
          desc: parts.desc || '',
          priceLabel: centsPriceLabel(o.variant, money),
        }
        if (classifyReturnMethodKind(o) === 'home-delivery') deliverTiles.push(tile)
        else collectTiles.push(tile)
      })

      // Email reassurance: one GuidanceBar via mountStepFooter — dock mirrors it.
      var methodHeadline = copyHeadline(
        'returnMethodHeadline',
        stepCopyText(config, 'returnMethodHeadlineFallback')
      )

      var groups = []
      if (collectTiles.length) {
        groups.push({
          title: 'Collect',
          tiles: collectTiles,
        })
      }
      if (deliverTiles.length) {
        groups.push({
          title: 'Home delivery',
          tiles: deliverTiles,
        })
      }

      renderTileStep({
        headline: methodHeadline,
        groups: groups,
        tiles: choices.map(function (o) {
          var parts = mapReturnMethodDisplay(o.label, o)
          return {
            id: o.key,
            title: parts.title || o.label,
            desc: parts.desc || '',
            priceLabel: centsPriceLabel(o.variant, money),
          }
        }),
        selectedId: state.returnMethod.key,
        onSelect: function (id) {
          applyReturnMethodChoice(state, findReturnMethodChoice(config, id))
          render()
        },
      })
    }

    /* --------------------------------- review step ------------------------------- */

    /** Shared review — calm confirmation that ashes aren't returned (under Cremation row). */
    function sharedReviewConfirmHtml() {
      if (config.flow !== 'shared') return ''
      var primary = personalize(
        stepCopyText(config, 'sharedReviewConfirm'),
        stepCopyText(config, 'sharedReviewConfirmFallback')
      )
      if (!primary) return ''
      return wrapGuidanceBarHtml(
        '<p class="baw-guidance-line baw-step-footer-note baw-guidance-line--emphasis baw-step-footer-note--emphasis">' +
          escapeHtml(primary) +
          '</p>',
        { variant: 'confirm', className: 'baw-guidance--shared-review', live: true }
      )
    }

    function syncCareTeamNoteChrome(box) {
      if (!box) return
      var stored =
        typeof specialInstructionsStoredText === 'function'
          ? specialInstructionsStoredText(state)
          : String(state.specialInstructions || '').trim()
      var open = !!state.specialInstructionsOpen
      var hasNote = !!stored
      var preview =
        typeof specialInstructionsPreview === 'function'
          ? specialInstructionsPreview(stored, 72)
          : stored
      var optionalEl = box.querySelector('.baw-review-notes-toggle-optional')
      var primary = box.querySelector('[data-baw-special-instructions-primary]')
      var previewEl = box.querySelector('[data-baw-special-instructions-preview]')
      var countEl = box.querySelector('[data-baw-special-instructions-count]')
      var toggle = box.querySelector('[data-baw-special-instructions-toggle]')
      var fields = box.querySelector('.baw-review-notes-fields')
      box.classList.toggle('is-open', open)
      box.classList.toggle('has-note', hasNote)
      if (primary) {
        primary.textContent = open
          ? 'Note for our care team'
          : hasNote
            ? 'Edit note for our care team'
            : 'Add a note for our care team'
      }
      if (optionalEl) optionalEl.hidden = !!(open || hasNote)
      if (previewEl) {
        previewEl.textContent = preview || ''
        previewEl.hidden = !preview || open
      }
      if (countEl) countEl.textContent = String(stored.length) + ' / ' + SPECIAL_INSTRUCTIONS_MAX
      if (toggle) {
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false')
        toggle.setAttribute(
          'aria-label',
          open
            ? 'Note for our care team'
            : hasNote
              ? 'Edit note for our care team'
              : 'Add a note for our care team'
        )
        toggle.hidden = false
      }
      if (fields) fields.hidden = !open
    }

    /** Clear fixed-above-dock pin + in-flow spacer for mobile notes. */
    function clearCareTeamNotePin(box) {
      if (!box) return
      var callouts = box.closest('.baw-review-callouts')
      if (callouts) {
        callouts.classList.remove('is-notes-pinned')
        callouts.style.removeProperty('--baw-notes-pin-spacer-h')
      }
      var inner = box.closest('.baw-step-inner')
      if (inner) {
        inner.style.removeProperty('transform')
        inner.style.removeProperty('animation')
      }
      box.style.removeProperty('left')
      box.style.removeProperty('width')
      box.style.removeProperty('right')
      box.style.removeProperty('bottom')
      box.style.removeProperty('max-height')
    }

    /**
     * Mobile/tablet Review: pin the open notes card just above Total+Continue.
     * Measures the dock (and soft keyboard) so ack / rating / payments cannot
     * sit between the note header and the sticky footer.
     */
    function syncCareTeamNotePin(box) {
      if (!box) return
      var shouldPin =
        !!state.specialInstructionsOpen &&
        box.classList.contains('is-open') &&
        isMobileFunnel() &&
        state.currentStepId === 'review'
      if (!shouldPin) {
        clearCareTeamNotePin(box)
        return
      }
      var callouts = box.closest('.baw-review-callouts')
      var anchor = callouts || box.parentElement
      if (!anchor) return

      var toggle = box.querySelector('.baw-review-notes-toggle')
      var spacerH = 52
      if (toggle) {
        var th = toggle.getBoundingClientRect().height
        if (th > 0) spacerH = Math.ceil(th)
      }
      if (callouts) {
        callouts.classList.add('is-notes-pinned')
        callouts.style.setProperty('--baw-notes-pin-spacer-h', spacerH + 'px')
      }

      /* .baw-step-inner fade-in can leave a transform containing block that
         traps position:fixed — clear it while the notes card is pinned. */
      var inner = box.closest('.baw-step-inner')
      if (inner) {
        inner.style.setProperty('transform', 'none')
        inner.style.setProperty('animation', 'none')
      }

      syncFunnelChromePadding()
      var dockH = measureFunnelDockHeight()
      var keyboard = 0
      if (window.visualViewport) {
        keyboard = Math.max(0, window.innerHeight - window.visualViewport.height)
      }
      /* Sit above the soft keyboard while typing; otherwise above the dock. */
      var bottom = keyboard > 0 ? keyboard : dockH
      var rect = anchor.getBoundingClientRect()
      box.style.left = Math.round(rect.left) + 'px'
      box.style.width = Math.round(rect.width) + 'px'
      box.style.right = 'auto'
      box.style.bottom = Math.round(bottom) + 'px'
      var viewH = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight
      var maxH = Math.max(120, Math.floor(viewH - (keyboard > 0 ? 0 : dockH) - 12))
      box.style.maxHeight = maxH + 'px'
    }

    function setCareTeamNoteOpen(box, open, opts) {
      opts = opts || {}
      state.specialInstructionsOpen = !!open
      syncCareTeamNoteChrome(box)
      syncFunnelChromePadding()
      if (open) {
        var ta = box && box.querySelector('[data-baw-special-instructions]')
        if (ta && opts.focus !== false) {
          window.setTimeout(function () {
            try { ta.focus({ preventScroll: true }) } catch (err) { ta.focus() }
            try {
              if (isMobileFunnel() && box) {
                /* Fixed pin above dock — do not scrollIntoView(end); that used
                   scroll-padding and left ack/rating in the gap above Total. */
                syncCareTeamNotePin(box)
              } else {
                ta.scrollIntoView({ block: 'nearest', inline: 'nearest' })
              }
            } catch (errScroll) { /* ignore */ }
          }, 0)
        } else if (isMobileFunnel() && box) {
          syncCareTeamNotePin(box)
        }
      } else {
        clearCareTeamNotePin(box)
        if (opts.returnFocus !== false) {
          var toggle = box && box.querySelector('[data-baw-special-instructions-toggle]')
          if (toggle) {
            window.setTimeout(function () {
              try { toggle.focus({ preventScroll: true }) } catch (err2) { toggle.focus() }
            }, 0)
          }
        }
      }
    }

    function syncSharedAshesAckChrome(rootEl) {
      var box = (rootEl || document).querySelector('[data-baw-shared-ashes-ack]')
      if (!box) return
      var check = box.querySelector('[data-baw-shared-ashes-ack-check]')
      var err = box.querySelector('[data-baw-shared-ashes-ack-error]')
      var invalid = !!state.sharedAshesAckInvalid
      box.classList.toggle('is-invalid', invalid)
      if (invalid) box.setAttribute('data-invalid', 'true')
      else box.removeAttribute('data-invalid')
      if (check) {
        check.checked = !!state.sharedAshesAcknowledged
        if (invalid) {
          check.setAttribute('aria-invalid', 'true')
          check.setAttribute('aria-describedby', 'baw-shared-ashes-ack-error')
        } else {
          check.removeAttribute('aria-invalid')
          check.removeAttribute('aria-describedby')
        }
      }
      if (err) err.hidden = !invalid
    }

    function clearSharedAshesAckIfNeeded() {
      if (config.flow === 'shared') return
      state.sharedAshesAcknowledged = false
      state.sharedAshesAckInvalid = false
    }

    function focusSharedAshesAck() {
      var check =
        (els.footer && els.footer.querySelector('[data-baw-shared-ashes-ack-check]')) ||
        (els.step && els.step.querySelector('[data-baw-shared-ashes-ack-check]'))
      if (!check) return
      try {
        check.focus({ preventScroll: false })
      } catch (err) {
        check.focus()
      }
      try {
        check.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      } catch (err2) { /* ignore */ }
    }

    function renderReviewStep() {
      var items = buildLedger(state, config)
      var total = ledgerTotal(items)
      var groups = buildReviewDetailGroups(items, state, config)
      var galleryItems = buildMemorialGalleryItems(items, state, config)
      var headline = stepCopyText(config, 'reviewHeadline') || 'Does everything look right?'
      var reviewSubtitle = stepCopyText(config, 'reviewSubtitle')

      function priceHtml(line) {
        if (!line || line.hidePrice) return ''
        if (line.included) return '<span class="baw-summary-item-price is-included">Included</span>'
        if (line.price == null) return ''
        if (line.price) return '<span class="baw-summary-item-price">' + escapeHtml(money(line.price)) + '</span>'
        if (line.variantId != null) {
          return '<span class="baw-summary-item-price is-included">Included</span>'
        }
        return ''
      }

      function splitMiddot(value) {
        return String(value || '')
          .split(/\s·\s/)
          .map(function (part) { return String(part || '').trim() })
          .filter(Boolean)
      }

      function sectionHeadHtml(title, editStepId, ariaLabel) {
        return (
          '<div class="baw-summary-section-head">' +
          '<span class="baw-summary-section-title">' + escapeHtml(title) + '</span>' +
          (editStepId
            ? '<button type="button" class="baw-review-edit" data-edit="' +
              escapeHtml(editStepId) + '"' +
              (ariaLabel ? ' aria-label="' + escapeHtml(ariaLabel) + '"' : '') +
              '>Edit</button>'
            : '') +
          '</div>'
        )
      }

      function factColumnHtml(fact) {
        if (!fact || !fact.primary) return ''
        var extraRows = fact.extraRows || (fact.extra || []).map(function (line) {
          return { text: line, priceHtml: '' }
        })
        var rows = [{ text: fact.primary, priceHtml: fact.priceHtml || fact.changeHtml || '' }].concat(
          extraRows.filter(function (row) { return row && row.text })
        )
        var lineClass =
          'baw-summary-section-line' + (fact.id === 'ashes-placement' ? ' is-placement' : '')
        var linesHtml = rows.map(function (row) {
          var secondaryClass =
            fact.id === 'cremation' && row.text === 'Ashes are not returned'
              ? ' is-secondary'
              : ''
          return (
            '<div class="' + lineClass + secondaryClass + '">' +
            '<span class="baw-summary-section-value">' + escapeHtml(row.text) + '</span>' +
            (row.priceHtml || '') +
            '</div>'
          )
        }).join('')
        return (
          '<div class="baw-summary-section" data-baw-review-fact="' + escapeHtml(fact.id || '') + '">' +
          sectionHeadHtml(fact.label, fact.editStepId, fact.editStepId ? 'Edit ' + fact.label : '') +
          linesHtml +
          '</div>'
        )
      }

      function firstPricedLine(lines) {
        var list = lines || []
        var i
        for (i = 0; i < list.length; i++) {
          if (list[i] && !list[i].hidePrice) return list[i]
        }
        return list[0] || null
      }

      function cremationFactFromGroup(group) {
        var raw = group.lines[0] && group.lines[0].value || ''
        return {
          id: 'cremation',
          label: 'Cremation',
          editStepId: group.editStepId,
          primary: raw,
          extra: config.flow === 'shared' ? ['Ashes are not returned'] : [],
          priceHtml: priceHtml(group.lines[0]),
        }
      }

      function arrivalFactFromGroup(group) {
        var byLine = {}
        ;(group.lines || []).forEach(function (line) {
          if (line && line.id) byLine[line.id] = line
        })
        var main = (byLine['transport-main'] && byLine['transport-main'].value) || ''
        var sub = byLine['transport-sub'] && byLine['transport-sub'].value
        var extra = []
        var dateV = byLine['transport-date'] && byLine['transport-date'].value
        var timeV = byLine['transport-time'] && byLine['transport-time'].value
        if (dateV) dateV = String(dateV).replace(/\s+\d{4}\s*$/, '')
        var whenParts = []
        if (dateV) whenParts.push(dateV)
        if (timeV) whenParts.push(timeV)
        var whenLabel = whenParts.join(' · ')
        var loc = byLine['transport-location'] && byLine['transport-location'].value
        if (loc && !(main && /lonsdale/i.test(main) && /lonsdale/i.test(loc))) {
          extra.push(loc)
        }
        var vet = byLine['transport-vet'] && byLine['transport-vet'].value
        if (vet) extra.push(vet)
        var address = byLine['transport-address'] && byLine['transport-address'].value
        if (address) extra.push(address)
        var primary
        if (isLonsdaleTransport(state)) {
          var cat = familyFacingTransportCategoryLabel(state) || 'Bring to Lonsdale'
          var duration = lonsdaleServiceSummaryLabel(state)
          primary = duration ? cat + ' · ' + duration : cat
          if (whenLabel) extra.unshift(whenLabel)
        } else {
          var careBits = []
          if (main) {
            careBits.push(
              String(main)
                .replace(/\bAfter Hours and Public Holiday Collection\b/gi, 'After Hours')
                .replace(/\bSame[\s-]*Day Collection\b/gi, 'After Hours')
                .replace(/\bPriority Collection\b/gi, 'Priority')
                .replace(/\bRegional Collection\b/gi, 'Regional')
                .replace(/\bStandard Collection\b/gi, 'Standard')
                .replace(/\s+Collection\b/g, '')
                .replace(/\s+/g, ' ')
                .trim()
            )
          } else if (sub) {
            careBits.push(
              String(sub)
                .replace(/\bAfter Hours and Public Holiday Collection\b/gi, 'After Hours')
                .replace(/\bSame[\s-]*Day Collection\b/gi, 'After Hours')
                .replace(/\bPriority Collection\b/gi, 'Priority')
                .replace(/\bRegional Collection\b/gi, 'Regional')
                .replace(/\bStandard Collection\b/gi, 'Standard')
                .replace(/\s+Collection\b/g, '')
                .replace(/\s+/g, ' ')
                .trim()
            )
          }
          // Main already includes category · service; only append date/time (skip duplicate sub).
          if (whenLabel) careBits.push(whenLabel)
          primary = careBits.filter(Boolean).join(' · ')
        }
        if (!primary && extra.length) primary = extra.shift()
        return {
          id: group.id,
          label: group.title || 'Arrival',
          editStepId: group.editStepId,
          primary: primary,
          extra: extra,
          priceHtml: priceHtml(firstPricedLine(group.lines)),
        }
      }

      function memorialsFactFromGroup(group) {
        var line = group.lines && group.lines[0]
        if (!line || !line.value) return null
        return {
          id: 'memorials',
          label: 'Memorials',
          editStepId: group.editStepId,
          primary: line.value,
          extra: [],
          priceHtml: priceHtml(line),
        }
      }

      function ashesFactFromGroup(group) {
        var lines = group.lines || []
        var sectionLabel = group.title || memorialReturnSectionTitle(state, config)
        if (config.flow === 'shared') {
          var deliveryLine = lines[lines.length - 1] || lines[0] || null
          return {
            id: 'ashes-return',
            label: sectionLabel,
            editStepId: group.editStepId,
            primary: deliveryLine && deliveryLine.value ? String(deliveryLine.value).trim() : '',
            extraRows: [],
            priceHtml: priceHtml(deliveryLine),
          }
        }
        var speed = lines[0] || null
        var method = lines.length > 1 ? lines[1] : null
        var speedParts = splitMiddot(speed && speed.value)
        var primary = speedParts.slice(0, 2).join(' · ') || String((speed && speed.value) || '').trim()
        var extraRows = []
        if (speedParts.length > 2) {
          extraRows.push({ text: speedParts.slice(2).join(' · '), priceHtml: '' })
        }
        if (method && method.value) {
          extraRows.push({
            text: method.value,
            priceHtml: priceHtml(method),
          })
        }
        return {
          id: 'ashes-return',
          label: sectionLabel,
          editStepId: group.editStepId,
          primary: primary,
          extraRows: extraRows,
          priceHtml: priceHtml(speed || firstPricedLine(lines)),
        }
      }

      function bookingSectionHtml() {
        var facts = []
        groups.forEach(function (group) {
          if (!group || !group.lines || !group.lines.length) return
          if (group.id === 'cremation') {
            facts.push(cremationFactFromGroup(group))
          } else if (group.id === 'care') {
            facts.push(arrivalFactFromGroup(group))
          } else if (group.id === 'memorials') {
            facts.push(memorialsFactFromGroup(group))
          } else if (group.id === 'ashes-placement') {
            var placeLines = (group.lines || []).filter(function (line) {
              return line && line.value
            })
            if (placeLines.length) {
              var extraPlace = placeLines.slice(1).map(function (line) {
                return line.value
              })
              facts.push({
                id: 'ashes-placement',
                label: group.title || 'Ashes placement',
                editStepId: placeLines[0].editStepId || 'memorial:urn',
                primary: placeLines[0].value,
                extra: extraPlace,
                changeHtml: '',
              })
            }
          } else if (group.id === 'ashes-return') {
            facts.push(ashesFactFromGroup(group))
          }
        })
        facts = facts.filter(function (fact) { return fact && fact.primary })
        if (!facts.length) return ''
        return (
          '<section class="baw-review-booking" aria-label="Your booking">' +
          facts.map(factColumnHtml).join('') +
          '</section>'
        )
      }

      function personalPreviewOfKind(g, kind) {
        var lines = (g && g.metaLines) || []
        var i
        for (i = 0; i < lines.length; i++) {
          if (lines[i] && lines[i].preview && lines[i].preview.kind === kind) {
            return lines[i].preview
          }
        }
        return null
      }

      function reviewNameplateChipHtml(preview) {
        if (!preview || preview.kind !== 'nameplate') return ''
        var line1 = String(preview.text || '').trim()
        var line2 = String(preview.textLine2 || '').trim()
        var colourMod = preview.colour === 'silver' ? ' is-silver' : ' is-gold'
        if (typeof renderNameplatePreviewHtml === 'function') {
          var plateInner = renderNameplatePreviewHtml(
            {
              text: line1,
              textLine2: line2,
              font: preview.font,
              lines: preview.lines || (line2 ? 2 : 1),
            },
            preview.image || '',
            { embed: 'plate' }
          )
          return (
            '<span class="baw-review-product-plate baw-review-product-plate--fitted' +
            colourMod +
            '" aria-hidden="true">' +
            plateInner +
            '</span>'
          )
        }
        var img = preview.image
          ? '<img src="' + escapeHtml(sizedShopifyImageUrl(preview.image, memorialCdnPx('MEMORIAL_ARTBOARD_PX', 800))) + '" alt="" decoding="async">'
          : ''
        var fontDef = typeof nameplateFontDef === 'function' ? nameplateFontDef(preview.font) : null
        var family = (fontDef && fontDef.cssFamily) || 'Georgia, serif'
        var ink = ''
        if (line1 || line2) {
          ink =
            '<span class="baw-review-product-plate-ink">' +
            (line1
              ? '<span class="baw-review-product-plate-l1" style="font-family:' + family + '">' +
                escapeHtml(line1) + '</span>'
              : '') +
            (line2
              ? '<span class="baw-review-product-plate-l2">' + escapeHtml(line2) + '</span>'
              : '') +
            '</span>'
        }
        return (
          '<span class="baw-review-product-plate' + colourMod + '" aria-hidden="true">' +
          img + ink +
          '</span>'
        )
      }

      function reviewEngravingChipHtml(preview) {
        if (!preview || preview.kind !== 'engraving') return ''
        var line1 = String(preview.text || '').trim()
        var line2 = String(preview.textLine2 || '').trim()
        if (!line1 && !line2) return ''
        var fontDef = typeof nameplateFontDef === 'function' ? nameplateFontDef('lavanderia') : null
        var family = (fontDef && fontDef.cssFamily) || 'Georgia, serif'
        var fontClass = fontDef && fontDef.id ? ' baw-font-' + fontDef.id : ''
        var lines = ''
        if (line1) {
          lines +=
            '<span class="baw-engraving-text-preview__line baw-engraving-text-preview__line--name' +
            fontClass + '" style="font-family:' + family + '">' +
            escapeHtml(line1) +
            '</span>'
        }
        if (line2) {
          lines +=
            '<span class="baw-engraving-text-preview__line' + fontClass +
            '" style="font-family:' + family + '">' +
            escapeHtml(line2) +
            '</span>'
        }
        return (
          '<span class="baw-review-product-engraving" aria-hidden="true">' +
          '<span class="baw-engraving-text-preview__eyebrow">Engraving</span>' +
          lines +
          '</span>'
        )
      }

      function memorialCardImage(g, catalog) {
        var M = typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
        var cat = catalog && catalog.byKey && g && g.memorialGroupKey
          ? catalog.byKey[g.memorialGroupKey]
          : null
        var card = null
        if (M && cat && g && g.productId != null) {
          if (M.findDisplayCardInCategory) card = M.findDisplayCardInCategory(cat, g.productId)
          if (!card && M.findCardInCategory) card = M.findCardInCategory(cat, g.productId)
        }
        var photo = personalPreviewOfKind(g, 'photo')
        var hasClientPhoto = !!(photo && String(photo.url || '').trim())
        if (
          !hasClientPhoto &&
          M &&
          M.reviewUsesBlankCardWhenNoPhoto &&
          M.reviewUsesBlankCardWhenNoPhoto(g, card)
        ) {
          var blankSrc = M.resolveReviewBlankCardImage
            ? M.resolveReviewBlankCardImage({
                reviewBlankCardImage:
                  (card && card.reviewBlankCardImage) ||
                  (g && g.reviewBlankCardImage) ||
                  null,
                personaliseImage:
                  (card && card.personaliseImage) ||
                  (g && g.personaliseImage) ||
                  null,
              })
            : ''
          // Never fall through to merchandising Card photo / Shopify product photo.
          return blankSrc || ''
        }
        var platePreview = personalPreviewOfKind(g, 'nameplate')
        var npMetal = platePreview && platePreview.colour
        if (npMetal && M && M.resolveMetalCardImage) {
          var metalSrc = M.resolveMetalCardImage(
            {
              card_image:
                (card && card._variantCutoffSettings && card._variantCutoffSettings.card_image) ||
                (card && (card.baseImage || card.image)) ||
                null,
              card_image_gold: (card && card.cardImageGold) || null,
              card_image_silver: (card && card.cardImageSilver) || null,
              personalise_image:
                (card && card.personaliseImage) ||
                (g && g.personaliseImage) ||
                null,
              personalise_image_gold: (card && card.personaliseImageGold) || null,
              personalise_image_silver: (card && card.personaliseImageSilver) || null,
            },
            npMetal
          )
          if (metalSrc) return metalSrc
        }
        // Prefer live catalog Card photo / included_card_image over a stale
        // line.image that may still point at the Shopify product master
        // (Satin Pouch is 1800×1800 / 1.5MB — TE thumb is 480×480 / ~67KB).
        if (card) {
          if (M && M.resolveCardImage) {
            var catalogSrc = M.resolveCardImage(card) || card.image || ''
            if (catalogSrc) return catalogSrc
          } else if (card.image) {
            return card.image
          }
        }
        if (g && g.image) return g.image
        if (M && M.findCardInCategory && cat && g.productId != null) {
          if (!card && M.findCardInCategory) card = M.findCardInCategory(cat, g.productId)
          if (card) {
            if (M.resolveCardImage) return M.resolveCardImage(card) || card.image || ''
            return card.image || ''
          }
        }
        return ''
      }

      function memorialTitleParts(g) {
        var title = String((g && g.title) || 'Memorial').trim()
        var colour = String((g && g.colour) || '').trim()
        var M = typeof AngelAshesBookingMemorials !== 'undefined' ? AngelAshesBookingMemorials : null
        if (colour && M && M.isWeightSizeVariantMeta && M.isWeightSizeVariantMeta(colour)) {
          colour = ''
        }
        if (
          colour &&
          /^(?:xxs|xs|s|small|m|medium|l|large|xl|xxl|extra\s+small|extra\s+large)$/i.test(colour)
        ) {
          colour = ''
        }
        // Quantity already lives on the title (“3 Paw Prints”) — don't add “3 prints”.
        if (colour && /^\d+\s+\S/.test(colour) && /^\d+\s+/.test(title)) {
          return { title: title, variant: '' }
        }
        if (colour) {
          var colourEsc = colour.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
          var stripped = title
            .replace(new RegExp('\\s*[\\u2014\\u2013\\-|]\\s*' + colourEsc + '\\s*$', 'i'), '')
            .replace(/\s+/g, ' ')
            .trim()
          if (stripped) return { title: stripped, variant: colour }
          if (title.toLowerCase() !== colour.toLowerCase()) {
            return { title: title, variant: colour }
          }
        }
        var dashed = title.match(/^(.*?)\s+[\u2014\u2013]\s+(.+)$/)
        if (dashed) {
          var left = dashed[1].trim()
          var right = dashed[2].trim()
          if (left && right && right.length <= 40 && right.indexOf('\u2014') === -1) {
            return { title: left, variant: right }
          }
        }
        return { title: title, variant: '' }
      }

      function memorialPreviewAttrsAll(g, fallbackUrl) {
        var photo = personalPreviewOfKind(g, 'photo')
        var plate = personalPreviewOfKind(g, 'nameplate')
        var engraving = personalPreviewOfKind(g, 'engraving')
        var attrs = ''
        var labels = []
        if (photo && photo.url) {
          attrs +=
            ' data-baw-photo-preview="' + escapeHtml(photo.url) + '"' +
            ' data-baw-photo-orientation="' +
            (photo.orientation === 'portrait' ? 'portrait' : 'landscape') + '"'
          if (photo.frameUrl) {
            attrs += ' data-baw-photo-mockup-frame="' + escapeHtml(photo.frameUrl) + '"'
          }
          labels.push('photo')
        }
        if (plate && plate.groupKey) {
          attrs += ' data-baw-nameplate-preview="' + escapeHtml(plate.groupKey) + '"'
          if (plate.productId != null && String(plate.productId) !== '') {
            attrs += ' data-baw-nameplate-product="' + escapeHtml(String(plate.productId)) + '"'
          }
          labels.push('nameplate')
        }
        if (engraving && engraving.groupKey) {
          attrs += ' data-baw-engraving-preview="' + escapeHtml(engraving.groupKey) + '"'
          if (engraving.productId != null && String(engraving.productId) !== '') {
            attrs += ' data-baw-engraving-product="' + escapeHtml(String(engraving.productId)) + '"'
          }
          labels.push('engraving')
        }
        if (!attrs && fallbackUrl) {
          attrs = ' data-baw-product-preview="' + escapeHtml(fallbackUrl) + '"'
          labels.push('memorial')
        }
        if (!attrs) return null
        return {
          attrs: attrs,
          label: labels.length > 1 ? 'View this memorial' : 'View ' + labels[0],
        }
      }

      function memorialsSectionHtml(catalog) {
        if (!galleryItems.length) return ''
        var cards = galleryItems.map(function (g) {
          var parts = memorialTitleParts(g)
          var src = memorialCardImage(g, catalog)
          var photoPreview = personalPreviewOfKind(g, 'photo')
          var platePreview = personalPreviewOfKind(g, 'nameplate')
          var engravingPreview = personalPreviewOfKind(g, 'engraving')
          var previewInfo = memorialPreviewAttrsAll(g, src)
          var hasEngraving = !!(
            engravingPreview && (engravingPreview.text || engravingPreview.textLine2)
          )
          var displayName = parts.variant
            ? parts.title + ' — ' + parts.variant
            : parts.title
          var hasClientPhoto = !!(photoPreview && String(photoPreview.url || '').trim())
          var Mreview = typeof AngelAshesBookingMemorials !== 'undefined'
            ? AngelAshesBookingMemorials
            : null
          var reviewCat = catalog && catalog.byKey && g.memorialGroupKey
            ? catalog.byKey[g.memorialGroupKey]
            : null
          var reviewCard = null
          if (Mreview && reviewCat && g.productId != null) {
            if (Mreview.findDisplayCardInCategory) {
              reviewCard = Mreview.findDisplayCardInCategory(reviewCat, g.productId)
            }
            if (!reviewCard && Mreview.findCardInCategory) {
              reviewCard = Mreview.findCardInCategory(reviewCat, g.productId)
            }
          }
          var useBlankUrn = !!(
            !hasClientPhoto &&
            Mreview &&
            Mreview.reviewUsesBlankCardWhenNoPhoto &&
            Mreview.reviewUsesBlankCardWhenNoPhoto(g, reviewCard)
          )
          var hasMockup = !!(
            photoPreview &&
            photoPreview.frameUrl &&
            typeof renderPhotoPrintPreview === 'function' &&
            (photoPreview.url || photoPreview.frameUrl)
          )
          // Photo Tribute without a client photo: blank urn JPEG, not an empty
          // mockup cutout and never the merchandising sample-pet Card photo.
          if (useBlankUrn) hasMockup = false
          var plateSrc = platePreview && platePreview.image
            ? sizedShopifyImageUrl(platePreview.image, 800)
            : ''
          var plateHtml = reviewNameplateChipHtml(
            plateSrc && platePreview
              ? Object.assign({}, platePreview, { image: plateSrc })
              : platePreview
          )
          var engravingHtml = reviewEngravingChipHtml(engravingPreview)
          var urnImg
          if (hasMockup) {
            var reviewMockupOpts = {
              frameUrl: photoPreview.frameUrl,
              nativeWide: !!photoPreview.nativeWide,
              nativeTall: !!photoPreview.nativeTall,
            }
            // Match sidebar thumbs: Photo Tribute artboards are tall; Ink/Clay
            // dual-window are wide. Missing hints + landscape → 0-height box.
            if (!reviewMockupOpts.nativeWide && !reviewMockupOpts.nativeTall) {
              if (
                String(g.memorialGroupKey || '') === 'urn' ||
                /photo tribute/i.test(String(parts.title || ''))
              ) {
                reviewMockupOpts.nativeTall = true
              } else {
                reviewMockupOpts.nativeWide = true
              }
            }
            if (reviewMockupOpts.nativeTall) reviewMockupOpts.nativeWide = false
            urnImg =
              '<span class="baw-review-product-mockup">' +
              '<span class="baw-review-product-mockup-stage">' +
              renderPhotoPrintPreview(
                photoPreview.url || '',
                photoPreview.orientation === 'landscape' ? 'landscape' : 'portrait',
                reviewMockupOpts
              ) +
              '</span></span>'
          } else if (src) {
            // Catalog tiles: grid thumb width (warm-cache hit). Artboard 800 is for
            // mockups / nameplates / lightbox only — not these merchandising shots.
            urnImg =
              '<img class="baw-review-product-urn" src="' + escapeHtml(sizedShopifyImageUrl(src, memorialCdnPx('MEMORIAL_REVIEW_THUMB_PX', 360))) +
              '" alt="' + escapeHtml(displayName) + '" width="360" height="360" decoding="async">'
          } else {
            urnImg = '<div class="baw-review-product-placeholder" aria-hidden="true"></div>'
          }
          var windowHtml = plateHtml + engravingHtml
          var stage =
            '<span class="baw-review-product-stage' +
            (hasMockup ? ' has-mockup' : '') +
            (platePreview ? ' has-plate' : '') +
            (hasEngraving ? ' has-engraving' : '') +
            '">' + urnImg + windowHtml + '</span>'
          var previewHint = previewInfo
            ? '<span class="baw-review-product-eye" aria-hidden="true">' +
              '<svg width="16" height="16" viewBox="0 0 24 24" focusable="false">' +
              '<path fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" d="M2.8 12s3.4-6.5 9.2-6.5S21.2 12 21.2 12s-3.4 6.5-9.2 6.5S2.8 12 2.8 12z"/>' +
              '<circle cx="12" cy="12" r="2.6" fill="none" stroke="currentColor" stroke-width="1.7"/>' +
              '</svg></span>'
            : ''
          var media = previewInfo
            ? (
              '<button type="button" class="baw-review-product-media"' +
              previewInfo.attrs +
              ' aria-label="' + escapeHtml(previewInfo.label) + '">' +
              stage +
              previewHint +
              '</button>'
            )
            : '<div class="baw-review-product-media">' + stage + '</div>'
          return (
            '<article class="baw-review-product">' +
            media +
            '<div class="baw-review-product-copy">' +
            '<h4 class="baw-review-product-title">' + escapeHtml(parts.title) + '</h4>' +
            (parts.variant
              ? '<p class="baw-review-product-variant">' + escapeHtml(parts.variant) + '</p>'
              : '') +
            '</div></article>'
          )
        }).join('')
        return (
          '<section class="baw-review-memorials" aria-label="Your memorials">' +
          '<div class="baw-review-products" data-count="' + galleryItems.length + '">' +
          cards +
          '</div>' +
          '</section>'
        )
      }

      var petStrip = ''
      if (state.pet && state.pet.name) {
        var breed = state.pet.breed ? String(state.pet.breed).trim() : ''
        petStrip =
          '<div class="baw-review-pet">' +
          '<div class="baw-review-pet-text">' +
          '<span class="baw-review-pet-name">' + escapeHtml(state.pet.name) + '</span>' +
          (breed
            ? '<span class="baw-review-pet-sep"> · </span><span class="baw-review-pet-meta">' +
              escapeHtml(breed) +
              '</span>'
            : '') +
          '</div>' +
          '<button type="button" class="baw-review-edit" data-edit="pet">Edit</button>' +
          '</div>'
      }

      root.setAttribute('data-review-gallery', String(galleryItems.length))
      var memorialCatalog = getMemorialCatalog(state, config)
      var portraitHtml = ''
      if (
        typeof AngelAshesBookingMemorials !== 'undefined' &&
        AngelAshesBookingMemorials.portraitOfferVisible(memorialCatalog, state)
      ) {
        var po = memorialCatalog.settings.portrait
        var poVariant = AngelAshesBookingMemorials.firstAvailableVariant(po.product)
        var poHeading = AngelAshesBookingMemorials.interpolatePet(po.heading, state)
        var poCopy = String(AngelAshesBookingMemorials.interpolatePet(po.copy, state) || '').trim()
        if (poCopy === '-') poCopy = ''
        var poOffer = state.portraitOffer && state.portraitOffer.variantId ? state.portraitOffer : null
        var poClientPhoto =
          poOffer && (poOffer.localPreviewUrl || poOffer.photoUrl)
            ? String(poOffer.localPreviewUrl || poOffer.photoUrl).trim()
            : ''
        var poFallbackImg =
          (po.cardImage && String(po.cardImage).trim()) ||
          (poVariant && poVariant.image) ||
          (po.product && po.product.image) ||
          ''
        var poImg = poClientPhoto || poFallbackImg
        var poHasPhoto = !!poClientPhoto
        var poUploading = !!(poOffer && poOffer.uploading)
        var poCtaLabel = poUploading
          ? 'Uploading…'
          : poHasPhoto
            ? 'Change photo'
            : (po.ctaLabel || 'Upload photo')
        var poErr =
          poOffer && poOffer.uploadErrorMessage
            ? String(poOffer.uploadErrorMessage).trim()
            : ''
        portraitHtml =
          '<section class="baw-portrait-offer' +
          (poHasPhoto ? ' is-photo-selected' : '') +
          (poUploading ? ' is-uploading' : '') +
          '" data-baw-portrait-offer>' +
          (poImg
            ? '<div class="baw-portrait-offer-media' +
              (poHasPhoto ? ' is-client-photo' : '') +
              '"><img src="' +
              escapeHtml(sizedShopifyImageUrl(poImg, memorialCdnPx('MEMORIAL_GRID_THUMB_PX', 360))) +
              '" alt="" loading="eager" decoding="async" width="96" height="96"></div>'
            : '<div class="baw-portrait-offer-media baw-portrait-offer-media--empty" aria-hidden="true"></div>') +
          '<div class="baw-portrait-offer-copy">' +
          '<h3 class="baw-portrait-offer-title">' + escapeHtml(poHeading) + '</h3>' +
          '<p class="baw-portrait-offer-meta">' +
          (poCopy
            ? '<span class="baw-portrait-offer-text">' + escapeHtml(poCopy) + '</span>'
            : '') +
          '<span class="baw-portrait-offer-price">' + escapeHtml(money(poVariant.price)) +
          (poVariant.compareAtPrice && poVariant.compareAtPrice > poVariant.price
            ? ' <span class="baw-portrait-offer-compare">' + escapeHtml(money(poVariant.compareAtPrice)) + '</span>'
            : '') +
          '</span></p></div>' +
          '<div class="baw-portrait-offer-actions">' +
          '<button type="button" class="baw-portrait-offer-cta" data-baw-portrait-add' +
          (poUploading ? ' disabled aria-busy="true"' : '') +
          '>' +
          escapeHtml(poCtaLabel) +
          '</button>' +
          '<input type="file" class="baw-portrait-offer-input" data-baw-portrait-photo-input accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif" tabindex="-1" aria-hidden="true">' +
          (poErr
            ? '<p class="baw-portrait-offer-error" role="alert">' + escapeHtml(poErr) + '</p>'
            : '') +
          '</div></section>'
        if (!poOffer) {
          AngelAshesBookingMemorials.track('portrait_offer_viewed', {
            product_id: po.product.id,
            variant_id: poVariant.id,
          })
        }
      }

      var html =
        '<div class="baw-step-inner baw-step-inner--review">' +
        '<div class="baw-review-head">' +
        '<div class="baw-review-head-copy baw-step-intro">' +
        '<h2 class="baw-headline baw-wizard-title">' + escapeHtml(headline) + '</h2>' +
        (reviewSubtitle
          ? '<p class="baw-helper">' + escapeHtml(reviewSubtitle) + '</p>'
          : '') +
        '</div>' +
        '</div>' +
        '<div class="baw-review-layout">' +
        '<div class="baw-review-visual">' +
        memorialsSectionHtml(memorialCatalog) +
        '</div>' +
        '<div class="baw-review-rail">' +
        petStrip +
        bookingSectionHtml() +
        portraitHtml +
        renderReviewCheckoutPanelHtml(total, config, state, {
          calloutsHtml: renderReviewCalloutsHtml(state, config),
        }) +
        '</div>' +
        '</div>' +
        '</div>'

      if (els.reviewChrome) {
        els.reviewChrome.innerHTML =
          '<h2 class="baw-review-chrome-headline baw-wizard-title">' + escapeHtml(headline) + '</h2>' +
          (reviewSubtitle
            ? '<p class="baw-review-chrome-helper">' + escapeHtml(reviewSubtitle) + '</p>'
            : '')
        els.reviewChrome.hidden = false
      }

      els.step.innerHTML = html
      if (typeof wirePhotoPreviewDecodeIn === 'function') {
        els.step.querySelectorAll('.baw-review-product-mockup').forEach(function (el) {
          wirePhotoPreviewDecodeIn(el)
        })
        // Re-measure after flex stage layout; unify Gold/Silver plate sizes to
        // the Gold reference so Ink Silver is not smaller than Photo Tribute/Clay.
        if (typeof resizePhotoMockupsIn === 'function' && typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(function () {
            els.step.querySelectorAll('.baw-review-product-mockup').forEach(function (el) {
              resizePhotoMockupsIn(el)
            })
            if (typeof syncReviewNameplatePlateSizes === 'function') {
              syncReviewNameplatePlateSizes(els.step)
            }
          })
        }
      }
      if (typeof fitNameplatePreview === 'function') {
        window.requestAnimationFrame(function () {
          els.step.querySelectorAll('.baw-review-product-plate--fitted').forEach(function (plate) {
            fitNameplatePreview(plate)
            if (typeof ensureNameplateFontsThenFit === 'function') {
              ensureNameplateFontsThenFit(plate)
            }
          })
        })
      }

      els.step.querySelectorAll('[data-edit]').forEach(function (btn) {
        btn.addEventListener('click', function () { goTo(btn.getAttribute('data-edit')) })
      })
      var notesBox = els.step.querySelector('[data-baw-review-notes]')
      if (notesBox) {
        syncCareTeamNoteChrome(notesBox)
        /* After footer mount, pin open notes above Total on mobile/tablet. */
        window.setTimeout(function () {
          syncCareTeamNotePin(notesBox)
        }, 0)
      }
      clearSharedAshesAckIfNeeded()
      syncSharedAshesAckChrome(els.step)
      els.step.querySelectorAll('[data-baw-summary-edit-ashes-placement]').forEach(function (btn) {
        btn.addEventListener('click', function () { openAshesPlacementEditor() })
      })
      bindSummaryPhotoPreviewClicks(els.step)
      ;(function wirePortraitOfferPhoto() {
        var portraitAdd = els.step.querySelector('[data-baw-portrait-add]')
        var portraitInput = els.step.querySelector('[data-baw-portrait-photo-input]')
        if (!portraitAdd || !portraitInput) return

        function revokePortraitLocalPreview() {
          var offer = state.portraitOffer
          if (!offer || !offer.localPreviewUrl) return
          try { URL.revokeObjectURL(offer.localPreviewUrl) } catch (e) { /* ignore */ }
          offer.localPreviewUrl = null
        }

        function ensurePortraitOfferInState() {
          var po = memorialCatalog.settings.portrait
          var v = AngelAshesBookingMemorials.firstAvailableVariant(po.product)
          if (!po || !po.product || !v) return null
          var existing = state.portraitOffer
          var already =
            existing &&
            existing.variantId != null &&
            String(existing.variantId) === String(v.id)
          if (already) return existing
          var fallbackImg =
            (po.cardImage && String(po.cardImage).trim()) ||
            v.image ||
            (po.product && po.product.image) ||
            null
          state.portraitOffer = {
            productId: po.product.id,
            variantId: v.id,
            label: AngelAshesBookingMemorials.productTitle(po.product),
            price: v.price,
            image: fallbackImg,
            photoUrl: existing && existing.photoUrl ? existing.photoUrl : null,
            localPreviewUrl: existing && existing.localPreviewUrl ? existing.localPreviewUrl : null,
            uploading: false,
            uploadErrorMessage: null,
            uploadGeneration: (existing && existing.uploadGeneration) || 0,
          }
          AngelAshesBookingMemorials.track('portrait_offer_added', {
            product_id: po.product.id,
            variant_id: v.id,
          })
          return state.portraitOffer
        }

        function beginPortraitPhotoUpload(file) {
          var offer = ensurePortraitOfferInState()
          if (!offer || !file || offer.uploading) return
          var clientErr =
            typeof validatePhotoFileClient === 'function'
              ? validatePhotoFileClient(file)
              : null
          if (clientErr) {
            offer.uploadErrorMessage = clientErr
            render()
            return
          }
          revokePortraitLocalPreview()
          try {
            offer.localPreviewUrl = URL.createObjectURL(file)
          } catch (e) {
            offer.localPreviewUrl = null
          }
          offer.uploading = true
          offer.uploadErrorMessage = null
          offer.uploadGeneration = (offer.uploadGeneration || 0) + 1
          var generation = offer.uploadGeneration
          renderSummary()
          render()
          uploadPhoto(file).then(function (url) {
            var done = state.portraitOffer
            if (!done || done.uploadGeneration !== generation) return
            done.uploading = false
            done.photoUrl = url
            done.uploadErrorMessage = null
            revokePortraitLocalPreview()
            renderSummary()
            render()
          }).catch(function (err) {
            var failed = state.portraitOffer
            if (!failed || failed.uploadGeneration !== generation) return
            failed.uploading = false
            failed.uploadErrorMessage =
              (err && err.message) || 'We couldn\u2019t upload that photo. Please try again.'
            render()
          })
        }

        portraitAdd.addEventListener('click', function () {
          var offer = ensurePortraitOfferInState()
          if (!offer || offer.uploading) return
          // Keep this sync with the click so the file picker is not blocked.
          renderSummary()
          var totalEl = els.step.querySelector('.baw-review-total-value')
          if (totalEl) {
            totalEl.textContent = money(ledgerTotal(buildLedger(state, config)))
          }
          try { portraitInput.click() } catch (e) { /* ignore */ }
        })

        portraitInput.addEventListener('change', function () {
          var file = portraitInput.files && portraitInput.files[0]
          try { portraitInput.value = '' } catch (e) { /* ignore */ }
          if (file) beginPortraitPhotoUpload(file)
        })
      })()

      var continueBtn = els.step.querySelector('[data-baw-continue]')
      if (continueBtn) {
        continueBtn.setAttribute('data-baw-continue-action', 'submit')
      }
      mountReviewCheckoutFooter()
      syncSharedAshesAckChrome(root)
    }
    function photoComposeUrl() {
      var upload = config.photoUploadUrl
      if (!upload) return null
      if (upload.indexOf('upload-photo') === -1) return null
      return upload.replace('upload-photo', 'compose-photo')
    }


    __bawCtx.photoComposeUrl = photoComposeUrl

    function isBookingThemeEditorPreview() {
      try {
        if (window.Shopify && window.Shopify.designMode) return true
      } catch (e) { /* ignore */ }
      try {
        var search = String(window.location.search || '')
        if (/[?&](preview_theme_id|_ab|_bt|_fd)=/.test(search)) return true
      } catch (e2) { /* ignore */ }
      return false
    }


    __bawCtx.isBookingThemeEditorPreview = isBookingThemeEditorPreview

    /* --------------------------------- submit ------------------------------------- */

    function bookingSubmitBlockedReason() {
      var blocked = bookingSubmitBlockedStep()
      return blocked ? blocked.reason : ''
    }

    function bookingSubmitBlockedStep() {
      var steps = computeSteps(state, config)
      for (var i = 0; i < steps.length; i++) {
        var stepId = steps[i].id
        if (stepId === 'review') continue
        if (!canAdvanceFrom(stepId)) {
          return {
            stepId: stepId,
            reason: continueBlockedReason(stepId) || 'Please complete your booking before checkout.',
          }
        }
      }
      return null
    }

    function submitOrder(leaveTo) {
      var afterUrl = leaveTo || '/cart'
      var continueBtn = continueButton()
      var shopBtn =
        (els.footer && els.footer.querySelector('[data-baw-review-shop]')) ||
        (els.step && els.step.querySelector('[data-baw-review-shop]'))
      var errorEl =
        (els.footer && els.footer.querySelector('#baw-submit-error')) ||
        (els.step && els.step.querySelector('#baw-submit-error'))
      clearSharedAshesAckIfNeeded()
      if (config.flow === 'shared' && !state.sharedAshesAcknowledged) {
        state.sharedAshesAckInvalid = true
        syncSharedAshesAckChrome(root)
        focusSharedAshesAck()
        if (errorEl) {
          errorEl.style.display = 'none'
          errorEl.textContent = ''
        }
        return
      }
      state.sharedAshesAckInvalid = false
      syncSharedAshesAckChrome(root)
      var blocked = bookingSubmitBlockedStep()
      if (blocked) {
        if (blocked.stepId && blocked.stepId !== state.currentStepId) goTo(blocked.stepId)
        return
      }

      var items = buildLedger(state, config)
      var missingOnReview = typeof reviewLinesMissingVariant === 'function'
        ? reviewLinesMissingVariant(items)
        : []
      if (missingOnReview.length) {
        if (errorEl) {
          errorEl.style.display = 'block'
          errorEl.textContent = 'One of these items isn\u2019t available to order right now. Please go back and choose another option, or call us and we\u2019ll help.'
        }
        return
      }
      var lines = buildCartLines(items)
      var attributes = typeof normalizeCartAttributes === 'function'
        ? normalizeCartAttributes(buildCartAttributes(state))
        : buildCartAttributes(state)
      var note =
        typeof specialInstructionsText === 'function'
          ? specialInstructionsText(state)
          : String(state.specialInstructions || '').trim()
      var batches = typeof partitionCartAddBatches === 'function'
        ? partitionCartAddBatches(lines)
        : [lines]

      if (!lines.length) return

      if (continueBtn) { continueBtn.disabled = true; continueBtn.classList.add('is-loading'); continueBtn.textContent = 'Just a moment\u2026' }
      if (shopBtn) { shopBtn.disabled = true; shopBtn.classList.add('is-loading') }

      function cartPost(url, body) {
        var headers = { Accept: 'application/json' }
        var opts = { method: 'POST', headers: headers, credentials: 'same-origin' }
        if (body != null) {
          headers['Content-Type'] = 'application/json'
          opts.body = JSON.stringify(body)
        }
        return fetch(url, opts).then(function (res) {
          return res.text().then(function (t) {
            var parsed = null
            if (t) {
              try { parsed = JSON.parse(t) } catch (e) { parsed = null }
            }
            if (!res.ok) {
              var desc =
                (parsed && (parsed.description || parsed.message || parsed.error)) ||
                t ||
                (url + ' failed')
              if (parsed && parsed.errors) {
                try {
                  var extra = typeof parsed.errors === 'string'
                    ? parsed.errors
                    : JSON.stringify(parsed.errors)
                  if (extra && extra !== '{}' && String(desc).indexOf(extra) === -1) {
                    desc = String(desc) + ' ' + extra
                  }
                } catch (e2) { /* ignore */ }
              }
              var err = new Error(String(desc))
              err.status = res.status
              err.url = url
              err.shopifyDescription = String(desc)
              err.responseBody = t
              throw err
            }
            return parsed || {}
          })
        })
      }

      function addCartBatches(index, knownKeys) {
        knownKeys = knownKeys || {}
        if (index >= batches.length) return Promise.resolve({})
        var rawBatch = batches[index]

        function rememberKeys(added) {
          if (typeof indexCartLineKeys === 'function') {
            indexCartLineKeys(added && added.items, knownKeys)
          }
          return addCartBatches(index + 1, knownKeys)
        }

        function postBatch() {
          var nestedBatch = typeof attachCartLineParentRefs === 'function'
            ? attachCartLineParentRefs(rawBatch, knownKeys)
            : rawBatch
          var payloadItems = typeof cartAddPayloadItems === 'function'
            ? cartAddPayloadItems(nestedBatch)
            : nestedBatch
          function requirePostedLines(posted, body) {
            if (
              typeof cartAddCoversPostedLines === 'function' &&
              !cartAddCoversPostedLines(posted, body)
            ) {
              var dropped = new Error('Booking wizard: cart did not keep every line')
              dropped.shopifyDescription = 'A selected item was not added to the cart'
              var returned = body && body.items
              if (returned && returned.length) dropped.cartAlreadyAccepted = true
              throw dropped
            }
            return body
          }

          return cartPost('/cart/add.js', { items: payloadItems }).then(function (body) {
            return rememberKeys(requirePostedLines(payloadItems, body))
          }).catch(function (err) {
            if (err && err.cartAlreadyAccepted) throw err
            var hadParent = typeof cartBatchHasParentRefs === 'function'
              ? cartBatchHasParentRefs(payloadItems)
              : false
            if (!hadParent) throw err
            var flatItems = typeof cartAddPayloadItems === 'function'
              ? cartAddPayloadItems(rawBatch)
              : rawBatch
            return cartPost('/cart/add.js', { items: flatItems }).then(function (body) {
              return rememberKeys(requirePostedLines(flatItems, body))
            })
          })
        }

        var needsParentKeys = typeof cartLineParentBookingId === 'function' &&
          rawBatch.some(function (line) {
            var parent = cartLineParentBookingId(line)
            if (!parent || knownKeys[parent]) return false
            if (typeof cartLineBookingId !== 'function') return true
            return !rawBatch.some(function (other) {
              return cartLineBookingId(other) === parent
            })
          })
        if (!needsParentKeys) return postBatch()
        return fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        }).then(function (res) {
          return res.json()
        }).then(function (cart) {
          if (typeof indexCartLineKeys === 'function') {
            indexCartLineKeys(cart && cart.items, knownKeys)
          }
          return postBatch()
        }).catch(function () {
          return postBatch()
        })
      }

      function duplicateCartVariantIds(list) {
        var counts = {}
        ;(list || []).forEach(function (line) {
          if (!line || line.id == null) return
          var id = String(line.id)
          counts[id] = (counts[id] || 0) + 1
        })
        return Object.keys(counts).filter(function (id) { return counts[id] > 1 })
      }

      function cartSubmitUserMessage(err) {
        var raw = String((err && (err.shopifyDescription || err.message)) || '').toLowerCase()
        if (
          /sold out|not available|cannot find variant|cannot add this item|inventory|is already in your cart/i.test(raw)
        ) {
          return 'One of these items isn\u2019t available to order right now. Please go back and choose another option, or call us and we\u2019ll help.'
        }
        if (/property|properties|too long|invalid/i.test(raw) && /422/.test(String((err && err.status) || ''))) {
          return 'We couldn\u2019t save this booking. Please try again, or call us and we\u2019ll help.'
        }
        return 'We couldn\u2019t save this just now. Please try again, or call us and we\u2019ll help.'
      }

      // Replace an earlier booking in this cart. Leave jewellery and other
      // products the client already added.
      function removePreviousBookingLines() {
        return fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        }).then(function (res) {
          return res.json()
        }).then(function (cart) {
          var existingAttrs = cart && cart.attributes
          if (existingAttrs && typeof existingAttrs === 'object' && !Array.isArray(existingAttrs)) {
            var merged = Object.assign({}, existingAttrs, attributes)
            attributes = typeof normalizeCartAttributes === 'function'
              ? normalizeCartAttributes(merged)
              : merged
          }
          var updates = typeof previousBookingCartUpdates === 'function'
            ? previousBookingCartUpdates(cart && cart.items)
            : {}
          if (!updates || !Object.keys(updates).length) return cart
          return cartPost('/cart/update.js', { updates: updates })
        })
      }

      removePreviousBookingLines()
        .catch(function (keepErr) {
          console.warn('Booking wizard: could not update the existing cart', keepErr)
          return {}
        })
        .then(function () { return addCartBatches(0) })
        .then(function () {
          var attrBody = { attributes: attributes }
          if (note) attrBody.note = note
          return cartPost('/cart/update.js', attrBody)
        })
        .then(function () {
          // Shop Pay / Checkout can ignore `note` when it arrives with attributes.
          if (!note) return {}
          return cartPost('/cart/update.js', { note: note })
        })
        .then(function () {
          clearBookingDraft(config)
          window.location.href = afterUrl
        }).catch(function (err) {
          if (errorEl) {
            errorEl.style.display = 'block'
            errorEl.textContent = cartSubmitUserMessage(err)
          }
          if (continueBtn) {
            var primaryLabel = typeof reviewPrimaryCta === 'function' ? reviewPrimaryCta(config) : null
            continueBtn.disabled = false
            continueBtn.classList.remove('is-loading')
            continueBtn.textContent = (primaryLabel && primaryLabel.label) || 'Continue to checkout'
          }
          if (shopBtn) { shopBtn.disabled = false; shopBtn.classList.remove('is-loading') }
          console.error('Booking wizard: cart submit failed', {
            status: err && err.status,
            url: err && err.url,
            message: err && err.message,
            shopify: err && err.shopifyDescription,
            response: err && err.responseBody,
            lineCount: lines.length,
            batchCount: batches.length,
            variantIds: lines.map(function (line) { return line && line.id }),
            duplicateVariantIds: duplicateCartVariantIds(lines),
            noteLength: note ? note.length : 0,
          })
        })
    }

    return {
      start: function () {
        render()
        // After first paint: warm Goodbye Room + early memorial thumbs in idle time.
        if (
          typeof AngelAshesBookingMemorials !== 'undefined' &&
          AngelAshesBookingMemorials.scheduleWizardImageWarm
        ) {
          AngelAshesBookingMemorials.scheduleWizardImageWarm(config, state, state.currentStepId)
        }
      },
    }
  }

  /* -------------------------------- bootstrap -------------------------------- */

  function syncBookingAppShell() {
    var section = document.querySelector('.shopify-section.section-booking-wizard')
    var html = document.documentElement
    if (!html) return

    if (!section) {
      html.classList.remove('baw-app-shell')
      return
    }

    // Immersive app shell on every breakpoint — hide storefront header/footer so the
    // booking card is the only chrome (desktop included).
    html.classList.add('baw-app-shell')
    section.style.setProperty('--baw-chrome-offset', '0px')
    var y = window.pageYOffset || 0
    if (y > 0) window.scrollTo(0, 0)
  }

  var appShellBound = false
  function bindBookingAppShell() {
    syncBookingAppShell()
    if (appShellBound) return
    appShellBound = true
    window.addEventListener('resize', syncBookingAppShell)
    window.addEventListener('orientationchange', function () {
      window.setTimeout(syncBookingAppShell, 80)
    })
  }

  function showWizardBootError(root, message) {
    if (!root) return
    var shell = root.querySelector('[data-baw-root]')
    if (shell) shell.hidden = false
    var step = root.querySelector('[data-baw-step]')
    if (!step) return
    step.innerHTML =
      '<div class="baw-step-inner">' +
      stepIntroHtml(
        'Booking wizard couldn\u2019t load',
        '<p class="baw-helper">' + escapeHtml(message || 'Please refresh the page, or contact us to book.') + '</p>'
      ) +
      '</div>'
  }

  function collectBlockConfigs(root) {
    var nodes = root.querySelectorAll('[data-baw-block-config]')
    var blocks = []
    nodes.forEach(function (el) {
      try {
        var entry = JSON.parse(el.textContent)
        if (entry && entry.type) blocks.push(entry)
      } catch (e) {
        console.warn('Booking wizard: skipped invalid block config', e)
      }
    })
    return blocks
  }

  function init(root) {
    if (!root || root.getAttribute('data-baw-ready') === '1') return
    var configEl = root.querySelector('[data-baw-config]')
    if (!configEl) {
      console.error('Booking wizard: missing config JSON inside .baw')
      showWizardBootError(root, 'Missing booking configuration. Save the theme section and try again.')
      return
    }
    var raw
    try {
      raw = JSON.parse(configEl.textContent)
    } catch (e) {
      console.error('Booking wizard: invalid config', e, configEl.textContent)
      showWizardBootError(
        root,
        'Invalid booking configuration (often a product image or collection JSON issue). Check the browser console, then re-save Theme Editor.'
      )
      return
    }
    // Prefer section-emitted blocks. Theme-block scripts (if any leftover) only
    // fill in when the section config has no blocks.
    var sectionBlocks = Array.isArray(raw.blocks) ? raw.blocks : []
    if (sectionBlocks.length) {
      raw.blocks = sectionBlocks
    } else {
      var blockConfigs = collectBlockConfigs(root)
      raw.blocks = blockConfigs.length ? blockConfigs : []
    }
    root.setAttribute('data-baw-ready', '1')
    try {
      var config = normalizeConfig(raw)
      var memorialCols =
        config.memorials && config.memorials.desktopColumns != null
          ? Number(config.memorials.desktopColumns)
          : 3
      if (isFinite(memorialCols) && memorialCols >= 2 && memorialCols <= 8) {
        root.setAttribute('data-baw-memorial-cols', String(memorialCols))
        root.style.setProperty('--baw-mpc-desktop-cols', String(memorialCols))
      }
      createWizard(root, config).start()
      bindBookingAppShell()
    } catch (err) {
      console.error('Booking wizard: failed to start', err)
      root.removeAttribute('data-baw-ready')
      showWizardBootError(root, 'Something went wrong starting the booking steps. Please refresh and try again.')
    }
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('.baw:not([data-baw-ready="1"])')
    roots.forEach(init)
  }

  function boot() {
    bindBookingAppShell()
    initAll(document)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot)
  } else {
    boot()
  }

  // Theme Editor re-renders sections without a full page reload.
  document.addEventListener('shopify:section:load', function (event) {
    if (event && event.target) initAll(event.target)
  })
})()
